// ===== Central Store (React Context) =====
import React from 'react';
import { Icon } from './icons.jsx';
import { useToast } from './primitives.jsx';
import {
  STAGE_NAMES,
  AGENTS, AGENT_COLORS, LEADS, ALERTS, CLIENTES, PRODUTOS,
  WEBHOOKS, API_LOGS, ANEXOS, MIME_BY_EXT, MAX_ATTACHMENT_SIZE_MB,
  COMMISSION_RATES, POSVENDA_ETAPAS_BY_ID, POSVENDA_SEED, TODAY, MONTHS, brl, duracaoDoTipo, INTEGRACAO_DE_AGENTES,
  formatAlertWhen, EMISSORAS, EMISSORAS_BY_ID, TRANSPORTE_BY_ID,
  nomusDataHora, nomusData, nomusNum, nomusValor, buildErpClientePayload,
  SAC_TIPOS_BY_ID, SAC_STATUS_BY_ID, CHAMADOS, buildChamadoInteracao,
} from './data.jsx';
import { RBAC, useRbacVersion, scopeLeads, scopeClientes, scopeAlerts, scopeChamados } from './auth.jsx';
import { eventoParaBanco, eventoDoBanco } from './timeline-texto.js';
import { criarRastro, rastrearEscritas } from './rastro-de-escritas.js';
import { conferirParaErp, payloadDoCliente } from './cliente-erp.js';
import { validarArquivo } from './anexos.js';

const StoreContext = React.createContext(null);
const useStore = () => React.useContext(StoreContext);

// Usuário autenticado quando o provider sobe sem sessão real (ex.: alguns testes
// de unidade montam StoreProvider isolado). Papel "closer" = menor privilégio,
// mesmo default que usePermission já aplica quando me.papel vem vazio.
const ME_FALLBACK = { id: null, nome: "—", initials: "—", papel: "closer", email: "", idPessoaVendedor: null };

// `quando` (o texto "Hoje, 14:30") e `vencido` sao DERIVACAO DE TELA, nao dado:
// uma coluna "vencido" no banco nasceria errada no instante seguinte, e o texto
// relativo depende de quando se olha. Ficam fora do tradutor de propósito --
// api.js nao deve saber o que a tela escreve -- e sao vestidos aqui, na entrada
// e depois de cada escrita, para os dois caminhos darem o mesmo objeto.
function vestirAlerta(a) {
  const d = a.when instanceof Date ? a.when : new Date(a.when || Date.now());
  return {
    ...a,
    when: d,
    quando: formatAlertWhen(d),
    vencido: !a.feito && d < new Date(Date.now() - 60 * 60 * 1000),
  };
}

// `api` é opcional de propósito: os testes de unidade montam o provider
// isolado, sem banco, e devem continuar funcionando com as constantes.
// Recarga automatica (levantamento de 02/10, item A4). Antes os dados "quentes" (negocios, propostas, agenda e
// notificacoes) eram lidos UMA vez, no login: o closer so via o lead que o SDR repassou depois de apertar F5.
const INTERVALO_DE_RECARGA_MS = 60_000;
// Piso entre duas recargas: trocar de aba varias vezes seguidas nao pode martelar o banco.
const PISO_DE_RECARGA_MS = 30_000;

function StoreProvider({ children, usuarioAutenticado, api: apiBruta = null }) {
  const meBase = usuarioAutenticado || ME_FALLBACK;
  // Gravacoes em andamento e "geracao" (src/rastro-de-escritas.js). A recarga depende disto: uma leitura que
  // comeca antes de uma gravacao terminar pode trazer o dado VELHO e desfazer na tela o que a pessoa salvou.
  const rastro = React.useRef(criarRastro());
  const api = React.useMemo(() => rastrearEscritas(apiBruta, rastro.current), [apiBruta]);
  const [recarga, setRecarga] = React.useState(0);
  const ultimaRecargaRef = React.useRef(Date.now());
  React.useEffect(() => {
    if (!api) return undefined;
    const pedir = () => {
      if (typeof document !== 'undefined' && document.hidden) return;   // aba escondida: nao gasta rede
      if (rastro.current.pendentes > 0) return;                          // gravando: a recarga espera o proximo ciclo
      if (Date.now() - ultimaRecargaRef.current < PISO_DE_RECARGA_MS) return;
      ultimaRecargaRef.current = Date.now();
      setRecarga((n) => n + 1);
    };
    const relogio = setInterval(pedir, INTERVALO_DE_RECARGA_MS);
    document.addEventListener('visibilitychange', pedir);
    window.addEventListener('focus', pedir);
    return () => {
      clearInterval(relogio);
      document.removeEventListener('visibilitychange', pedir);
      window.removeEventListener('focus', pedir);
    };
  }, [api]);
  // LEADS (data.jsx) já está vazio desde a Frente J; fica como estado inicial
  // só até o banco responder.
  const [leads, setLeads]       = React.useState(LEADS);
  // `updateLead` precisa saber o dono ANTERIOR para avisar o novo; ele e um useCallback sem `leads` nas
  // dependencias (por isso o ref). `notifyPeople` e declarado bem mais abaixo: a funcao entra neste ref depois.
  const leadsRef = React.useRef(leads);
  leadsRef.current = leads;
  const notificarRef = React.useRef(null);
  const [alerts, setAlerts]     = React.useState(ALERTS);
  const [clientes, setClientes] = React.useState(CLIENTES);
  // Ref sempre atual. `syncClienteERP` e chamada logo DEPOIS de `await createCliente`,
  // pelo modal, com a copia da funcao do render anterior -- cuja lista de clientes
  // ainda nao tem o cliente novo. Ela devolvia `null` sem avisar, e a caixa "criar
  // tambem no Nomus ao salvar" nunca criou nada no ERP (achado de 30/09).
  const clientesRef = React.useRef(clientes);
  clientesRef.current = clientes;
  const [webhooks, setWebhooks] = React.useState(WEBHOOKS);
  const [apiLogs, setApiLogs]   = React.useState(API_LOGS);
  // Tokens de agente: espelham api_tokens (só o hash/prefixo permanece; o valor puro é exibido uma única vez)
  const [agentTokens, setAgentTokens] = React.useState([]);
  // Só o usuário da sessão até o banco responder. A lista de exemplo saiu em
  // 19/08 junto com a ligação deste módulo — "Produção PDLED" era o último
  // usuário fictício, e aparecia na tela de Admin como se fosse gente.
  const [users, setUsers]       = React.useState([{ ...meBase, ativo: true, ultimoAcesso: "agora" }]);
  const [timeline, setTimeline] = React.useState({});
  const [activeFunnel, setActiveFunnel] = React.useState("projetos");
  // ----- Referência vinda do banco (Etapa 2) -----
  const [funis, setFunis] = React.useState([]);
  const [etapasPorFunil, setEtapasPorFunil] = React.useState({});
  // Declarado aqui (antes do efeito abaixo) e não junto dos outros hooks de UI mais
  // adiante no arquivo: o efeito de carga de funis/etapas precisa de `toast` no corpo
  // para avisar quem usa quando a carga falha — hooks não podem ser chamados fora de
  // ordem, então `toast` sobe para antes de ser consumido.
  const { toasts, push: toast } = useToast();

  // O banco usa `codigo`/`nome`/`sla_dias`; as telas esperam
  // `id`/`name`/`short`/`slaEmDias`. A tradução acontece aqui, na entrada, para
  // o resto do app não perceber a troca de origem. `uuid` guarda a chave real,
  // necessária nas escritas da Task 4 — o banco quer a chave, a tela quer o código.
  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    Promise.all([api.listarFunis(), api.listarEtapas()])
      .then(([listaFunis, listaEtapas]) => {
        if (!vivo) return;

        setFunis(listaFunis.map((f) => ({
          id: f.codigo, name: f.nome, short: f.nome_curto, subtitle: f.subtitulo,
          description: f.descricao, ciclo: f.ciclo, icon: f.icon, accent: f.accent,
          uuid: f.id,
        })));

        const porFunil = {};
        for (const f of listaFunis) {
          porFunil[f.codigo] = listaEtapas
            .filter((e) => e.funil_id === f.id)
            .sort((a, b) => a.ordem - b.ordem)
            .map((e) => ({
              id: e.codigo, name: e.nome, short: e.nome_curto,
              slaEmDias: e.sla_dias, uuid: e.id,
              isGanho: e.is_ganho, isPerdido: e.is_perdido,
            }));
        }
        setEtapasPorFunil(porFunil);
      })
      .catch((e) => {
        // Falha aqui deixa o Pipeline sem colunas. Melhor gritar do que
        // mostrar uma tela vazia que parece "nenhum negócio": o console.error é para
        // quem depura, o toast é para quem está na tela — já fomos mordidos por um caso
        // (link expirado devolvendo ao login sem explicação) em que só o log existia.
        console.error('Não foi possível carregar funis e etapas:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar a configuração do funil. Tente recarregar a página.' });
      });
    return () => { vivo = false; };
  }, [api, toast]);

  // Negócios (Task 3): depende de etapasPorFunil já carregado, porque o
  // mapeador traduz etapa_id (uuid do banco) para o código legível de stage
  // usando essa lista. Sem isso, todo negócio cairia com stage null.
  React.useEffect(() => {
    if (!api || !Object.keys(etapasPorFunil).length) return;
    let vivo = true;
    const todasEtapas = Object.values(etapasPorFunil).flat();
    // Recarga (nao a carga inicial): descarta a leitura se uma gravacao aconteceu enquanto ela voltava, e nao avisa
    // na tela quando falha (so no console; um aviso por minuto em rede ruim seria pior que o defeito).
    const geracao = rastro.current.geracao;
    const recarregando = recarga > 0;
    const leituraVelha = () => recarregando && rastro.current.geracao !== geracao;
    // Chave por uuid (não por e.id): l.etapa_id vem cru do banco, e etapas
    // aqui já estão traduzidas — e.id é o código legível (PROPOSTA), e.uuid é
    // a chave do banco. Indexar por e.id faz a busca abaixo nunca bater, e
    // todo negócio cai com funil null (some do Pipeline sem erro nenhum).
    const funilDaEtapa = {};
    for (const [codigoFunil, lista] of Object.entries(etapasPorFunil)) {
      for (const e of lista) funilDaEtapa[e.uuid] = codigoFunil;
    }
    // O BANT (texto de Budget, Authority, Need e Timeline) mora em `negocio_bant`, nao em `negocios`: vem numa leitura
    // a parte, junto. Se ela falhar, os negocios aparecem do mesmo jeito (a pessoa e avisada) e, numa recarga, o BANT que
    // a tela ja tinha e mantido: `null` aqui significa "nao sei", e nao "nao ha BANT".
    const bantLido = api.listarBant
      ? api.listarBant().catch((e) => {
        console.error('Nao foi possivel carregar a qualificacao BANT:', e.message);
        if (vivo && !recarregando) toast({ kind: 'err', msg: 'Não foi possível carregar a qualificação BANT dos negócios.' });
        return null;
      })
      : Promise.resolve(null);
    Promise.all([api.listarNegocios(), bantLido])
      .then(([linhas, bantLinhas]) => {
        if (!vivo || leituraVelha()) return;
        const bantPorNegocio = bantLinhas
          ? Object.fromEntries(bantLinhas.map((b) => [b.negocio_id, { budget: b.budget, authority: b.authority, need: b.need, timeline: b.timeline }]))
          : null;
        setLeads((anteriores) => {
          const antes = bantPorNegocio ? null : new Map(anteriores.map((l) => [l.id, l.bant]));
          return linhas.map((l) => {
            const mapeado = { ...api.mapear(l, todasEtapas), funil: funilDaEtapa[l.etapa_id] || null };
            if (bantPorNegocio) return { ...mapeado, bant: bantPorNegocio[l.id] || mapeado.bant };
            const anterior = antes.get(l.id);
            return anterior ? { ...mapeado, bant: anterior } : mapeado;
          });
        });

        // A timeline vem junto, numa consulta so para os negocios carregados.
        // Uma consulta por negocio aberto seria mais simples e faria dezenas de
        // idas ao banco enquanto o vendedor navega pelo funil.
        if (!api.listarTimeline) return;
        api.listarTimeline(linhas.map((l) => l.id))
          .then((eventos) => {
            if (!vivo || leituraVelha()) return;
            const porNegocio = {};
            for (const ev of eventos || []) {
              (porNegocio[ev.negocio_id] ||= []).push(eventoDoBanco(ev));
            }
            setTimeline(porNegocio);
          })
          .catch((e) => {
            // Timeline vazia por FALHA e timeline vazia por negocio novo sao
            // identicas na tela. Sem este aviso, o vendedor concluiria que nada
            // aconteceu no negocio.
            console.error('Nao foi possivel carregar o historico dos negocios:', e.message);
            if (vivo && !recarregando) toast({ kind: 'err', msg: 'Não foi possível carregar o histórico dos negócios.' });
          });
      })
      .catch((e) => {
        // Mesmo raciocínio do efeito de funis/etapas acima: Pipeline vazio sem
        // aviso é indistinguível de "não há negócios" — já mordeu este projeto
        // duas vezes. console.error é para quem depura, o toast é para quem
        // está na tela.
        console.error('Não foi possível carregar os negócios:', e.message);
        if (vivo && !recarregando) toast({ kind: 'err', msg: 'Não foi possível carregar os negócios. Tente recarregar a página.' });
      });
    return () => { vivo = false; };
  }, [api, etapasPorFunil, toast, recarga]);

  // Usuários (Task 6): o estado inicial já contém o usuário da sessão, então
  // uma falha aqui deixaria a tela de Admin com uma equipe de uma pessoa só —
  // e pareceria correta. Sem o toast, "o banco caiu" fica indistinguível de
  // "você é o único cadastrado"; por isso o aviso aqui é tão obrigatório
  // quanto nos efeitos de funis/etapas e negócios acima.
  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    api.listarUsuarios()
      .then((linhas) => {
        if (!vivo) return;
        setUsers(linhas.map((u) => ({
          id: u.id, nome: u.nome, initials: u.iniciais, email: u.email, telefone: u.telefone || "",
          papel: u.papel, ativo: u.ativo, idPessoaVendedor: u.id_pessoa_vendedor,
          ultimoAcesso: u.ultimo_acesso_em || "—",
        })));
      })
      .catch((e) => {
        console.error('Não foi possível carregar os usuários:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar os usuários. Tente recarregar a página.' });
      });
    return () => { vivo = false; };
  }, [api, toast]);

  // Agenda (tarefas). Ate 21/09 este efeito nao existia: `ALERT_SEEDS` e `[]`
  // e nada preenchia o estado, entao a agenda comecava vazia toda vez e o que
  // o vendedor criava na sessao morria no F5.
  //
  // `vencido` e derivado AQUI, no carregamento, e nao guardado no banco: uma
  // coluna de "vencido" nasceria errada no instante seguinte. `quando` (o
  // texto "Hoje, 14:30") tambem e derivacao de tela, nao dado.
  React.useEffect(() => {
    if (!api?.listarTarefas) return;
    let vivo = true;
    const geracao = rastro.current.geracao;
    const recarregando = recarga > 0;
    api.listarTarefas()
      .then((linhas) => {
        if (!vivo || (recarregando && rastro.current.geracao !== geracao)) return;
        setAlerts(linhas.map((l) => vestirAlerta(api.mapearTarefa(l))));
      })
      .catch((e) => {
        console.error('Nao foi possivel carregar a agenda:', e.message);
        if (vivo && !recarregando) toast({ kind: 'err', msg: 'Não foi possível carregar a agenda. Tente recarregar a página.' });
      });
    return () => { vivo = false; };
  }, [api, toast, recarga]);

  // Convites que EU recebi (tarefa_envolvidos onde user_id = eu), por tarefa.
  // O sino usa para oferecer aceitar/recusar e mostrar a resposta ja dada.
  const [meusConvites, setMeusConvites] = React.useState({});
  React.useEffect(() => {
    if (!api?.listarMeusConvites || !meBase?.id) return;
    let vivo = true;
    api.listarMeusConvites(meBase.id)
      .then((linhas) => { if (vivo) setMeusConvites(Object.fromEntries(linhas.map((l) => [l.tarefa_id, l.status]))); })
      .catch((e) => {
        console.error('Nao foi possivel carregar os convites:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar seus convites de tarefa.' });
      });
    return () => { vivo = false; };
  }, [api, toast, meBase?.id]);

  // Aceitar ou recusar -- so o proprio participante (RLS da 0063). Aceitar faz
  // o horario contar como ocupado na agenda dele (agenda_conflito).
  const responderConvite = React.useCallback(async (tarefaId, aceitar) => {
    const status = aceitar ? "aceito" : "recusado";
    if (!api?.responderConvite) return false;
    try {
      await api.responderConvite(tarefaId, meRef.current.id, status);
    } catch (e) {
      console.error('Nao foi possivel responder ao convite:', e.code || '', e.message);
      toast({
        kind: "err",
        msg: e.code === '42501'
          ? "Não foi possível responder: o convite não é seu ou já foi removido."
          : "Não foi possível responder ao convite.",
      });
      return false;
    }
    setMeusConvites((m) => ({ ...m, [tarefaId]: status }));
    toast({ kind: "ok", msg: aceitar ? "Convite aceito · o horário passa a contar como ocupado na sua agenda" : "Convite recusado" });
    return true;
  }, [api, toast]);

  // Livre/ocupado de um colega -- o seletor de envolvidos consulta antes de
  // vincular, para avisar que a pessoa entrara como pendente.
  const consultarAgenda = React.useCallback((userId, inicio, fim) => (
    api?.consultarAgenda ? api.consultarAgenda(userId, inicio, fim) : Promise.resolve([])
  ), [api]);

  // Carteira de clientes. Até 14/09 este efeito não existia: `CLIENTES` é `[]`
  // desde o commit inicial e nada preenchia o estado, então os 2.201 clientes do
  // banco nunca chegaram à tela — e o envio ao ERP barrava em "Cliente não tem
  // idPessoaCliente", porque `cliente.erpId` nunca existia.
  //
  // ATENÇÃO ao que a RLS devolve aqui: `clientes_sel` (0022:188) só mostra o
  // cliente que já tem negócio do usuário apontando para ele, ou tudo se o papel
  // for admin/backoffice. Um vendedor vê ZERO desta carteira importada, porque
  // nenhum dos 2.201 tem negócio ainda. Isso é decisão de modelo pendente com o
  // Kaio (spec de 14/09) — aqui só não se finge que é problema de carga.
  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    (async () => {
      // Os nichos primeiro, num try proprio: `listarClientes` engole a falha deles para
      // nao bloquear a carteira, entao e aqui que o usuario fica sabendo. A checagem
      // de tipo e para as apis de teste antigas, que nao tem o metodo; a real sempre tem.
      if (typeof api.carregarNichos === 'function') {
        try {
          await api.carregarNichos();
        } catch (e) {
          console.error('Não foi possível carregar os nichos:', e.message);
          if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar os nichos. O filtro e o cadastro de nicho podem falhar.' });
        }
      }
      try {
        const linhas = await api.listarClientes();
        if (!vivo) return;
        setClientes(linhas.map(api.mapearCliente));
      } catch (e) {
        // Mesmo raciocínio dos outros efeitos: carteira vazia sem aviso é
        // indistinguível de "não há clientes" — e aqui ainda por cima levaria o
        // vendedor a cadastrar de novo quem já existe no ERP.
        console.error('Não foi possível carregar a carteira de clientes:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar os clientes. Tente recarregar a página.' });
      }
    })();
    return () => { vivo = false; };
  }, [api, toast]);

  // Catálogo. Mesma história da carteira: `PRODUTOS` (src/data.jsx:134) é `[]`
  // desde o commit inicial e nada preenchia o estado — 12.723 produtos no banco,
  // zero na tela, e sem produto não se monta proposta.
  //
  // São doze vezes o teto de 1.000 do PostgREST, então a carga é paginada e
  // confere o total (ver `lerTudo` em data/api.js). Custo aceito: treze idas ao
  // banco na subida. Carregar sob demanda, só ao abrir a proposta, é o próximo
  // passo — está registrado na spec, não esquecido.
  const [produtos, setProdutos] = React.useState(PRODUTOS);

  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    (async () => {
      try {
        const linhas = await api.listarProdutos();
        if (!vivo) return;
        setProdutos(linhas.map(api.mapearProduto));
      } catch (e) {
        console.error('Não foi possível carregar o catálogo de produtos:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar o catálogo. Recarregue a página antes de montar propostas.' });
      }
    })();
    return () => { vivo = false; };
  }, [api, toast]);

  // Descrição técnica, dimensões e peso (0070). Só essas colunas: o resto do
  // produto vem do Nomus. A tela só muda depois que o banco confirma -- RLS que
  // barra devolve 0 linhas sem erro, e a api traduz em 42501.
  const atualizarProduto = React.useCallback(async (id, patch) => {
    if (!api?.atualizarProduto) {
      setProdutos(arr => arr.map(p => p.id === id ? { ...p, ...patch } : p));
      return true;
    }
    try {
      const salvo = await api.atualizarProduto(id, patch);
      setProdutos(arr => arr.map(p => p.id === id ? { ...p, ...salvo } : p));
      toast({ kind: "ok", msg: "Produto atualizado" });
      return true;
    } catch (e) {
      console.error('Nao foi possivel salvar o produto:', e.code || '', e.message);
      toast({
        kind: "err",
        msg: e.code === '42501' ? "Seu cargo não pode editar produtos." : "Não foi possível salvar o produto.",
      });
      return false;
    }
  }, [api, toast]);

  // Preço, bloqueio e saldo ao vivo (consultar-produtos-nomus, 23/09). Sem
  // banco, NÃO simula: lança, e a tela mostra "não conferido" -- a simulação
  // que existia aqui chegou a barrar pedido por bloqueio sorteado.
  const consultarProdutosNomus = React.useCallback(async (ids) => {
    if (!api?.consultarProdutosNomus) throw new Error('Consulta ao Nomus indisponível sem conexão com o banco.');
    return api.consultarProdutosNomus(ids);
  }, [api]);

  // Lookups do Nomus (0052). Vêm do banco, não de constante: a lista de condições
  // de pagamento escrita à mão no protótipo tinha nove dos dez nomes errados, e o
  // id é o que o ERP usa para decidir o vencimento do boleto.
  //
  // Também são o que traduz a escolha da tela (id do Nomus) no uuid que a coluna
  // guarda — por isso ficam no store e não dentro do componente: quem grava é o
  // store, e sem a lista carregada ele não tem como gravar.
  const [condicoesPagamento, setCondicoesPagamento] = React.useState([]);
  const [tiposVenda, setTiposVenda] = React.useState([]);

  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    // `try` em volta da CHAMADA, não só `.catch` na promessa: uma api sem estes
    // métodos (dublê desatualizado, versão antiga) estoura de forma síncrona aqui
    // dentro, e a React derruba a árvore inteira em vez de mostrar o aviso.
    (async () => {
      try {
        const [cond, tipos] = await Promise.all([api.listarCondicoesPagamento(), api.listarTiposVenda()]);
        if (!vivo) return;
        setCondicoesPagamento(cond);
        setTiposVenda(tipos);
      } catch (e) {
        // Mesmo raciocínio dos efeitos acima, com uma agravante: sem estas listas
        // a proposta não grava tipo de venda nem condição, e o vendedor só
        // descobriria na hora de enviar ao ERP — com a venda já fechada.
        console.error('Não foi possível carregar as condições de pagamento e tipos de venda:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar as condições de pagamento. Recarregue a página antes de criar propostas.' });
      }
    })();
    return () => { vivo = false; };
  }, [api, toast]);

  const lookupsNomus = React.useMemo(
    () => ({ condicoes: condicoesPagamento, tiposVenda }),
    [condicoesPagamento, tiposVenda],
  );

  // Cargos e permissões (Parte 1 de "cargos como dados"). Antes viviam só em
  // constantes de auth.jsx: a tela de Admin editava a memória do navegador e
  // nada sobrevivia a um F5, porque RBAC.emit() já não grava mais nada.
  const [papeis, setPapeis] = React.useState({});

  // Tambem chamada depois que o Admin grava um cargo (Parte 2): a tela passa a
  // mostrar o que o banco guardou, nao o que ela pediu.
  const carregarPapeis = React.useCallback(async () => {
    const linhas = await api.listarPapeis();
    const mapa = {};
    for (const p of linhas) {
      mapa[p.codigo] = {
        label: p.label, descricao: p.descricao, home: p.home,
        ordem: p.ordem, builtin: p.builtin,
        escopos: Object.fromEntries((p.papel_escopos || []).map((e) => [e.dominio, e.escopo])),
        permissoes: Object.fromEntries((p.papel_permissoes || []).map((x) => [x.acao, x.permitido])),
      };
    }
    return mapa;
  }, [api]);

  const aplicarPapeis = React.useCallback((mapa) => {
    setPapeis(mapa);
    RBAC.sincronizarComBanco(mapa);
  }, []);

  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    carregarPapeis()
      .then((mapa) => { if (vivo) aplicarPapeis(mapa); })
      .catch((e) => {
        // Falhar calado aqui é grave: sem a matriz, `usePermission` nega tudo e
        // o CRM parece quebrado sem explicar por quê.
        console.error('Não foi possível carregar cargos e permissões:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar as permissões. Tente recarregar a página.' });
      });
    return () => { vivo = false; };
  }, [api, toast, carregarPapeis, aplicarPapeis]);

  // Propostas (Etapa 3a · Task 1). Antes 100% memória: uma proposta gerada sumia
  // no F5. Carregadas no startup, como negócios e usuários — e não sob demanda ao
  // abrir o drawer — porque o card do negócio já mostra a contagem de propostas
  // (src/drawer.jsx:980) e o gate de etapa consulta a lista (src/kanban.jsx:421),
  // ou seja, a tela precisa delas antes de alguém abrir o drawer.
  // O efeito fica declarado aqui, junto do estado que ele preenche; `propostas` é
  // declarado mais acima porque outros callbacks já dependem dele.
  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    const geracao = rastro.current.geracao;
    const recarregando = recarga > 0;
    api.listarPropostas()
      .then((linhas) => {
        if (!vivo || (recarregando && rastro.current.geracao !== geracao)) return;
        const mapa = {};
        for (const l of linhas) {
          (mapa[l.negocio_id] ||= []).push(api.mapearProposta(l));
        }
        setPropostas(mapa);
      })
      .catch((e) => {
        // Mesmo raciocínio dos efeitos acima: sem aviso, "o banco caiu" fica
        // igual a "este negócio não tem proposta" — e aqui é pior, porque some
        // dinheiro da tela e alguém pode refazer uma proposta que já existe.
        console.error('Não foi possível carregar as propostas:', e.message);
        if (vivo && !recarregando) toast({ kind: 'err', msg: 'Não foi possível carregar as propostas. Tente recarregar a página.' });
      });
    return () => { vivo = false; };
  }, [api, toast, recarga]);

  const [me, setMe] = React.useState({ ...meBase });
  // ----- Ajustes: parâmetros comerciais, tabelas e transportadoras -----
  const [config, setConfig] = React.useState(() => ({
    tabelas: {},          // { [tabelaId]: { ativa, descontoBase } }
    transportadoras: {},  // { [idPessoa]: { ativa, prazoDias, regiao } }
    comercial: {
      // Nenhuma ate alguem escolher em Ajustes: "tp_premium" era do prototipo.
      tabelaPadrao: null,
      validadeDias: 7,
      prazoFaturamentoDias: 20,
      modalidadeTransporte: 1,
      servicoEntrega: "Entrega padrão",
      formaPagamentoId: 10,
      condicaoPagamentoId: 1,
      freteGratisAcima: 0,
      seguroPct: 0,
      descontoMax: { closer: 10, sdr: 5, bdr: 5, backoffice: 12, diretor: 25 },
    },
  }));
  // Ref sempre atual: os elos da cadeia precisam do papel vigente no instante
  // da ação, mesmo que o callback tenha sido criado num render anterior.
  const meRef = React.useRef(me);
  meRef.current = me;
  const [anexos, setAnexos]     = React.useState(ANEXOS);
  const [propostas, setPropostas] = React.useState({});
  const propostasRef = React.useRef(propostas);
  propostasRef.current = propostas;
  const [propostaSeq, setPropostaSeq] = React.useState(8201); // nº sequencial de proposta (CP). Ex. real: CP 8201.
  // Ref espelhando propostaSeq: o nº do pedido não pode depender do agendamento
  // do setState (dois envios no mesmo tick gerariam o mesmo CP).
  const propostaSeqRef = React.useRef(8201);
  // Lista viva de usuários (idPessoaVendedor é editável em Admin → Usuários)
  const usersRef = React.useRef([]);
  const [controleSeq, setControleSeq] = React.useState(0);  // nº de controle interno do CRM (independente do ERP)
  const [notifications, setNotifications] = React.useState([]); // notificações de sistema (menções em tarefas/alertas)
  // Tabelas de preco REAIS, do banco. Ate 16/09 a tela usava quatro inventadas
  // de src/data.jsx, e escolher uma no cadastro do cliente dava
  // `22P02 invalid input syntax for type uuid` -- o id da constante nem uuid era.
  const [tabelasPreco, setTabelasPreco] = React.useState([]);
  // ----- Gestão: metas, taxas de comissão e pós-venda -----
  // Metas: vem da tabela `metas` (0010). Ate 22/09 eram numeros fixos aqui
  // (empresa R$ 1,4 mi, individual R$ 300 mil) e todo "% da meta" da tela era
  // percentual de um numero inventado. Comecam em ZERO ate o banco responder:
  // se a carga falhar, a tela mostra "sem meta" com um aviso -- nunca um valor
  // que ninguem definiu.
  const [metas, setMetas] = React.useState({
    empresa: 0,
    porFunil: {},
    individuais: {},
    competencias: {},
    // Metas dos agentes de IA: NAO tem lugar no banco -- a 0010 so aceita
    // escopo empresa/funil/usuario/papel. Seguem em memoria, e a tela de
    // Gestao avisa que nao sao gravadas. Gravar exige coluna nova (decisao).
    agentes: {
      ag_helios:    { leadsEnriquecidos: 600 },
      ag_wpp:       { leadsEnriquecidos: 450 },
      ag_qualifier: { leadsEnriquecidos: 320 },
    },
  });
  const [metasLinhas, setMetasLinhas] = React.useState([]);
  // O que o banco tem para o mes atual (meta vigente). O "Salvar metas" grava
  // so o que difere disto.
  const metasBaseRef = React.useRef(null);
  // Ha edicao nao salva? Enquanto houver, recarregar do banco NAO sobrescreve o
  // rascunho -- senao uma gravacao parcial apagaria da tela o que o gestor
  // digitou e ainda nao conseguiu salvar.
  const metasSujasRef = React.useRef(false);
  const MES_ATUAL_ID = MONTHS[0].id;

  React.useEffect(() => {
    if (!api?.listarMetas) return;
    let vivo = true;
    api.listarMetas()
      .then((linhas) => { if (vivo) setMetasLinhas(linhas); })
      .catch((e) => {
        console.error('Nao foi possivel carregar as metas:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar as metas. Os relatórios ficam sem meta até recarregar a página.' });
      });
    return () => { vivo = false; };
  }, [api, toast]);

  // Deriva a meta VIGENTE do mes atual. Depende de `funis` porque a meta de
  // funil chega por uuid e a tela a indexa pelo codigo ("projetos").
  React.useEffect(() => {
    if (!api?.metasVigentes) return;
    const vigentes = api.metasVigentes(metasLinhas, MES_ATUAL_ID, funis);
    metasBaseRef.current = vigentes;
    if (!metasSujasRef.current) setMetas((m) => ({ ...vigentes, agentes: m.agentes }));
  }, [api, metasLinhas, funis, MES_ATUAL_ID]);

  // Meta vigente de QUALQUER mes -- os relatorios pedem a do mes selecionado.
  // Le o que esta gravado, nunca o rascunho da tela de Gestao.
  const metasDoMes = React.useCallback((monthId) => (
    api?.metasVigentes ? api.metasVigentes(metasLinhas, monthId, funis) : metas
  ), [api, metasLinhas, funis, metas]);
  const [comissaoRates, setComissaoRates] = React.useState({ ...COMMISSION_RATES });
  const [posVenda, setPosVenda] = React.useState(POSVENDA_SEED);
  const [chamados, setChamados] = React.useState(CHAMADOS);
  const [sacSeq, setSacSeq] = React.useState(CHAMADOS.length);
  const sacSeqRef = React.useRef(CHAMADOS.length);
  const [undoBuffer, setUndoBuffer] = React.useState(null); // { message, undoFn, timer }

  // Dispara todos os webhooks ativos cujo evento/trigger corresponde — registra em API Logs.
  const fireWebhookByEvent = React.useCallback((evento, payload) => {
    const targets = webhooks.filter(w => w.ativo && (w.trigger === evento || w.evento === evento));
    targets.forEach(w => {
      setWebhooks(arr => arr.map(x => x.id === w.id ? { ...x, lastFire: "agora", lastStatus: 200 } : x));
      setApiLogs(arr => [{
        ts: nowStamp(),
        agente: `Webhook \u00b7 ${evento}`,
        token: w.token ?? ("wh_" + w.id.slice(-6)),
        method: "POST",
        endpoint: (w.url || "").replace(/^https?:\/\/[^\/]+/, "") || "/webhook",
        status: 200,
        time: Math.floor(Math.random() * 100 + 50),
        payloadJson: JSON.stringify({ event: evento, timestamp: new Date().toISOString(), data: payload }, null, 2),
      }, ...arr].slice(0, 50));
    });
  }, [webhooks]);

  // Toast com bot\u00e3o "Desfazer" (janela de 5s). Ap\u00f3s o prazo, a a\u00e7\u00e3o vira definitiva.
  const toastWithUndo = React.useCallback((message, undoFn, kind = "warn") => {
    setUndoBuffer(prev => { if (prev?.timer) clearTimeout(prev.timer); return null; });
    const timer = setTimeout(() => setUndoBuffer(null), 5000);
    setUndoBuffer({ message, undoFn, timer });
    toast({ kind, msg: message });
  }, [toast]);
  const doUndo = React.useCallback(() => {
    setUndoBuffer(prev => {
      if (prev) { clearTimeout(prev.timer); try { prev.undoFn(); } catch {} toast({ kind: "ok", msg: "A\u00e7\u00e3o desfeita" }); }
      return null;
    });
  }, [toast]);
  const dismissUndo = React.useCallback(() => {
    setUndoBuffer(prev => { if (prev?.timer) clearTimeout(prev.timer); return null; });
  }, []);

  // Canais de venda (especificadores) = clientes com isCanal. Lista derivada,
  // fonte única = clientes.
  //
  // AS MÉTRICAS SÃO CALCULADAS AQUI, e isso é conserto de 15/09 (achado do Kaio:
  // "alguns relatórios ainda não puxam o dado corretamente").
  //
  // Antes, receita/deals/conversão saíam de `c.canalReceitaGerada`,
  // `c.canalDeals` e `c.canalTaxaConv` — três campos que NÃO EXISTEM. `clientes`
  // tem `is_canal`, `canal_tipo`, `canal_responsavel`, `canal_ativo`,
  // `parceria_repasse` e `pct_repasse`, e mais nada (conferido no banco). O `|| 0`
  // engolia a ausência, e os 281 canais apareciam todos com receita zero, zero
  // deals e 0% de conversão — inclusive no painel "Conversão por canal", onde a
  // ordenação por conversão não ordenava nada porque tudo empatava em zero.
  //
  // Calcular é melhor que criar coluna: receita de canal é a SOMA dos negócios
  // ganhos dele. Coluna denormalizada precisaria de alguém para manter em dia, e
  // quando desandasse desandaria em silêncio — que é exatamente o defeito que
  // acabou de ser encontrado.
  const metricasPorCanal = React.useMemo(() => {
    const m = new Map();
    const pega = (id) => {
      if (!m.has(id)) m.set(id, { deals: 0, ganhos: 0, receita: 0, clientes: new Set() });
      return m.get(id);
    };
    for (const l of leads) {
      if (!l.especificadorId) continue;
      const e = pega(l.especificadorId);
      e.deals += 1;
      if (l.clienteId) e.clientes.add(l.clienteId);
      // Perdido não conta como ganho mesmo se a etapa for de ganho: as duas
      // marcas existem e podem discordar.
      if (l.stage === "GANHO" && !l.perdido) { e.ganhos += 1; e.receita += (l.valor || 0); }
    }
    return m;
  }, [leads]);

  // Contagem do funil, calculada dos negocios. Alimenta cinco paineis em tres
  // arquivos (Resumo, Relatorios e o BI de detalhe), que ate 16/09 liam
  // `FUNNEL_COUNTS_PROJETOS` e `FUNNEL_COUNTS_LOJISTAS` -- duas constantes de
  // src/data.jsx com `count: 0, pct: 0` escritos a mao em todas as etapas.
  //
  // O QUE `pct` E, e o que ele NAO e: e a fatia do funil que esta naquela etapa
  // AGORA (count / total do funil). NAO e conversao de etapa para etapa -- para
  // isso seria preciso saber por onde cada negocio passou, e o CRM nao guarda
  // esse historico de forma confiavel. Chamar ocupacao de conversao seria o tipo
  // de numero que decide preco e esta errado.
  //
  // Perdido fica de fora: negocio perdido nao ocupa etapa, esta encerrado.
  const contagemDoFunil = React.useMemo(() => {
    const porFunil = {};
    for (const [codigo, etapas] of Object.entries(etapasPorFunil || {})) {
      const doFunil = leads.filter((l) => l.funil === codigo && !l.perdido);
      const total = doFunil.length;
      porFunil[codigo] = (etapas || [])
        .filter((e) => !e.isPerdido)
        .map((e) => {
          const count = doFunil.filter((l) => l.stage === e.id).length;
          return {
            stage: e.id,
            nome: e.name,
            count,
            pct: total ? Math.round((count / total) * 100) : 0,
          };
        });
    }
    return porFunil;
  }, [leads, etapasPorFunil]);

  const especificadores = React.useMemo(() => clientes
    .filter(c => c.isCanal)
    .map(c => {
      const m = metricasPorCanal.get(c.id);
      const deals = m?.deals ?? 0;
      const receitaGerada = m?.receita ?? 0;
      // Sem negócio nenhum a taxa é INDEFINIDA, não zero: "0% de conversão" diz
      // que o canal tentou e não fechou, e a maioria destes 281 nunca teve
      // negócio. Quem exibe decide o que fazer com null.
      const taxaConv = deals > 0 ? Math.round((m.ganhos / deals) * 100) : null;
      return {
      id: c.id,
      nome: c.nome,
      responsavel: c.canalResponsavel || c.contato || "",
      tipo: c.canalTipo || "Outro",
      cidade: c.cidade || "—",
      parceriaRepasse: !!c.parceriaRepasse,
      pctRepasse: c.pctRepasse || 0,
      clientesCount: m?.clientes.size ?? 0,
      receitaGerada,
      deals,
      ganhos: m?.ganhos ?? 0,
      taxaConv,
      repassePago: Math.round(receitaGerada * ((c.pctRepasse || 0) / 100)),
      ativo: c.canalAtivo !== false,
      inicio: c.canalInicio || c.criadoEm || "—",
      clienteId: c.id,
      };
    })
    // Ja ordenado por receita: e o que faz o "Ranking de arquitetos & canais"
    // ser de fato um ranking quando `slice(0, 6)` corta a lista.
    .sort((a, b) => b.receitaGerada - a.receitaGerada), [clientes, metricasPorCanal]);

  // ----- Lead actions -----
  // Assíncrona porque grava no banco antes de existir na tela (mesma razão do
  // moveLeadStage logo abaixo). O toast de sucesso já mora aqui dentro, só no fim
  // do caminho feliz — as duas falhas abaixo retornam antes de chegar nele. Devolve
  // o id do lead quando dá certo e `undefined` quando não: quem precisar de um
  // follow-up condicionado ao sucesso (ex.: fechar o modal) precisa dar `await` e
  // checar o retorno, não presumir sucesso assim que a chamada dispara.
  const createLead = React.useCallback(async (data) => {
    // Sem banco (teste de unidade com StoreProvider isolado): id sintético local,
    // igual ao comportamento de antes desta tarefa.
    let id = "ld_" + (Date.now()).toString(36);
    // Quem TROUXE o negocio (Kaio, 25/09): a ADM de vendas ou o orcamentista
    // cadastram pelo closer "sem tirar que o closer trouxe o negocio desde o
    // inicio". So o formulario de quem tem escopo `team` manda `originadorId`.
    // Sem ele, e quem esta na sessao -- NUNCA o `ownerId`: deduzir do dono
    // faria o SDR perder a visibilidade do negocio ao repassa-lo (ver o teste
    // de store-negocios sobre originador_user_id).
    const quemTrouxe = data.originadorId || meRef.current.id;
    const cadastrouPorOutro = quemTrouxe !== meRef.current.id;
    // As telas só conhecem os códigos legíveis (ex. "projetos", "OUT_NOVA");
    // calculados aqui fora do bloco `if (api)` porque o card final também
    // precisa do código do funil, com ou sem banco.
    const funilCodigo = data.funil || "projetos";
    const stageCodigo = data.stage || "OUT_NOVA";
    let linha = null;
    if (api) {
      // O banco só aceita as chaves (uuid). Converte antes de gravar.
      const funilUuid = funis.find((f) => f.id === funilCodigo)?.uuid;
      const etapaUuid = (etapasPorFunil[funilCodigo] || []).find((e) => e.id === stageCodigo)?.uuid;
      if (!funilUuid || !etapaUuid) {
        console.error('Funil ou etapa desconhecidos ao criar negócio:', funilCodigo, stageCodigo);
        toast({ kind: "err", msg: "Não foi possível criar o negócio: funil ou etapa inválidos." });
        return;
      }
      try {
        // Grava primeiro: um card que aparece na tela sem existir no banco some
        // no próximo reload sem aviso nenhum — pior do que a tela reagir devagar.
        linha = await api.criarNegocio({
          empresa: data.empresa, contato: data.contato || null, cargo: data.cargo || null,
          contato_decisor: !!data.contatoDecisor,
          email: data.email || null, telefone: data.telefone || null, segmento: data.segmento || null,
          valor: Number(data.valor) || 0,
          funil_id: funilUuid, etapa_id: etapaUuid,
          tipo_cliente: data.tipoCliente || "projeto_direto",
          calor: data.calor || "warm",
          origem: data.origem || "humano",
          owner_id: data.ownerId || meRef.current.id,
          // originador_user_id é a coluna em que a política negocios_sel se apoia
          // (owner_id = auth.uid() or originador_user_id = auth.uid(),
          // db/migrations/0022:160-167). Sem ela, o SDR que cria o negócio perde
          // a visibilidade da linha sob RLS assim que ele for repassado a um
          // closer (owner_id muda, e não sobra nenhuma coluna apontando para
          // quem originou) — e o escopo "meus originados" (src/auth.jsx:468)
          // fica permanentemente vazio (achado 4 da revisão final).
          //
          // Quem trouxe o negocio -- ver `quemTrouxe` acima. Para quem nao tem
          // escopo `team`, continua sendo o usuario da sessao.
          originador_user_id: quemTrouxe,
          // Gravado explícito, e não deixado para o default da coluna: esta é
          // a coluna que a RLS lê, e um default silencioso some do código no
          // dia em que alguém migrar a criação de negócios de agente de IA
          // para este mesmo caminho.
          originador_tipo: "humano",
          cliente_id: data.clienteId || null,
          // A tabela do cliente fica FIXA no negocio (0077, Kaio 25/09): trocar
          // a do cliente depois nao muda a deste negocio.
          tabela_id: data.tabelaId || null,
          especificador_id: data.especificadorId || null,
          proxima_acao: data.proximaAcao || null,
          parceria_repasse: !!data.parceriaRepasse,
          // pct_repasse NAO vai daqui: a decisao B (27/08) tirou a % do repasse
          // da mao do vendedor. O gatilho public.deriva_pct_repasse (migration
          // 0037) preenche a partir do cadastro do canal (clientes.pct_repasse),
          // e criarNegocio faz .select() -- entao `linha` ja volta com a % certa.
          // Enviar pct_repasse aqui hoje daria 42501 (coluna revogada do authenticated).
        });
        id = linha.id;
      } catch (e) {
        console.error('Não foi possível criar o negócio:', e.message);
        toast({ kind: "err", msg: "Não foi possível criar o negócio. Tente novamente." });
        return;
      }
      if (cadastrouPorOutro) {
        try {
          await api.registrarCadeia(id, { seq: 1, tipo: "cadastro", atorTipo: "humano", atorId: meRef.current.id, atorNome: meRef.current.nome, papel: meRef.current.papel });
        } catch (e) {
          // O negocio existe; so a nota de quem cadastrou nao entrou. Avisar,
          // porque e ela que diferencia "trouxe" de "cadastrou" depois.
          console.error('Não foi possível registrar quem cadastrou o negócio:', e.message);
          toast({ kind: "err", msg: "Negócio criado, mas não foi possível registrar quem o cadastrou." });
        }
      }
    }
    // Campos sem coluna em `negocios` (tags/cadeia/bant vivem em tabelas M:N
    // à parte; contatoDecisor/dynamic/ultimaAtividade não existem no schema):
    // o banco nunca vai devolvê-los, então ficam por conta do formulário —
    // com ou sem banco.
    // `contatoDecisor` saiu daqui na 0046: agora tem coluna em negocios e volta
    // do banco como qualquer outro campo. Os demais continuam sem coluna.
    const camposSemColuna = {
      tags: data.tags || [],
      criadoEm: "manual",
      historicoEtapas: [],
      dynamic: null,
      bant: { budget: null, authority: null, need: null, timeline: null },
      // A origem e quem TROUXE -- o mesmo originador_user_id que vai ao banco.
      // seq 0, como a origem derivada depois do F5 (cadeiaDoNegocio). Quem
      // cadastrou pelo closer vem em seguida, como o elo gravado acima.
      cadeia: [
        { seq: 0, tipo: "primeiro_contato", atorTipo: "humano", atorId: quemTrouxe, atorNome: (usersRef.current.find(u => u.id === quemTrouxe) || meRef.current).nome, papel: null, data: new Date().toISOString(), meta: { canal: "manual" } },
        ...(cadastrouPorOutro ? [{ seq: 1, tipo: "cadastro", atorTipo: "humano", atorId: meRef.current.id, atorNome: meRef.current.nome, papel: meRef.current.papel, data: new Date().toISOString(), meta: {} }] : []),
      ],
      ultimaAtividade: new Date().toISOString(),
    };
    // Com banco: o card nasce da linha que voltou da escrita, não de um objeto
    // remontado a partir do formulário — bantScore (default 0 na coluna) e os
    // fallbacks amigáveis ("—" para cargo/segmento vazios) divergiam do que o
    // banco realmente guardou, e sumiam/trocavam de valor sozinhos no próximo
    // reload. `api.mapear` é a mesma tradução usada ao carregar a lista.
    const lead = (api && linha)
      ? { ...api.mapear(linha, Object.values(etapasPorFunil).flat()), funil: funilCodigo, ...camposSemColuna }
      : {
          id,
          empresa: data.empresa,
          contato: data.contato,
          cargo: data.cargo || "—",
          email: data.email || "",
          telefone: data.telefone || "",
          segmento: data.segmento || "—",
          valor: Number(data.valor) || 0,
          stage: data.stage || "OUT_NOVA",
          origem: data.origem || "humano",
          agenteId: data.agenteId || null,
          ownerId: data.ownerId || meRef.current.id,
          calor: data.calor || "warm",
          diasNoEstagio: 0,
          proximaAcao: data.proximaAcao || "—",
          proximaDue: false,
          bantScore: 1,
          motivoPerda: null,
          obsPerda: null,
          externalId: null,
          funil: funilCodigo,
          clienteId: data.clienteId || null,
          especificadorId: data.especificadorId || null,
          tipoCliente: data.tipoCliente || "projeto_direto",
          // Saiu de `camposSemColuna` na 0046, então precisa ser explícito aqui:
          // este ramo é o modo sem banco, que monta o card na mão.
          contatoDecisor: !!data.contatoDecisor,
          parceriaRepasse: !!data.parceriaRepasse,
          pctRepasse: Number(data.pctRepasse) || 0,
          repasse: null,
          perdido: false,
          originador: data.originador || { tipo: "sdr", id: data.ownerId || meRef.current.id, nome: (usersRef.current.find(u => u.id === (data.ownerId || meRef.current.id)) || meRef.current).nome },
          criadoData: new Date(),
          ultimoMovimento: new Date().toISOString(),
          ...camposSemColuna,
        };
    setLeads(arr => [lead, ...arr]);
    appendTimeline(id, {
      tipo: "criacao", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Lead criado manualmente.</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: `Lead "${lead.empresa}" criado` });
    if (lead.ownerId && lead.ownerId !== meRef.current.id) {
      notificarRef.current?.([lead.ownerId], {
        tipo: 'repasse', titulo: `Novo negócio para você: ${lead.empresa}`,
        contexto: `cadastrado por ${meRef.current.nome}`, leadId: id,
      });
    }
    return id;
  }, [api, funis, etapasPorFunil]);

  // Grava e SÓ ENTÃO mexe na tela. Devolve `true`/`false`, como `moveLeadStage`.
  //
  // Até 14/09 isto era uma linha de `setLeads` e nada mais: vínculo de cliente,
  // canal, repasse, número do pedido e data prometida morriam no F5. Três
  // comentários no projeto (drawer.jsx:902, modals.jsx:942) existiam só para
  // lembrar disso e ordenar as chamadas em volta da limitação.
  //
  // Pessimista pelo mesmo motivo de `moveLeadStage`, e aqui mais forte: a policy
  // `negocios_upd` (0022:176) recusa negócio de outro dono E, pelo `with check`,
  // recusa passar o negócio para outra pessoa — que é o que o form de edição faz
  // quando troca o responsável. Atualizar a tela antes deixaria o card mostrando
  // um dono que o banco não aceitou.
  const updateLead = React.useCallback(async (id, patch) => {
    if (api) {
      try {
        const dbPatch = api.negocioPatchParaBanco(patch);
        // O TEXTO do BANT vai para `negocio_bant` (o modal de edicao e a gaveta passam por aqui); em `negocios` vai so o
        // numero (`bant_score`), que `negocioPatchParaBanco` ja calcula. O texto vem primeiro: se for recusado (RLS:
        // so o dono ou o gestor), nada mais e gravado e a tela nao muda.
        if (patch.bant && api.salvarBant) await api.salvarBant(id, patch.bant, meRef.current.id);
        // Patch só com campos sem coluna (`tags`, `cadeia`) não tem o que gravar;
        // segue para a tela em vez de mandar um UPDATE vazio ao PostgREST.
        if (Object.keys(dbPatch).length) await api.atualizarNegocio(id, dbPatch);
      } catch (e) {
        console.error('Não foi possível salvar a alteração do negócio:', e.message);
        toast({
          kind: "err",
          msg: e.code === '42501'
            ? "Você não tem permissão para alterar este negócio."
            : "Não foi possível salvar a alteração do negócio.",
        });
        return false;
      }
    }
    const antes = leadsRef.current.find(l => l.id === id);
    // `bantScore` e o resumo (quantos dos quatro campos tem texto); acompanha o texto para o card nao ficar velho.
    const doBant = patch.bant
      ? { bantScore: ['budget', 'authority', 'need', 'timeline'].filter((k) => !!String(patch.bant[k] ?? '').trim()).length }
      : {};
    setLeads(arr => arr.map(l => l.id === id ? { ...l, ...patch, ...doBant } : l));
    // Quem recebe o negocio e avisado no sino (levantamento de 02/10, item A4). Passa por aqui tanto o botao
    // "Passar para o closer" (`repassar`) quanto o campo Responsavel do modal de edicao, entao e UM lugar so e
    // nao ha aviso duplicado. Nao avisa quem atribui a si mesmo, nem quem "troca" para o mesmo dono. O aviso e
    // bonus: se falhar, `notifyPeople` ja registra o erro e a gravacao acima continua valendo.
    if (patch.ownerId && antes && patch.ownerId !== antes.ownerId && patch.ownerId !== meRef.current.id) {
      notificarRef.current?.([patch.ownerId], {
        tipo: 'repasse', titulo: `Negócio repassado para você: ${antes.empresa}`,
        contexto: `por ${meRef.current.nome}`, leadId: id,
      });
    }
    return true;
  }, [api]);

  // Definição rápida da próxima ação (sem abrir o editor completo do negócio)
  //
  // GRAVA, via `updateLead`. Ate 21/09 era so memoria: o vendedor combinava
  // "visita tecnica quinta" com o cliente, a tela confirmava, e no proximo
  // carregamento o campo estava vazio. Pior que perder a anotacao, perdia-se o
  // compromisso -- e o card voltava ao Kanban sem nenhum sinal de atraso.
  //
  // Nao abre caminho de escrita proprio: `updateLead` ja grava, ja traduz pelo
  // mapeador e ja tem o tratamento de 42501. Duplicar isso aqui daria duas
  // implementacoes do mesmo UPDATE para divergirem depois.
  const setProximaAcao = React.useCallback(async (id, texto, opts = {}) => {
    const t = (texto || "").trim();
    const due = t ? (opts.due != null ? !!opts.due : false) : false;
    const ok = await updateLead(id, { proximaAcao: t || "—", proximaDue: due });
    // `updateLead` ja avisou o motivo. Sair aqui mantem a tela no valor antigo:
    // o pior resultado seria a tela mostrar a acao combinada e o banco discordar.
    if (!ok) return false;
    if (t) {
      appendTimeline(id, {
        tipo: "field", autorTipo: "humano", autor: meRef.current.nome,
        texto: <>Próxima ação definida: <strong>{t}</strong>{due ? " · marcada como urgente" : ""}.</>,
        when: "agora",
      });
      toast({ kind: "ok", msg: `Próxima ação: ${t}` });
    } else {
      toast({ kind: "info", msg: "Próxima ação removida" });
    }
    return true;
    // `appendTimeline` fica FORA das dependencias: e declarado depois neste
    // modulo e cita-lo aqui da "Cannot access before initialization" na
    // montagem. E `useCallback` estavel, entao nao ha o que observar.
  }, [updateLead, toast]);

  // Assíncrona porque grava no banco antes de mexer na tela. Devolve `true` só
  // depois que a gravação foi confirmada, `false` em qualquer falha (RLS recusada,
  // etapa desconhecida) — nunca presuma sucesso assim que a chamada dispara, dê
  // `await` e cheque o retorno se algo do seu lado depende de o movimento ter dado
  // certo (ex.: ProducaoModal fechando ou não o modal).
  const moveLeadStage = React.useCallback(async (id, targetStage, opts = {}) => {
    if (api) {
      // Grava primeiro e só então atualiza a tela: mover otimista faria o card
      // pular de coluna e voltar sozinho se o banco recusasse, o que confunde
      // mais do que uma espera de meio segundo.
      const lead = leads.find((l) => l.id === id);
      const etapa = (etapasPorFunil[lead?.funil] || []).find((e) => e.id === targetStage);
      if (!etapa) {
        // Mesmo raciocínio do catch logo abaixo: só console.error deixa o card
        // preso na coluna sem nenhum aviso a quem clicou — quem está na tela
        // precisa do toast, o log é só para quem depura.
        console.error('Etapa desconhecida ao mover negócio:', targetStage);
        toast({ kind: "err", msg: "Não foi possível mover o negócio. Tente recarregar a página." });
        return false;
      }
      try {
        await api.moverNegocio(id, etapa.uuid);   // o banco quer a chave, nao o codigo
      } catch (e) {
        console.error('Não foi possível mover o negócio:', e.message);
        toast({ kind: "err", msg: "Não foi possível mover o negócio. Tente novamente." });
        return false;
      }
    }
    setLeads(arr => arr.map(l => {
      if (l.id !== id) return l;
      const prev = l.stage;
      const cadeia = l.cadeia ?? [];
      // Ator real do movimento: agente (id do agente, sem papel) ou humano (usuário da sessão).
      const atual = meRef.current;
      const ator = (opts.autorTipo === "agente")
        ? { atorTipo: "agente", atorId: opts.autorId || l.agenteId || null, atorNome: opts.autor || "Agente", papel: null }
        : { atorTipo: "humano", atorId: opts.autorId || atual.id, atorNome: opts.autor || atual.nome, papel: opts.papel || atual.papel };
      const stages = etapasPorFunil[l.funil] || [];
      const idx = (st) => { const i = stages.findIndex(x => x.id === st); return i < 0 ? -1 : i; };
      const iQual = idx("QUALIFICADO");
      const cruzouQualificacao = iQual >= 0 && idx(targetStage) >= iQual && idx(prev) < iQual;
      const temElo = (tipo) => cadeia.some(e => e.tipo === tipo);
      let novosElos = [];
      // Qualificação é registrada ao CRUZAR o marco — inclusive quando etapas
      // intermediárias são puladas (ex.: Entrada → Proposta).
      if (cruzouQualificacao && !temElo("qualificacao")) {
        novosElos.push({ tipo: "qualificacao", ...ator, meta: { reuniaoAgendada: false, viaEtapa: targetStage, etapasPuladas: idx(targetStage) - iQual, implicita: idx(targetStage) > iQual } });
      }
      if (targetStage === "GANHO" && !temElo("fechamento")) {
        novosElos.push({ tipo: "fechamento", ...ator, meta: { valorFinal: l.valor, proposta: null } });
      }
      const cadeiaPatch = novosElos.length
        ? { cadeia: [...cadeia, ...novosElos.map((e, i) => ({ seq: cadeia.length + 1 + i, ...e, data: new Date().toISOString() }))] }
        : {};
      // Histórico de dias por etapa: base da mediana usada nos limites de "parado"
      // e na conversão histórica (Gestão). Grava a etapa que está sendo deixada.
      const diasNaEtapa = l.ultimoMovimento
        ? Math.max(0, Math.floor((Date.now() - new Date(l.ultimoMovimento).getTime()) / 86400000))
        : (l.diasNoEstagio || 0);
      const agoraIso = new Date().toISOString();
      const next = { ...l, stage: targetStage, diasNoEstagio: 0, ultimoMovimento: agoraIso, ultimaAtividade: agoraIso,
        historicoEtapas: [...(l.historicoEtapas || []), { stage: prev, dias: diasNaEtapa, saiuEm: agoraIso }],
        ...cadeiaPatch };
      // Fechamento: dispara webhook lead.stage_changed (filtro GANHO)
      if (targetStage === "GANHO") {
        setTimeout(() => fireWebhookByEvent("lead.stage_changed", {
          lead_id: id, empresa: l.empresa, novo_stage: "GANHO", funil: l.funil,
          valor: l.valor, pedido_nomus: opts.pedidoNomus || l.pedidoNomus || null, cadeia: l.cadeia,
        }), 60);
      }
      // Gatilho de repasse: GANHO + parceriaRepasse + especificador
      if (targetStage === "GANHO" && l.funil === "projetos" && l.especificadorId && l.parceriaRepasse) {
        const valorRepasse = Math.round((l.valor || 0) * (l.pctRepasse / 100));
        next.repasse = { valorRepasse, status: "Pendente", when: "agora" };
        setTimeout(() => {
          const esp = clientes.find(e => e.id === l.especificadorId);
          toast({ kind: "ok", msg: `Repasse de ${brl(valorRepasse)} para ${esp?.nome || "canal"} → Pendente` });
        }, 100);
        appendTimeline(id, {
          tipo: "alert", autorTipo: "agente", autor: "Automação · Repasse",
          texto: <>Negócio fechado · repasse de <strong>{brl(valorRepasse)}</strong> criado em <strong>Contas a Pagar</strong>.</>,
          when: "agora",
        });
      }
      appendTimeline(id, {
        tipo: "stage", autorTipo: opts.autorTipo || "humano", autor: opts.autor || meRef.current.nome,
        texto: <>Estágio movido <strong>{STAGE_NAMES[prev]?.toUpperCase()} → {STAGE_NAMES[targetStage]?.toUpperCase()}</strong>.</>,
        when: "agora",
      });
      // O toast de sucesso mora aqui, e não em cada chamador: só este updater roda
      // depois que a gravação já foi confirmada (os dois `return false` acima cobrem
      // toda falha, antes de qualquer setLeads acontecer). Os seis pontos de chamada
      // que existiam antes emitiam esse aviso por conta própria, sem esperar o banco —
      // o usuário via "Acme → Proposta" em verde ao lado de "Não foi possível mover" em
      // vermelho quando a RLS recusava a gravação (achado 2 da revisão final). Manter a
      // mensagem só aqui dentro é o que impede um novo ponto de chamada de reintroduzir
      // o mesmo defeito: quem só chamar moveLeadStage(...) sem tocar em toast nenhum já
      // fica correto por padrão.
      const regressao = idx(prev) >= 0 && idx(targetStage) >= 0 && idx(targetStage) < idx(prev);
      toast({
        kind: (opts.justificativa || regressao) ? "warn" : "ok",
        msg: `${l.empresa} ${regressao ? "voltou para" : "→"} ${STAGE_NAMES[targetStage]}${opts.justificativa ? " · justificativa registrada" : ""}`,
      });
      // Avanço com critério de saída não atendido: a justificativa fica no histórico.
      if (opts.justificativa) {
        appendTimeline(id, {
          tipo: "alert", autorTipo: opts.autorTipo || "humano", autor: opts.autor || meRef.current.nome,
          texto: <>Critério de saída de <strong>{STAGE_NAMES[targetStage]}</strong> não atendido · justificativa: <em>{opts.justificativa}</em></>,
          when: "agora",
        });
      }
      // Auto-roteamento de pós-venda: 1ª compra → Onboarding; recompra → Produção
      if (targetStage === "GANHO") {
        const cli = l.clienteId ? clientes.find(c => c.id === l.clienteId) : null;
        const priorWon = arr.filter(x => x.id !== id && x.stage === "GANHO" && !x.perdido && l.clienteId && x.clienteId === l.clienteId).length;
        const recompra = (priorWon + (cli && (cli.faturamento || 0) > 0 ? 1 : 0)) >= 1;
        // Produção e faturamento acontecem no pós-venda: todo negócio ganho entra em
        // Produção; o Onboarding de relacionamento vem depois do faturamento (1ª compra).
        const etapaPV = "producao";
        setPosVenda(map => (map[id] && map[id].etapa) ? map : ({ ...map, [id]: { ...(map[id] || {}), etapa: etapaPV, primeiraCompra: !recompra } }));
        setTimeout(() => toast({ kind: "ok", msg: `Pós-venda: ${l.empresa} → Produção (${recompra ? "recompra" : "1ª compra"})` }), 160);
        appendTimeline(id, {
          tipo: "alert", autorTipo: "agente", autor: "Automação · Pós-venda",
          texto: <>Negócio entrou no <strong>Pós-venda</strong> → <strong>Produção</strong> ({recompra ? "recompra" : "1ª compra"}).</>,
          when: "agora",
        });
      }
      return next;
    }));
    // Sinaliza sucesso para o chamador que precisa condicionar algo próprio à gravação
    // ter dado certo (ex.: ProducaoModal só registra o CP/entrega, e só fecha, se o
    // negócio realmente moveu — ver drawer.jsx). O toast e a linha do tempo do MOVIMENTO
    // em si já foram tratados acima; isto aqui é só para follow-ups do chamador.
    return true;
  }, [fireWebhookByEvent, me, etapasPorFunil, api, leads]);

  // Arquivar como perdido — mantém a etapa atual (não move para outra coluna/área)
  // GRAVA TAMBEM. Este nao estava na varredura de mocks de 16/09, e a razao e
  // instrutiva: o detector procurava `toast({kind:'ok'})` e esta funcao usa
  // `toastWithUndo`. Passou despercebido justamente por ter UX melhor que as
  // outras -- marcar negocio como perdido e decisao comercial, e ela sumia no F5
  // igual as demais.
  const markLeadLost = React.useCallback(async (id, motivo, obs) => {
    const snapshot = leads.find(l => l.id === id);
    if (api?.marcarNegocioPerdido) {
      try {
        await api.marcarNegocioPerdido(id, motivo, obs);
      } catch (e) {
        console.error('Nao foi possivel marcar o negocio como perdido:', e.code || '', e.message);
        toast({
          kind: "err",
          msg: e.code === '42501'
            ? "Você não tem permissão para alterar este negócio."
            : "Não foi possível marcar o negócio como perdido.",
        });
        return false;
      }
    }
    setLeads(arr => arr.map(l => {
      if (l.id !== id) return l;
      appendTimeline(id, {
        tipo: "stage", autorTipo: "humano", autor: meRef.current.nome,
        texto: <>Negócio marcado como <strong>Perdido</strong> na etapa <strong>{STAGE_NAMES[l.stage]?.toUpperCase()}</strong> · motivo: <strong>{motivo}</strong>.</>,
        when: "agora",
      });
      // perdidoEm junto: o banco carimba pelo gatilho (0065); sem isto a coorte so
      // enxergaria a perda depois de recarregar a pagina.
      return { ...l, perdido: true, perdidoEm: new Date().toISOString(), motivoPerda: motivo, obsPerda: obs || null };
    }));
    // Webhook lead.lost
    if (snapshot) {
      fireWebhookByEvent("lead.lost", {
        lead_id: id, empresa: snapshot.empresa, funil: snapshot.funil, motivo, observacao: obs || "",
        segmento: snapshot.segmento, cadeia: snapshot.cadeia, valorPotencial: snapshot.valor,
      });
    }
    // Toast com Desfazer (5s) — restaura o estado anterior do negócio
    // O DESFAZER TAMBEM GRAVA. Restaurar so na tela faria o negocio voltar a
    // perdido no proximo carregamento -- e o vendedor teria clicado em desfazer
    // acreditando que resolveu.
    toastWithUndo(
      `Negócio "${snapshot?.empresa || ""}" marcado como perdido`,
      () => {
        setLeads(arr => arr.map(l => l.id === id ? snapshot : l));
        api?.reabrirNegocio?.(id).catch((e) => {
          console.error('Nao foi possivel desfazer a perda:', e.message);
          toast({ kind: "err", msg: "Não foi possível desfazer. O negócio segue como perdido." });
        });
      },
      "warn"
    );
    return true;
  }, [api, leads, fireWebhookByEvent, toastWithUndo, toast]);

  // EXCLUI o lead (decisão do Kaio, 01/10: só nas duas primeiras etapas; o banco recusa o resto, 0090).
  // Como `reopenLead`: só tira da tela depois que o banco confirmou, e avisa o motivo se recusou.
  const deleteLead = React.useCallback(async (id) => {
    if (api?.excluirNegocio) {
      try {
        await api.excluirNegocio(id);
      } catch (e) {
        console.error('Nao foi possivel excluir o negocio:', e.code || '', e.message);
        toast({
          kind: "err",
          msg: e.code === '42501'
            ? "Não foi possível excluir: só é possível nas duas primeiras etapas, por quem tem permissão."
            : "Não foi possível excluir o lead.",
        });
        return false;
      }
    }
    setLeads(arr => arr.filter(l => l.id !== id));
    toast({ kind: "ok", msg: "Lead excluído" });
    return true;
  }, [api, toast]);

  // Reabrir negócio perdido (volta ativo, na mesma etapa)
  // GRAVA. Ate 21/09 reabrir um negocio perdido era decisao comercial que sumia
  // no F5: o card voltava ao fluxo e, no proximo carregamento, estava perdido de
  // novo -- sem aviso nenhum.
  const reopenLead = React.useCallback(async (id) => {
    if (api?.reabrirNegocio) {
      try {
        await api.reabrirNegocio(id);
      } catch (e) {
        console.error('Nao foi possivel reabrir o negocio:', e.code || '', e.message);
        toast({
          kind: "err",
          msg: e.code === '42501'
            ? "Você não tem permissão para reabrir este negócio."
            : "Não foi possível reabrir o negócio.",
        });
        return false;
      }
    }
    setLeads(arr => arr.map(l => l.id === id ? { ...l, perdido: false, perdidoEm: null, motivoPerda: null, obsPerda: null } : l));
    appendTimeline(id, { tipo: "stage", autorTipo: "humano", autor: meRef.current.nome, texto: <>Negócio <strong>reaberto</strong> · voltou ao fluxo ativo.</>, when: "agora" });
    toast({ kind: "ok", msg: "Negócio reaberto" });
    return true;
    // `appendTimeline` fica FORA das dependencias: ele e declarado depois neste
    // modulo, e cita-lo aqui da "Cannot access before initialization" na
    // montagem. E `useCallback` estavel, entao nao ha o que observar.
  }, [api, toast]);

  // Confirmar pagamento de repasse a canal (Pendente → Pago)
  const confirmRepasse = React.useCallback((leadId) => {
    let valor = 0, nome = "";
    setLeads(arr => arr.map(l => {
      if (l.id !== leadId || !l.repasse) return l;
      valor = l.repasse.valorRepasse;
      const esp = clientes.find(e => e.id === l.especificadorId);
      nome = esp?.nome || "canal";
      return { ...l, repasse: { ...l.repasse, status: "Pago", pagoEm: "agora" } };
    }));
    appendTimeline(leadId, {
      tipo: "alert", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Repasse de <strong>{brl(valor)}</strong> para <strong>{nome}</strong> confirmado e baixado em Contas a Pagar.</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: `Repasse de ${brl(valor)} → Pago` });
  }, [clientes]);

  // Repasse SDR → Closer (mantém originador / funil de origem)
  // Repasse ao closer: TROCA O DONO e registra o elo da cadeia — as duas coisas
  // no banco. Até 14/09 isto era só memória (setLeads + timeline + toast), no
  // mesmo arquivo e pelo mesmo motivo que `updateLead` era.
  //
  // Duas gravações e não uma, porque são dois fatos diferentes: quem responde
  // pelo negócio AGORA (`negocios.owner_id`) e quem passou para quem, e quando
  // (`negocio_cadeia`). A segunda é o que sustenta a promessa do toast — "origem
  // preservada no funil de quem iniciou o lead".
  //
  // A ORDEM IMPORTA: o dono primeiro. Se a RLS recusar a troca — e ela recusa,
  // `negocios_upd` tem `with check (owner_id = auth.uid() or sou_gestor())`, o
  // que barra um closer passando o negócio adiante —, não se grava elo nenhum
  // de um repasse que não aconteceu.
  //
  // Um repasse por negócio de cada vez. Reteste do Kaio (25/09): no "testecrm2"
  // o repasse rodou DUAS vezes no mesmo segundo -- dois UPDATEs de dono na
  // auditoria, dois eventos na timeline. O seletor pode disparar o onChange em
  // dobro; a trava fica aqui para valer para qualquer tela.
  const repassesEmCurso = React.useRef(new Set());
  const repassar = React.useCallback(async (leadId, closerId) => {
    const closer = usersRef.current.find(u => u.id === closerId);
    if (!(await updateLead(leadId, { ownerId: closerId }))) return false;

    const lead = leads.find(l => l.id === leadId);
    // O MAIOR seq + 1, e nao "quantos elos + 1": a origem derivada leva seq 0 e
    // elos antigos podem ter buraco -- contar colidiria com o unique (negocio_id, seq).
    const seq = Math.max(0, ...(lead?.cadeia ?? []).map(e => Number(e.seq) || 0)) + 1;
    const elo = {
      seq, tipo: "handoff", atorTipo: "humano",
      atorId: meRef.current.id, atorNome: meRef.current.nome, papel: meRef.current.papel,
      data: new Date().toISOString(),
      meta: { de: lead?.ownerId, para: closerId, paraNome: closer?.nome, paraPapel: closer?.papel },
    };
    if (api) {
      try {
        await api.registrarCadeia(leadId, elo);
      } catch (e) {
        // O dono JÁ mudou no banco. Avisar em vez de calar: a cadeia é o que
        // explica o repasse depois, e perdê-la em silêncio deixa o negócio com
        // dono novo e sem história de como chegou lá.
        console.error('Não foi possível registrar a cadeia do repasse:', e.message);
        toast({ kind: "err", msg: "Repasse feito, mas o histórico da atribuição não foi registrado." });
      }
    }
    setLeads(arr => arr.map(l => l.id === leadId ? { ...l, cadeia: [...(l.cadeia ?? []), elo] } : l));
    appendTimeline(leadId, {
      tipo: "field", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Negócio repassado para o closer <strong>{closer?.nome}</strong> · origem preservada no funil de quem iniciou o lead.</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: `Repassado para ${closer?.nome}` });
    return true;
  }, [api, updateLead, leads]);
  const handoffToCloser = React.useCallback(async (leadId, closerId) => {
    if (repassesEmCurso.current.has(leadId)) return false;
    repassesEmCurso.current.add(leadId);
    try {
      return await repassar(leadId, closerId);
    } finally {
      repassesEmCurso.current.delete(leadId);
    }
  }, [repassar]);

  // Ref de moveLeadStage: usada pela integração ERP, definida depois neste módulo.
  const moveLeadStageRef = React.useRef(null);
  moveLeadStageRef.current = moveLeadStage;

  // GRAVA. Ate 18/09 isto era `setTimeline(...)` e ponto: mudanca de etapa, nota,
  // repasse -- tudo o que aconteceu no negocio sumia no F5. A tabela
  // `timeline_eventos` existia desde a 0008 e o front nunca escreveu nela.
  //
  // A tela e atualizada PRIMEIRO e a escrita segue por fora. Timeline e registro
  // do que ja aconteceu: segurar a interface esperando o banco atrasaria a acao
  // que o vendedor acabou de fazer, e ela ja aconteceu de qualquer forma.
  //
  // Se a escrita falhar, o evento fica marcado como `naoGravado` e a tela avisa.
  // O que NAO pode acontecer e o de sempre: sumir em silencio.
  const appendTimeline = React.useCallback((leadId, event) => {
    const local = { ...event, id: event.id || 'tl_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6) };
    setTimeline(t => ({ ...t, [leadId]: [local, ...(t[leadId] || [])] }));
    if (!api?.criarEventoTimeline) return;
    api.criarEventoTimeline(eventoParaBanco(leadId, event, meRef.current?.id || null))
      .catch((e) => {
        console.error('Nao foi possivel gravar o evento na timeline:', e.code || '', e.message);
        setTimeline(t => ({
          ...t,
          [leadId]: (t[leadId] || []).map(ev => ev.id === local.id ? { ...ev, naoGravado: true } : ev),
        }));
        toast({ kind: 'err', msg: 'O histórico deste negócio não pôde ser salvo.' });
      });
  }, [api, toast]);

  // Appenda um elo à cadeia de atribuição do lead
  const appendCadeia = React.useCallback((leadId, elo) => {
    setLeads(prev => prev.map(l => {
      if (l.id !== leadId) return l;
      const cadeia = l.cadeia ?? [];
      return { ...l, cadeia: [...cadeia, { seq: cadeia.length + 1, ...elo, data: new Date().toISOString() }] };
    }));
  }, []);

  // Recebe lead enriquecido de agente externo → entra direto em ENTRADA
  const receiveLead = React.useCallback((enrichedData) => {
    const id = "ld_" + Date.now().toString(36);
    const novoLead = {
      id,
      empresa: enrichedData.empresa,
      contato: enrichedData.contato || "—",
      cargo: enrichedData.cargo || "—",
      contatoDecisor: false,
      email: enrichedData.email || "",
      telefone: enrichedData.telefone || "",
      segmento: enrichedData.segmento || "—",
      scoreFit: enrichedData.scoreFit || 0,
      fonteCadeia: enrichedData.fonte || "",
      funil: enrichedData.funil ?? "projetos",
      stage: "ENTRADA",
      origem: "agente",
      agenteId: enrichedData.agenteId ?? "ag_helios",
      ownerId: null,
      valor: 0,
      cadeia: [{ seq: 1, tipo: "geracao", atorTipo: "agente", atorId: enrichedData.agenteId ?? "ag_helios", atorNome: enrichedData.agenteNome ?? "Agente Prospecção", papel: null, data: new Date().toISOString(), meta: { scoreEnriquecimento: enrichedData.scoreFit, fonte: enrichedData.fonte, segmento: enrichedData.segmento } }],
      perdido: false, calor: "cold", diasNoEstagio: 0, proximaAcao: "—", proximaDue: false,
      bantScore: 1, motivoPerda: null, obsPerda: null, tags: [], externalId: null,
      criadoEm: "via agente", criadoData: new Date(),
      clienteId: null, especificadorId: null,
      tipoCliente: enrichedData.funil === "lojistas" ? "lojista" : "projeto_direto",
      parceriaRepasse: false, pctRepasse: 0, repasse: null,
      bant: { budget: null, authority: null, need: null, timeline: null },
      dynamic: null,
      originador: { tipo: "ia", id: enrichedData.agenteId ?? "ag_helios", nome: enrichedData.agenteNome ?? "Agente Prospecção" },
    };
    setLeads(arr => [novoLead, ...arr]);
    appendTimeline(id, {
      tipo: "alert", autorTipo: "agente", autor: enrichedData.agenteNome ?? "Agente Prospecção",
      texto: <>Lead recebido · score <strong>{enrichedData.scoreFit}</strong> · {enrichedData.fonte}</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: `Lead recebido: ${enrichedData.empresa}` });
    return id;
  }, []);

  // Carrega as notificações da pessoa que está usando o sistema.
  //
  // Até 15/09 o sino era 100% memória: a notificação nascia no `setState` e
  // morria no F5. Quem fosse mencionado e recarregasse a página perdia o aviso
  // — e não ficava sabendo que perdeu, porque não havia o que comparar. A
  // tabela, o índice e a RLS existem desde a 0013; faltava a tela usar.
  //
  // O filtro por destinatário mora na api (ver comentário lá): `notif_sel`
  // deixa gestor ver a equipe inteira, e sem filtro o sino de um admin traria
  // notificação dos outros.
  React.useEffect(() => {
    if (!api || !me?.id) return;
    let vivo = true;
    const geracao = rastro.current.geracao;
    const recarregando = recarga > 0;
    // try/catch em volta da CHAMADA, e não só `.catch` na promessa: api sem o
    // método (dublê antigo, pacote parcial) estoura de forma síncrona aqui
    // dentro e derruba o Provider inteiro — o sino levaria junto a tela toda.
    (async () => {
      try {
        const linhas = await api.listarNotificacoes(me.id);
        if (!vivo || (recarregando && rastro.current.geracao !== geracao)) return;
        const nomes = Object.fromEntries((usersRef.current || []).map(u => [u.id, u.nome]));
        setNotifications(linhas.map((l) => api.mapearNotificacao(l, nomes)));
      } catch (e) {
        console.error('Não foi possível carregar as notificações:', e.message);
        if (vivo && !recarregando) toast({ kind: 'err', msg: 'Não foi possível carregar as notificações.' });
      }
    })();
    return () => { vivo = false; };
  }, [api, me?.id, toast, recarga]);

  React.useEffect(() => {
    if (!api) return;
    let vivo = true;
    (async () => {
      try {
        const linhas = await api.listarTabelasPreco();
        if (vivo) setTabelasPreco(linhas.map(api.mapearTabelaPreco));
      } catch (e) {
        console.error('Não foi possível carregar as tabelas de preço:', e.message);
        if (vivo) toast({ kind: 'err', msg: 'Não foi possível carregar as tabelas de preço.' });
      }
    })();
    return () => { vivo = false; };
  }, [api, toast]);

  // ----- Notificações do sistema -----
  // Avisa responsável + envolvidos/menções DENTRO do sistema. Não manda e-mail,
  // e é isso que o código diz agora.
  //
  // Até 15/09 aqui havia `canal: ["sistema", "e-mail"]` e uma linha de log por
  // destinatário anunciando "E-mail · Resend · status 200". Nenhum e-mail saía:
  // eram os mesmos dois enfeites que existiam no envio da proposta. Quem
  // abrisse a tela de logs concluiria que as pessoas foram avisadas por e-mail.
  //
  // O `canal` não era lido por tela nenhuma (conferido) — era uma afirmação
  // falsa guardada em memória, esperando alguém acreditar nela.
  const notifyPeople = React.useCallback(async (ids, payload) => {
    const uniq = [...new Set((ids || []).filter(Boolean))];
    if (!uniq.length) return;
    const all = usersRef.current;
    const eu = meRef.current;

    const paraMim = (uid) => uid === eu.id;
    const emMemoria = (uid, id) => ({
      id,
      userId: uid,
      userNome: all.find(x => x.id === uid)?.nome || "—",
      tipo: payload.tipo,        // "responsavel" | "mencao"
      titulo: payload.titulo,
      contexto: payload.contexto || "",
      leadId: payload.leadId || null,
      origemTipo: payload.origemTipo || (payload.leadId ? 'negocio' : null),
      origemId: payload.origemId || payload.leadId || null,
      when: "agora",
      lida: false,
    });

    if (!api) {
      // Sem banco (teste de tela, demonstração) o sino volta a ser o de antes.
      setNotifications(arr => [...uniq.map(uid => emMemoria(uid, "ntf_" + Math.random().toString(36).slice(2, 9))), ...arr].slice(0, 60));
      return;
    }

    try {
      const paraOBanco = uniq.map(uid => ({
        destinatario_id: uid,
        // `autor_id` é exigido pela RLS: `notif_ins` (0023) aceita
        // `autor_id = auth.uid() or util.sou_gestor()`. Nulo aqui seria recusado
        // com 42501 justamente para quem não é gestor.
        autor_id: eu.id,
        tipo: payload.tipo,
        titulo: payload.titulo,
        texto: payload.contexto || null,
        // Convite de tarefa aponta para a TAREFA: e por ela que o sino acha o
        // vinculo a aceitar ou recusar. O resto segue apontando para o negocio.
        origem_tipo: payload.origemTipo || (payload.leadId ? 'negocio' : null),
        origem_id: payload.origemId || payload.leadId || null,
      }));
      // O aviso para OUTRA pessoa nao pode pedir a linha de volta: `notif_sel` so deixa ler a notificacao endereçada a
      // si, e o `.select()` faria o INSERT falhar com 42501 para quem nao e gestor (ver api.criarNotificacoes). O aviso
      // para MIM volta, porque o sino precisa do id para marcar como lida.
      const paraOutros = paraOBanco.filter(l => !paraMim(l.destinatario_id));
      const paraMimLinhas = paraOBanco.filter(l => paraMim(l.destinatario_id));
      if (paraOutros.length) await api.criarNotificacoes(paraOutros, { devolver: false });
      const linhas = paraMimLinhas.length ? await api.criarNotificacoes(paraMimLinhas) : [];

      // SÓ AS MINHAS entram no meu sino. Antes todas entravam: notificar a Carla
      // acrescentava a notificação DELA à lista de quem clicou. Em memória isso
      // passava despercebido (a lista morria no F5); com o banco por trás, o
      // engano ficaria visível e errado — eu veria um aviso endereçado a outra
      // pessoa, e ela veria o mesmo aviso de novo.
      const minhas = (linhas || []).filter(l => paraMim(l.destinatario_id));
      if (minhas.length) {
        const nomes = Object.fromEntries((all || []).map(u => [u.id, u.nome]));
        setNotifications(arr => [...minhas.map(l => api.mapearNotificacao(l, nomes)), ...arr].slice(0, 60));
      }
    } catch (e) {
      // Notificação que não grava é notificação que ninguém recebe. Calar aqui
      // faria a menção sumir sem que nem quem mencionou soubesse.
      console.error('Não foi possível registrar a notificação:', e.code || '', e.message);
      toast({ kind: 'err', msg: 'Não foi possível avisar as pessoas envolvidas.' });
      // Ainda assim mostra a minha na tela: o aviso local é melhor que nenhum.
      const minhasLocais = uniq.filter(paraMim).map(uid => emMemoria(uid, "ntf_" + Math.random().toString(36).slice(2, 9)));
      if (minhasLocais.length) setNotifications(arr => [...minhasLocais, ...arr].slice(0, 60));
    }
  }, [api, toast]);
  notificarRef.current = notifyPeople;

  const markNotificationsRead = React.useCallback(() => {
    // Otimista na tela e persistente no banco: o badge tem de apagar no clique,
    // e a marca tem de sobreviver ao F5 — senão o sino volta a acender sozinho
    // e a pessoa aprende a ignorá-lo.
    // A lista sai do ESTADO, não de dentro do `setNotifications(updater)`. O
    // updater não roda de forma síncrona no React 18: colher os ids lá dentro
    // devolvia sempre vazio, e o efeito seria o pior possível aqui — o badge
    // apagaria na tela e nada seria gravado, então o sino voltaria a acender no
    // F5. Ou seja: exatamente o defeito que este bloco veio consertar.
    //
    // Ids "ntf_" ficam de fora: são os locais, de quando a gravação falhou.
    // Mandá-los ao banco daria erro de uuid inválido, e esse erro apareceria no
    // lugar errado, escondendo a falha de verdade (a gravação).
    const aMarcar = notifications.filter(n => !n.lida && !String(n.id).startsWith('ntf_')).map(n => n.id);
    setNotifications(arr => arr.map(n => ({ ...n, lida: true })));
    if (api && aMarcar.length) {
      api.marcarNotificacoesLidas(aMarcar).catch((e) => {
        console.error('Não foi possível marcar as notificações como lidas:', e.message);
      });
    }
  }, [api, notifications]);

  // Marca atividade no negócio (nota, tarefa concluída): base do sinal de inatividade.
  const touchAtividade = React.useCallback((leadId) => {
    setLeads(arr => arr.map(l => l.id === leadId ? { ...l, ultimaAtividade: new Date().toISOString() } : l));
  }, []);

  const addNote = React.useCallback((leadId, texto, opts = {}) => {
    const mentions = opts.mentions || [];   // [{ id, nome }] pessoas mencionadas com @
    // Renderiza a nota destacando os @nomes mencionados
    let conteudo = <>{texto}</>;
    if (mentions.length) {
      const nomes = mentions.map(m => m.nome);
      const re = new RegExp("(@(?:" + nomes.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|") + "))", "g");
      const partes = texto.split(re);
      conteudo = <>{partes.map((p, i) => re.test(p) ? <strong key={i} className="note-mention">{p}</strong> : <React.Fragment key={i}>{p}</React.Fragment>)}</>;
    }
    appendTimeline(leadId, {
      tipo: "note",
      autorTipo: opts.autorTipo || "humano",
      autor: opts.autor || meRef.current.nome,
      texto: conteudo,
      when: "agora",
    });
    // Notifica menções (sistema + e-mail)
    const ids = mentions.map(m => m.id).filter(id => id && id !== meRef.current.id);
    if (ids.length) {
      notifyPeople(ids, {
        tipo: "mencao",
        titulo: `${meRef.current.nome} mencionou você em uma nota`,
        contexto: texto.length > 70 ? texto.slice(0, 70) + "…" : texto,
        leadId,
      });
    }
    const extra = ids.length ? ` · ${ids.length} pessoa(s) notificada(s)` : "";
    touchAtividade(leadId);
    toast({ kind: "ok", msg: `Nota adicionada · veja na aba Timeline do negócio${extra}` });
  }, [notifyPeople, touchAtividade]);

  // ----- Alerts (com date object) -----
  // GRAVA. Ate 21/09 era so memoria: o vendedor agendava o follow-up combinado
  // com o cliente, a tela confirmava, e o F5 apagava o compromisso.
  //
  // Envolvidos gravam em `tarefa_envolvidos` (0063), e o STATUS de cada um e o
  // banco que decide (0066): agenda ocupada -> pendente ate a pessoa aceitar;
  // livre -> aceito. O aviso vai pelo sino -- e SO pelo sino: nao ha envio de
  // e-mail (o comentario de 22/09 que dizia o contrario estava errado).
  const createAlert = React.useCallback(async (data) => {
    const whenDate = data.when instanceof Date ? data.when : new Date(data.when || Date.now());
    const responsavel = data.autor || meRef.current.id;
    const envolvidos = (data.envolvidos || []).filter(uid => uid && uid !== responsavel);
    // Fim: o que a tela mandou, ou inicio + duracao padrao do tipo.
    const fimDate = data.fim instanceof Date ? data.fim
      : new Date(whenDate.getTime() + duracaoDoTipo(data.tipo) * 60000);
    const rascunho = {
      leadId: data.leadId || null,
      clienteId: data.clienteId || null,
      tipo: data.tipo,
      descricao: data.descricao || "",
      when: whenDate,
      inicio: whenDate,
      fim: fimDate,
      canal: data.canal || "E-mail",
      feito: false,
      autor: responsavel,
      envolvidos,
      prioridade: data.prioridade || "média",
      subtarefas: data.subtarefas || [],
    };

    // O id vem do banco -- a coluna e uuid gerado pelo Postgres. Inventar
    // "al_<timestamp>" aqui faria toda escrita seguinte (concluir, editar,
    // subtarefa) mirar uma linha que nao existe.
    let id = "al_" + Date.now().toString(36);
    if (api?.criarTarefa) {
      try {
        const linha = await api.criarTarefa(
          api.tarefaParaBanco(rascunho, meRef.current.id),
          rascunho.subtarefas,
        );
        id = linha.id;
        rascunho.subtarefas = api.mapearTarefa(linha).subtarefas;
      } catch (e) {
        console.error('Nao foi possivel criar a tarefa:', e.code || '', e.message);
        toast({
          kind: "err",
          msg: e.code === '42501'
            ? "Você não tem permissão para criar tarefas."
            : "Não foi possível criar a tarefa.",
        });
        return null;
      }
    }
    // Vinculos depois da tarefa existir (precisam do id). Falhar aqui nao
    // desfaz a tarefa -- ela ja esta gravada --, mas a tela nao pode fingir que
    // convidou: os envolvidos ficam de fora e o aviso diz isso.
    let convites = {};
    let envolvidosGravados = envolvidos;
    if (api?.vincularEnvolvidos && envolvidos.length && !String(id).startsWith("al_")) {
      try {
        const vinc = await api.vincularEnvolvidos(id, envolvidos);
        convites = Object.fromEntries(vinc.map(v => [v.user_id, v.status]));
        envolvidosGravados = vinc.map(v => v.user_id);
      } catch (e) {
        console.error('Nao foi possivel convidar os envolvidos:', e.code || '', e.message);
        toast({ kind: "err", msg: "A tarefa foi criada, mas não foi possível convidar os envolvidos." });
        envolvidosGravados = [];
      }
    }
    const alert = vestirAlerta({ ...rascunho, id, envolvidos: envolvidosGravados, convites });
    setAlerts(arr => [alert, ...arr]);
    const all = usersRef.current;
    const respNome = all.find(u => u.id === responsavel)?.nome || "—";
    if (data.leadId) {
      const envNomes = envolvidos.map(uid => all.find(u => u.id === uid)?.nome).filter(Boolean);
      // "tarefa", e nao "alert": `timeline_tipo` (0002) nao tem "alert" e o banco
      // recusava o evento -- aparecia na tela e sumia no F5 (medido em 23/09).
      appendTimeline(data.leadId, {
        tipo: "tarefa", autorTipo: "humano", autor: meRef.current.nome,
        texto: <>Tarefa criada: <strong>{data.tipo}</strong> — {alert.quando} · resp. <strong>{respNome}</strong>{envNomes.length ? <> · envolvidos: {envNomes.join(", ")}</> : null}</>,
        when: "agora",
      });
    }
    // Aviso no sino (responsavel + convidados). O convite aponta para a TAREFA:
    // e por ela que o sino oferece aceitar/recusar.
    if (responsavel !== meRef.current.id) notifyPeople([responsavel], { tipo: "responsavel", titulo: `Você é responsável pela tarefa "${data.tipo}"`, contexto: alert.quando, leadId: data.leadId });
    const pendentes = envolvidosGravados.filter(uid => convites[uid] === "pendente");
    const aceitos = envolvidosGravados.filter(uid => convites[uid] !== "pendente");
    if (pendentes.length) notifyPeople(pendentes, { tipo: "convite", titulo: `Convite pendente: "${data.tipo}" · ${alert.quando}`, contexto: `Você já tem outro compromisso nesse horário · responsável: ${respNome}`, origemTipo: "tarefa", origemId: id });
    if (aceitos.length) notifyPeople(aceitos, { tipo: "convite", titulo: `Você foi incluído(a) na tarefa "${data.tipo}" · ${alert.quando}`, contexto: `Responsável: ${respNome}`, origemTipo: "tarefa", origemId: id });
    // O log falso de Google Calendar saiu daqui em 21/09. Ele escrevia uma
    // linha "POST /calendar/v3/... 200" na tela de logs de API, com token
    // sorteado e tempo de resposta sorteado, para uma chamada que nunca
    // aconteceu -- e o toast anunciava "sincronizada com Google Calendar".
    // Nao ha integracao com agenda nenhuma. Quando houver, o log volta a partir
    // da resposta de verdade.
    const notifMsg = envolvidosGravados.length
      ? ` · ${envolvidosGravados.length} convidado(s)${pendentes.length ? ` (${pendentes.length} pendente: agenda ocupada)` : ""}`
      : "";
    toast({ kind: "ok", msg: `Tarefa criada${notifMsg}` });
    return id;
  }, [api, notifyPeople, toast]);

  // GRAVA. Concluir tarefa e o gesto mais repetido da agenda, e ate 21/09 era o
  // que mais se perdia: o vendedor limpava a lista do dia e no F5 tudo voltava
  // a pendente -- inclusive os lembretes vencidos, em vermelho.
  const toggleAlertDone = React.useCallback(async (id) => {
    const alertRef = alerts.find(a => a.id === id);
    if (!alertRef) return false;
    const feito = !alertRef.feito;
    if (api?.atualizarTarefa) {
      try {
        await api.atualizarTarefa(id, api.tarefaPatchParaBanco({ feito }));
      } catch (e) {
        console.error('Nao foi possivel concluir a tarefa:', e.code || '', e.message);
        toast({
          kind: "err",
          msg: e.code === '42501'
            ? "Só o responsável, o autor ou a Direção podem alterar esta tarefa."
            : "Não foi possível alterar a tarefa.",
        });
        return false;
      }
    }
    if (!alertRef.feito && alertRef.leadId) touchAtividade(alertRef.leadId);
    // `feitoEm` junto: sem ele, a reuniao concluida agora so entraria na conta
    // de "reunioes realizadas no mes" depois de recarregar a pagina.
    setAlerts(arr => arr.map(a => a.id === id ? { ...a, feito, feitoEm: feito ? new Date().toISOString() : null, vencido: feito ? false : a.vencido } : a));
    // Issue #17 do Kaio (23/09): concluir a tarefa fica registrado no negocio.
    if (feito && alertRef.leadId) {
      appendTimeline(alertRef.leadId, {
        tipo: "tarefa", autorTipo: "humano", autor: meRef.current.nome,
        texto: <>Tarefa concluída: <strong>{alertRef.tipo}</strong></>,
        when: "agora",
      });
    }
    // Cadeia: reuniao_realizada quando uma tarefa de Reunião é concluída
    if (alertRef && !alertRef.feito && alertRef.leadId &&
        (alertRef.tipo.toLowerCase().includes("reuni") || alertRef.tipo.toLowerCase().includes("meeting"))) {
      setLeads(arr => arr.map(l => {
        if (l.id !== alertRef.leadId) return l;
        const cadeia = l.cadeia ?? [];
        return { ...l, cadeia: [...cadeia, { seq: cadeia.length + 1, tipo: "reuniao_realizada", atorTipo: "humano", atorId: meRef.current.id, atorNome: meRef.current.nome, papel: meRef.current.papel, data: new Date().toISOString(), meta: { alertId: id, canal: alertRef.canal } }] };
      }));
    }
    return true;
  }, [alerts, api, toast, touchAtividade, appendTimeline]);

  // ----- Tarefas: edição inline, concluír rápido, subtarefas -----
  // GRAVA, em dois tempos:
  //  1. os campos da tarefa. Reagendar preserva a DURACAO: mover so o inicio
  //     deixava o fim para tras e o banco recusava (check fim > inicio, 0063).
  //  2. os envolvidos: quem entra e vinculado (o banco decide pendente/aceito,
  //     0066), quem sai e desvinculado. Falha aqui nao desfaz o passo 1 -- a
  //     tarefa ja foi salva -- e o aviso diz exatamente o que nao foi.
  const updateAlert = React.useCallback(async (id, patchOriginal) => {
    const atual = alerts.find(a => a.id === id);
    const { envolvidos: envolvidosPedidos, ...patch } = patchOriginal || {};
    const novoInicio = patch.inicio ?? patch.when;
    if (novoInicio && patch.fim === undefined && atual) {
      const ini = novoInicio instanceof Date ? novoInicio : new Date(novoInicio);
      const durMs = (atual.fim && (atual.inicio || atual.when))
        ? new Date(atual.fim).getTime() - new Date(atual.inicio || atual.when).getTime()
        : 3600000;
      patch.fim = new Date(ini.getTime() + Math.max(60000, durMs));
    }
    const prevEnvolvidos = atual?.envolvidos || [];
    const responsavelFinal = patch.autor || atual?.autor;
    const entram = envolvidosPedidos ? envolvidosPedidos.filter(uid => uid && !prevEnvolvidos.includes(uid) && uid !== responsavelFinal) : [];
    const saem = envolvidosPedidos ? prevEnvolvidos.filter(uid => !envolvidosPedidos.includes(uid)) : [];

    if (api?.atualizarTarefa) {
      try {
        const dbPatch = api.tarefaPatchParaBanco(patch);
        // Patch so com envolvidos nao tem campo a gravar; nada de UPDATE vazio.
        if (Object.keys(dbPatch).length) await api.atualizarTarefa(id, dbPatch);
      } catch (e) {
        console.error('Nao foi possivel salvar a tarefa:', e.code || '', e.message);
        toast({
          kind: "err",
          msg: e.code === '42501'
            ? "Só o responsável, o autor ou a Direção podem alterar esta tarefa."
            : "Não foi possível salvar a tarefa.",
        });
        return false;
      }
    }

    let convitesNovos = {};
    let vinculosOk = true;
    if ((entram.length || saem.length) && api?.vincularEnvolvidos) {
      try {
        if (saem.length) await api.desvincularEnvolvidos(id, saem);
        if (entram.length) {
          const vinc = await api.vincularEnvolvidos(id, entram);
          convitesNovos = Object.fromEntries(vinc.map(v => [v.user_id, v.status]));
        }
      } catch (e) {
        vinculosOk = false;
        console.error('Nao foi possivel atualizar os envolvidos:', e.code || '', e.message);
        toast({ kind: "err", msg: "A tarefa foi salva, mas não foi possível atualizar os envolvidos." });
      }
    }

    const tipoTarefa = patch.tipo || atual?.tipo || "";
    const leadIdTarefa = atual?.leadId || null;
    setAlerts(arr => arr.map(a => {
      if (a.id !== id) return a;
      const next = { ...a, ...patch };
      if (novoInicio) next.when = novoInicio instanceof Date ? novoInicio : new Date(novoInicio);
      if (envolvidosPedidos && vinculosOk) {
        const convites = { ...(a.convites || {}) };
        saem.forEach(uid => { delete convites[uid]; });
        Object.assign(convites, convitesNovos);
        next.envolvidos = [...prevEnvolvidos.filter(uid => !saem.includes(uid)), ...entram];
        next.convites = convites;
      }
      return vestirAlerta(next);
    }));

    const all = usersRef.current;
    const respNome = all.find(u => u.id === responsavelFinal)?.nome || "—";
    if (vinculosOk && entram.length) {
      const quando = formatAlertWhen(novoInicio ? new Date(novoInicio) : (atual?.when || new Date()));
      const pend = entram.filter(uid => convitesNovos[uid] === "pendente");
      const ok = entram.filter(uid => convitesNovos[uid] !== "pendente");
      if (pend.length) notifyPeople(pend, { tipo: "convite", titulo: `Convite pendente: "${tipoTarefa}" · ${quando}`, contexto: `Você já tem outro compromisso nesse horário · responsável: ${respNome}`, origemTipo: "tarefa", origemId: id });
      if (ok.length) notifyPeople(ok, { tipo: "convite", titulo: `Você foi incluído(a) na tarefa "${tipoTarefa}" · ${quando}`, contexto: `Responsável: ${respNome}`, origemTipo: "tarefa", origemId: id });
    }
    if (patch.autor && patch.autor !== atual?.autor && patch.autor !== meRef.current.id) {
      notifyPeople([patch.autor], { tipo: "responsavel", titulo: `Você é responsável pela tarefa "${tipoTarefa}"`, contexto: "Atualização de tarefa", leadId: leadIdTarefa });
    }
    if (vinculosOk) toast({ kind: "ok", msg: "Tarefa atualizada" });
    return true;
  }, [alerts, api, notifyPeople, toast]);
  // O checklist tambem grava: era o item mais facil de perder sem perceber,
  // porque marcar um passo nao mostra toast nenhum.
  const toggleSubtask = React.useCallback(async (alertId, subId) => {
    const alvo = (alerts.find(a => a.id === alertId)?.subtarefas || []).find(s => s.id === subId);
    if (!alvo) return false;
    if (api?.atualizarSubtarefa) {
      try {
        await api.atualizarSubtarefa(subId, { feito: !alvo.done });
      } catch (e) {
        console.error('Nao foi possivel marcar o passo:', e.code || '', e.message);
        toast({ kind: "err", msg: "Não foi possível marcar este passo." });
        return false;
      }
    }
    setAlerts(arr => arr.map(a => {
      if (a.id !== alertId) return a;
      const subtarefas = (a.subtarefas || []).map(s => s.id === subId ? { ...s, done: !s.done } : s);
      return { ...a, subtarefas };
    }));
    return true;
  }, [alerts, api, toast]);

  const addSubtask = React.useCallback(async (alertId, label) => {
    const atual = alerts.find(a => a.id === alertId)?.subtarefas || [];
    // O id vem do banco, como na tarefa: inventar "st_<timestamp>" faria o
    // clique seguinte -- marcar o passo -- mirar uma linha que nao existe.
    let id = "st_" + Date.now().toString(36);
    if (api?.criarSubtarefa) {
      try {
        const linha = await api.criarSubtarefa(alertId, label, atual.length);
        id = linha.id;
      } catch (e) {
        console.error('Nao foi possivel adicionar o passo:', e.code || '', e.message);
        toast({ kind: "err", msg: "Não foi possível adicionar este passo." });
        return false;
      }
    }
    setAlerts(arr => arr.map(a => {
      if (a.id !== alertId) return a;
      return { ...a, subtarefas: [...(a.subtarefas || []), { id, label, done: false }] };
    }));
    return true;
  }, [alerts, api, toast]);

  // ----- BANT inline-editável -----
  // GRAVA (levantamento de 02/10). Ate aqui so mexia na memoria: o texto do BANT sumia no F5. Passa por `updateLead`
  // (um caminho de escrita so, com o mesmo tratamento de 42501): grava o texto em `negocio_bant`, o numero em
  // `negocios.bant_score`, e so entao mostra na tela. Devolve true/false.
  const updateBant = React.useCallback((leadId, field, value) => {
    const atual = leadsRef.current.find(l => l.id === leadId)?.bant || {};
    return updateLead(leadId, { bant: { budget: '', authority: '', need: '', timeline: '', ...atual, [field]: value } });
  }, [updateLead]);

  // Anexos do negocio: arquivos no Supabase Storage (bucket privado) e metadados em `anexos`; migration 0098, spec
  // docs/superpowers/specs/2026-10-02-anexos-do-negocio-storage.md. O envio e a remocao SIMULAVAM ate 02/10 (o arquivo sumia no F5);
  // agora cada passo grava de verdade e a tela so mostra o que o banco confirmou. Falha de carga, envio, download ou remocao aparece
  // em toast + console.error: devolver lista vazia calado pareceria "nao ha anexos".
  const getAnexosByLead = React.useCallback((leadId) => anexos[leadId] || [], [anexos]);

  // via `usersRef` (declarado acima): a lista de usuarios muda ao carregar, e as funcoes de anexo nao podem mudar junto (a ficha recarregaria).
  const nomesDosUsuarios = React.useCallback(() => Object.fromEntries(usersRef.current.map((u) => [u.id, u.nome])), []);

  const carregarAnexos = React.useCallback(async (leadId) => {
    if (!api?.listarAnexos || !leadId) return;
    try {
      const lista = await api.listarAnexos(leadId, nomesDosUsuarios());
      setAnexos((prev) => ({ ...prev, [leadId]: lista }));
    } catch (e) {
      console.error('Nao foi possivel carregar os anexos:', e.message);
      toast({ kind: 'err', msg: 'Não foi possível carregar os anexos deste negócio. Tente de novo.' });
    }
  }, [api, toast, nomesDosUsuarios]);

  // Um de cada vez: a ordem fica previsivel e uma falha nao derruba os outros. Devolve quantos foram enviados.
  const enviarAnexos = React.useCallback(async (leadId, arquivos) => {
    if (!api?.enviarAnexo) return 0;
    let enviados = 0;
    for (const arquivo of Array.from(arquivos || [])) {
      const v = validarArquivo(arquivo);
      if (!v.ok) { toast({ kind: 'err', msg: v.motivo }); continue; }
      try {
        const novo = await api.enviarAnexo(leadId, arquivo, meRef.current.id, nomesDosUsuarios());
        setAnexos((prev) => ({ ...prev, [leadId]: [novo, ...(prev[leadId] || [])] }));
        enviados += 1;
      } catch (e) {
        console.error('Falha ao enviar o anexo:', e.message);
        toast({ kind: 'err', msg: `Não foi possível enviar "${arquivo.name}". Tente de novo.` });
      }
    }
    if (enviados) toast({ kind: 'ok', msg: enviados === 1 ? 'Arquivo anexado.' : `${enviados} arquivos anexados.` });
    return enviados;
  }, [api, toast, nomesDosUsuarios]);

  const removerAnexo = React.useCallback(async (leadId, anexoId) => {
    if (!api?.apagarAnexo) return false;
    try {
      await api.apagarAnexo(anexoId);
      setAnexos((prev) => ({ ...prev, [leadId]: (prev[leadId] || []).filter((a) => a.id !== anexoId) }));
      toast({ kind: 'ok', msg: 'Anexo removido.' });
      return true;
    } catch (e) {
      console.error('Falha ao remover o anexo:', e.message);
      toast({ kind: 'err', msg: 'Não foi possível remover o anexo. Tente de novo.' });
      return false;
    }
  }, [api, toast]);

  // O link vale 60 s e e pedido ao banco NA HORA do clique. O download sai por um <a> clicado de dentro do clique, que o navegador
  // aceita sem bloquear (um window.open depois de um await seria tratado como pop-up).
  const baixarAnexo = React.useCallback(async (anexo) => {
    if (!api?.urlDoAnexo || !anexo?.storagePath) return false;
    try {
      const url = await api.urlDoAnexo(anexo.storagePath, anexo.nome);
      const a = document.createElement('a');
      a.href = url; a.rel = 'noopener'; a.download = anexo.nome;
      document.body.appendChild(a); a.click(); a.remove();
      return true;
    } catch (e) {
      console.error('Falha ao gerar o link do anexo:', e.message);
      toast({ kind: 'err', msg: 'Não foi possível baixar o arquivo. Tente de novo.' });
      return false;
    }
  }, [api, toast]);

  // ----- Tabela de preços por negócio -----
  // Preço por produto NÃO se edita aqui: a aba mostra o calculado, e o preço de
  // venda muda na proposta (Kaio, 16/09). O antigo setLeadItemPrice gravava só
  // na memória e saiu em 29/09, junto com a célula que o chamava.
  // A tabela do negocio GRAVA (0077). Ate 25/09 ficava so na memoria e sumia
  // no F5 -- reteste do Kaio: "a tabela de preco ainda nao esta ficando fixa".
  const setLeadTabela = React.useCallback(async (leadId, tabelaId) => {
    const ok = await updateLead(leadId, { tabelaId: tabelaId || null });
    if (ok) toast({ kind: "ok", msg: "Tabela de preços do negócio atualizada" });
    return ok;
  }, [updateLead]);

  // ----- Propostas por negócio -----
  const getPropostasByLead = React.useCallback((leadId) => propostas[leadId] || [], [propostas]);
  // Insere no topo SEM duplicar. A carga inicial e a gravacao correm juntas: se a
  // carga resolver depois de o banco gravar, ela ja traz a proposta nova -- e o
  // insert a colocaria de novo. O vendedor veria a mesma proposta duas vezes, sem
  // saber qual e a boa. Corrida medida e reproduzida em 28/08
  // (tests/unit/store-propostas.test.js, "corrida entre a carga inicial e a criacao").
  const inserirProposta = React.useCallback((map, leadId, prop) => ({
    ...map,
    [leadId]: [prop, ...(map[leadId] || []).filter((p) => p.id !== prop.id)],
  }), []);

  // Fecha a criação nos dois caminhos (banco e memória): valor do negócio,
  // timeline e toast são idênticos — o que muda é de onde veio a proposta.
  const finalizarCriacao = React.useCallback((leadId, prop, data) => {
    // O VALOR do negocio quem grava e o banco (migration 0074): a selecionada
    // para fechamento; sem nenhuma, a ultima criada (Kaio, 25/09). Aqui so a
    // tela acompanha a mesma regra. Gravar daqui era recusado desde a 0037.
    const outras = (propostasRef.current[leadId] || []).filter(p => p.id !== prop.id && !p.deletedAt);
    if (!outras.some(p => p.selecionadaFechamento)) {
      setLeads(arr => arr.map(l => l.id === leadId ? { ...l, valor: prop.total } : l));
    }
    const emissora = EMISSORAS_BY_ID[prop.emissoraId];
    appendTimeline(leadId, {
      tipo: "note", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Proposta <strong>{prop.controle}</strong> ({emissora?.nome}) gerada · {prop.itens.length} itens · <strong>{brl(prop.total)}</strong>.
        {data?.tabelaSoNestaProposta && <> Tabela <strong>{data.tabelaSoNestaProposta.desta}</strong> só nesta proposta (a do negócio é {data.tabelaSoNestaProposta.doNegocio}).</>}</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: `Proposta ${prop.controle} gerada · ${brl(prop.total)}` });
  }, []);

  const createProposta = React.useCallback(async (leadId, data) => {
    // Caminho do banco (Etapa 3a). O que fica na tela é o que VOLTA mapeado, não
    // o que foi enviado: se o banco normalizar algo (o `controle` vem da sequence,
    // 0039), a tela mostra o valor real em vez de um palpite otimista.
    if (api) {
      try {
        const { proposta, itens } = api.propostaParaBanco(data, {
          negocioId: leadId, autorId: meRef.current.id, lookups: lookupsNomus,
        });
        const prop = api.mapearProposta(await api.criarProposta(proposta, itens));
        setPropostas(map => inserirProposta(map, leadId, prop));
        finalizarCriacao(leadId, prop, data);
        return prop.id;
      } catch (e) {
        // Falhar calado aqui é o pior caso do módulo: o vendedor fecha o modal
        // achando que a proposta existe. Sem toast, ele só descobre no F5.
        console.error('Não foi possível gravar a proposta:', e.message);
        toast({ kind: "err", msg: "Não foi possível gravar a proposta. Tente de novo." });
        return null;
      }
    }

    // Caminho de memória: o provider sobe sem `api` nos testes de unidade
    // (store.jsx:24-26). Mantido igual ao que era antes da 3a.
    const id = "prop_" + Date.now().toString(36);
    let ctrlSeq;
    setControleSeq(seq => { ctrlSeq = seq + 1; return ctrlSeq; });
    ctrlSeq = (ctrlSeq || (controleSeq + 1));
    const anoAtual = TODAY.getFullYear();
    const controle = `PRP-${anoAtual}-${String(ctrlSeq).padStart(4, "0")}`; // controle interno CRM (independente do ERP)
    const prop = {
      id,
      controle,                                // identificador da proposta no CRM (o nº CP só vem do ERP, por ordem de criação lá)
      nome: data.nome || "Proposta",
      projeto: data.projeto || "",
      emissoraId: data.emissoraId || "pdled",
      vendedor: data.vendedor || null,         // { nome, telefone, email }
      itens: data.itens || [],                 // [{ ambienteId, codigo, idProduto, quantidade, precoUnitario, idUnidadeMedida, pctDesconto, observacoes }]
      ambientes: data.ambientes || [],         // agrupamento dos itens (vira prefixo [AMBIENTE] na obs do 1º item)
      totalProduto: data.totalProduto || 0,
      frete: data.frete || 0,                  // frete COBRADO (base + margem): nada a jusante muda
      freteMargem: data.freteMargem || 0,      // parte do frete que e margem (0094); interna
      metragemCubica: data.metragemCubica ?? null,   // cubagem dos volumes em m3 (0095); NULL = nunca calculada
      pesoCubado: data.pesoCubado ?? null,           // m3 x 300 kg/m3, em kg (0095)
      icms: data.icms || 0,
      desconto: data.desconto || 0,
      descontoGlobal: data.descontoGlobal || 0,
      total: data.total || 0,
      previsaoFaturamento: data.previsaoFaturamento || "A combinar",
      transportadora: data.transportadora || TRANSPORTE_BY_ID[1].nome,
      validade: data.validade || "7 dias",
      formaPagamento: data.formaPagamento || "Boleto Bancário",   // nome (proposta impressa)
      formaPagamentoId: data.formaPagamentoId ?? 10,               // idFormaPagamento (Nomus)
      modalidadeTransporte: data.modalidadeTransporte ?? 0,        // 0=CIF (padrao decidido pelo Kaio, 08/10: a maioria contrata frete CIF), 1=FOB…
      idPessoaTransportadora: data.idPessoaTransportadora ?? null, // Nomus; referencia logica (0096)
      condicoes: data.condicoes || "",
      // ---- Campos exigidos pelo pedido Nomus (POST /pedidos) ----
      tabelaId: data.tabelaId || null,
      tipoVendaId: data.tipoVendaId || null,               // idTipoMovimentacao
      tipoVendaNome: data.tipoVendaNome || "",
      condicaoPagamentoId: data.condicaoPagamentoId || null,
      condicaoPagamentoNome: data.condicaoPagamentoNome || "",
      dataFaturamento: data.dataFaturamento || null,
      obsPublica: data.obsPublica || "",
      obsInterna: data.obsInterna || "",
      parcelas: data.parcelas || [],
      status: "rascunho",                      // rascunho | enviada | aprovada
      selecionadaFechamento: false,            // marcada para fechamento do negócio
      erp: { enviado: false, pedidoId: null }, // nº CP é atribuído pelo ERP na criação do pedido
      criadaEm: "agora",
      autor: meRef.current.nome,
    };
    setPropostas(map => ({ ...map, [leadId]: [prop, ...(map[leadId] || [])] }));
    finalizarCriacao(leadId, prop, data);
    return id;
  }, [controleSeq, api, finalizarCriacao, inserirProposta, lookupsNomus]);

  // Aplica o patch na tela e, quando há banco, grava. A tela é atualizada antes
  // da ida ao banco (otimista) porque a revalidação de preços já mexeu na lista
  // e piscar de volta seria pior; se a gravação falhar, o toast avisa e o F5
  // mostra o estado real — não fingimos que deu certo.
  const updateProposta = React.useCallback((leadId, propId, patch) => {
    // O ICMS-ST estimado (0093) vale para os itens com que foi calculado. Se os itens mudam e quem chamou nao
    // recalculou, ele deixa de valer: mante-lo mostraria ao cliente o imposto de uma proposta que ja nao existe.
    // Vira NULL ("nunca calculado"), que o documento imprime como "a confirmar".
    if (patch.itens && patch.icmsSt === undefined) patch = { ...patch, icmsSt: null, icmsStTotal: null };
    setPropostas(map => ({ ...map, [leadId]: (map[leadId] || []).map(p => p.id === propId ? { ...p, ...patch } : p) }));
    if (!api) return;
    (async () => {
      try {
        const dbPatch = api.propostaPatchParaBanco(patch, lookupsNomus);
        if (Object.keys(dbPatch).length) await api.atualizarProposta(propId, dbPatch);
        if (patch.itens) {
          const { itens } = api.propostaParaBanco(patch, { negocioId: leadId });
          await api.substituirItensProposta(propId, itens);
        }
      } catch (e) {
        console.error('Não foi possível salvar a alteração da proposta:', e.message);
        toast({ kind: "err", msg: "Não foi possível salvar a alteração da proposta." });
      }
    })();
  }, [api, lookupsNomus]);

  const removeProposta = React.useCallback((leadId, propId) => {
    setPropostas(map => ({ ...map, [leadId]: (map[leadId] || []).filter(p => p.id !== propId) }));
    if (api) {
      // Exclusão lógica no banco (deleted_at); a listagem já filtra.
      api.removerProposta(propId).catch((e) => {
        console.error('Não foi possível remover a proposta:', e.message);
        toast({ kind: "err", msg: "Não foi possível remover a proposta no banco." });
      });
    }
    toast({ kind: "info", msg: "Proposta removida" });
  }, [api]);

  // Atualiza status da proposta (rascunho | enviada | negociacao | aprovada | no_nomus | perdida)
  const updatePropostaStatus = React.useCallback((leadId, propId, novoStatus) => {
    let prop = null;
    setPropostas(map => ({
      ...map,
      [leadId]: (map[leadId] || []).map(p => {
        if (p.id !== propId) return p;
        prop = { ...p, status: novoStatus };
        return prop;
      }),
    }));
    if (api) {
      api.atualizarStatusProposta(propId, novoStatus).catch((e) => {
        console.error('Não foi possível salvar o status da proposta:', e.message);
        toast({ kind: "err", msg: "Não foi possível salvar o status da proposta." });
      });
    }
    const labels = { rascunho: "Rascunho", enviada: "Enviada ao Cliente", negociacao: "Em Negociação", aprovada: "Aprovada", no_nomus: "No Nomus", perdida: "Perdida" };
    appendTimeline(leadId, {
      tipo: "field", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Proposta <strong>{prop?.controle}</strong> → status <strong>{labels[novoStatus] || novoStatus}</strong>.</>,
      when: "agora",
    });
    toast({ kind: novoStatus === "perdida" ? "err" : "ok", msg: `Proposta ${labels[novoStatus] || novoStatus}` });
  }, [api]);

  // Cria revisão de uma proposta (cópia com sufixo Rev.N)
  // Cria no banco uma proposta DERIVADA de outra (revisão ou cópia). As duas só
  // diferem no vínculo: revisão amarra em revisao_de/rev_num, cópia nasce solta.
  // Em ambos os casos os itens vão junto — uma revisão sem os itens da original
  // obrigaria o vendedor a remontar a proposta inteira, que é o oposto do ponto.
  const criarDerivadaNoBanco = React.useCallback(async (leadId, original, { nome, revisaoDe = null, revNum = 0 }) => {
    // `lookups` aqui tambem: sem eles a revisao nasceria sem tipo de venda e sem
    // condicao de pagamento, e o vendedor perderia na copia o que a original tinha.
    const { proposta, itens } = api.propostaParaBanco({ ...original, nome }, {
      negocioId: leadId, autorId: meRef.current.id, lookups: lookupsNomus,
    });
    const linha = await api.criarProposta({
      ...proposta,
      status: 'rascunho',                 // derivada sempre nasce editável
      selecionada_fechamento: false,      // e nunca herda a marca de fechamento
      ...(revisaoDe ? { revisao_de: revisaoDe, rev_num: revNum } : {}),
    }, itens);
    const nova = api.mapearProposta(linha);
    setPropostas(map => inserirProposta(map, leadId, nova));
    return nova;
  }, [api, inserirProposta, lookupsNomus]);

  const criarRevisao = React.useCallback(async (leadId, propId) => {
    if (api) {
      const lista = propostas[leadId] || [];
      const original = lista.find(p => p.id === propId);
      if (!original) return undefined;
      // Conta pelo VÍNCULO (revisaoDe), não pelo texto do controle: o controle
      // agora é derivado do inteiro da sequence, e casar string para descobrir
      // o número da revisão quebraria assim que o rótulo mudasse.
      const revNum = lista.filter(p => p.revisaoDe === propId).length + 1;
      const nomeBase = original.nome.replace(/ Rev\.\d+$/, "");
      try {
        const nova = await criarDerivadaNoBanco(leadId, original, {
          nome: `${nomeBase} Rev.${revNum}`, revisaoDe: propId, revNum,
        });
        appendTimeline(leadId, {
          tipo: "note", autorTipo: "humano", autor: meRef.current.nome,
          texto: <>Revisão <strong>{nova.controle}</strong> criada a partir de <strong>{original.controle}</strong>.</>,
          when: "agora",
        });
        toast({ kind: "ok", msg: `Revisão ${nova.controle} criada` });
        return nova.id;
      } catch (e) {
        console.error('Não foi possível criar a revisão:', e.message);
        toast({ kind: "err", msg: "Não foi possível criar a revisão." });
        return null;
      }
    }

    let nova = null;
    setPropostas(map => {
      const lista = map[leadId] || [];
      const original = lista.find(p => p.id === propId);
      if (!original) return map;
      // Conta quantas revisões já existem
      const baseControle = original.controle.replace(/ Rev\.\d+$/, "");
      const revCount = lista.filter(p => p.controle.startsWith(baseControle + " Rev.")).length;
      const revNum = revCount + 1;
      nova = {
        ...original,
        id: "prop_" + Date.now().toString(36),
        controle: `${baseControle} Rev.${revNum}`,
        nome: original.nome.replace(/ Rev\.\d+$/, "") + ` Rev.${revNum}`,
        status: "rascunho",
        erp: { enviado: false, pedidoId: null },
        criadaEm: "agora",
        revisaoDe: propId,
        revisaoNum: revNum,
      };
      return { ...map, [leadId]: [nova, ...lista] };
    });
    if (nova) {
      appendTimeline(leadId, {
        tipo: "note", autorTipo: "humano", autor: meRef.current.nome,
        texto: <>Revisão <strong>{nova.controle}</strong> criada a partir de <strong>{nova.controle.replace(/ Rev\.\d+$/, "")}</strong>.</>,
        when: "agora",
      });
      toast({ kind: "ok", msg: `Revisão ${nova.controle} criada` });
    }
    return nova?.id;
  }, [api, propostas, criarDerivadaNoBanco]);

  // Duplica proposta (cópia independente em Rascunho)
  const duplicarProposta = React.useCallback(async (leadId, propId) => {
    if (api) {
      const original = (propostas[leadId] || []).find(p => p.id === propId);
      if (!original) return undefined;
      try {
        const copia = await criarDerivadaNoBanco(leadId, original, { nome: `${original.nome} (cópia)` });
        toast({ kind: "ok", msg: `Proposta duplicada · ${copia.controle}` });
        return copia.id;
      } catch (e) {
        console.error('Não foi possível duplicar a proposta:', e.message);
        toast({ kind: "err", msg: "Não foi possível duplicar a proposta." });
        return null;
      }
    }

    let copia = null;
    setPropostas(map => {
      const original = (map[leadId] || []).find(p => p.id === propId);
      if (!original) return map;
      copia = {
        ...original,
        id: "prop_" + Date.now().toString(36),
        controle: original.controle + " (cópia)",
        nome: original.nome + " (cópia)",
        status: "rascunho",
        selecionadaFechamento: false,
        erp: { enviado: false, pedidoId: null },
        criadaEm: "agora",
        copiaDe: propId,
      };
      return { ...map, [leadId]: [copia, ...(map[leadId] || [])] };
    });
    if (copia) toast({ kind: "ok", msg: `Proposta duplicada · ${copia.controle}` });
    return copia?.id;
  }, [api, propostas, criarDerivadaNoBanco]);

  // Selecionar UMA proposta para fechamento do negócio (demais ficam não-selecionadas)
  const selectPropostaForClosing = React.useCallback((leadId, propId) => {
    let sel = null;
    setPropostas(map => ({
      ...map,
      [leadId]: (map[leadId] || []).map(p => {
        const on = p.id === propId;
        if (on) sel = p;
        return { ...p, selecionadaFechamento: on };
      }),
    }));
    // O banco grava o valor (0074); a tela so acompanha: a selecionada, ou,
    // sem nenhuma, a ultima criada -- a lista vem da mais nova para a mais antiga.
    const ultima = (propostasRef.current[leadId] || []).find(p => !p.deletedAt);
    const valorNovo = sel ? sel.total : ultima?.total;
    if (valorNovo != null) {
      setLeads(arr => arr.map(l => l.id === leadId ? { ...l, valor: valorNovo } : l));
    }
    if (api) {
      // Grava só quem MUDOU: a escolhida e a que perdeu a marca. Reescrever a
      // lista inteira gastaria uma ida ao banco por proposta do negócio.
      for (const p of (propostas[leadId] || [])) {
        const on = p.id === propId;
        if (!!p.selecionadaFechamento === on) continue;
        api.atualizarProposta(p.id, api.propostaPatchParaBanco({ selecionadaFechamento: on }))
          .catch((e) => {
            console.error('Não foi possível salvar a proposta de fechamento:', e.message);
            toast({ kind: "err", msg: "Não foi possível salvar a proposta escolhida para fechamento." });
          });
      }
    }
    appendTimeline(leadId, {
      tipo: "field", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Proposta <strong>{sel?.controle}</strong> selecionada para fechamento do negócio · valor <strong>{brl(sel?.total || 0)}</strong>.</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: `Proposta ${sel?.controle} marcada para fechamento` });
  }, [api, propostas]);

  // Invalida o link publico. Par da expiracao de 180 dias: o prazo cobre o
  // esquecimento, isto cobre o acidente -- proposta que foi para o endereco
  // errado, ou cliente que nao deve mais ver aquele preco.
  const revogarLinkProposta = React.useCallback(async (leadId, propId) => {
    if (!api) return;
    try {
      await api.revogarLinkProposta(propId);
    } catch (e) {
      console.error('Nao foi possivel invalidar o link da proposta:', e.message);
      toast({ kind: "err", msg: "Nao foi possivel invalidar o link." });
      return;
    }
    setPropostas(map => ({ ...map, [leadId]: (map[leadId] || []).map(p => (
      p.id === propId ? { ...p, temLinkPublico: false } : p
    )) }));
    appendTimeline(leadId, {
      tipo: "alert", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Link publico da proposta <strong>invalidado</strong>. Quem tiver o endereco antigo nao consegue mais abrir.</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: "Link invalidado. Envie a proposta de novo para gerar outro." });
  }, [api, toast]);

  // Enviar a proposta ao cliente por e-mail.
  //
  // O QUE ISTO ERA ATÉ 15/09: marcava o status, escrevia uma linha de log
  // dizendo "E-mail · Resend · status 200" e anunciava "proposta enviada". Nada
  // saía. Não era um recurso faltando — era o sistema afirmando um fato que não
  // aconteceu, e a tela de logs confirmando.
  //
  // Agora quem envia é a Edge Function `enviar-proposta-email` (a chave do
  // Resend não entra no pacote, §4), e NADA aqui declara sucesso antes de ela
  // responder que o e-mail saiu.
  const sendProposta = React.useCallback(async (leadId, propId) => {
    if (!api) return;
    let r;
    try {
      r = await api.enviarPropostaPorEmail(propId);
    } catch (e) {
      console.error('Falha ao enviar a proposta por e-mail:', e.code || '', e.message, e.causa || '');
      toast({ kind: "err", msg: e.message });
      return;
    }

    if (!r.ok) {
      // A função já devolve o motivo em português, e ele é acionável: cliente
      // sem e-mail se resolve no cadastro, recusa do provedor não. Trocar isso
      // por um "não foi possível enviar" genérico jogaria fora justamente a
      // parte que diz o que fazer — foi o erro que o envio ao Nomus cometeu e
      // que custou uma tarde em 14/09.
      console.error('Envio da proposta recusado:', r.status, r.erro);
      toast({ kind: "err", msg: r.erro || "Não foi possível enviar a proposta." });
      return;
    }

    const enviadaEm = r.enviadoEm || new Date().toISOString();
    setPropostas(map => ({ ...map, [leadId]: (map[leadId] || []).map(p => (
      p.id === propId ? { ...p, status: "enviada", enviadaEm, emailEnviadoPara: (r.enviadoPara || []).join(', '), tokenPublico: r.link } : p
    )) }));

    // `enviada_em` continua sendo gravada daqui, como antes — a função grava
    // `email_enviado_em`, que é outra coluna e outra pergunta ("quando o e-mail
    // saiu", não "quando a proposta passou a valer como enviada").
    api.atualizarProposta(propId, api.propostaPatchParaBanco({ status: "enviada", enviadaEm }))
      .catch((e) => {
        // O e-mail JÁ SAIU. Isto não desfaz nada, mas tem de aparecer: sem a
        // marca, a proposta volta do banco parecendo nunca enviada.
        console.error('Não foi possível registrar o envio da proposta:', e.message);
        toast({ kind: "err", msg: "O e-mail saiu, mas não foi possível registrar o envio." });
      });

    const destino = (r.enviadoPara || []).join(', ');
    appendTimeline(leadId, {
      tipo: "email", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Proposta enviada por e-mail{destino ? <> para <strong>{destino}</strong></> : ""}.</>,
      when: "agora",
    });
    // Dizer PARA QUEM foi, e não só "enviada": 4% dos cadastros guardam dois
    // endereços no mesmo campo, e o vendedor precisa ver que foram os dois.
    toast({ kind: "ok", msg: destino ? `Proposta enviada para ${destino}` : "Proposta enviada ao cliente" });
  }, [api]);

  // ===== ERP (Nomus) — proposta aprovada → pedido POST /pedidos =====
  // Integração real: POST https://pdled.nomus.com.br/pdled/rest/pedidos (Basic Auth integradorerp).
  // Valida campos obrigatórios antes de enviar; monta payload completo conforme mapeamento do design.
  const approvePropostaToERP = React.useCallback(async (leadId, propId) => {
    const lista   = propostas[leadId] || [];
    const prop    = lista.find(p => p.id === propId);
    const lead    = leads.find(l => l.id === leadId);
    const cliente = lead?.clienteId ? clientes.find(c => c.id === lead.clienteId) : null;
    if (!prop) return null;

    // Idempotência: uma proposta gera UM pedido. Reenviar devolveria um CP
    // órfão no ERP — para revisar o pedido, crie uma revisão da proposta.
    //
    // `enviada_em` SAIU desta guarda em 14/09, e a remoção conserta um remendo
    // que sobreviveu à sua razão. Na 3a `numero_cp` ainda não era escrito, então
    // a guarda ficava cega depois de um F5 e `enviada_em` entrou como substituta
    // — o comentário original dizia isso com todas as letras.
    //
    // Só que `enviada_em` marca "proposta enviada AO CLIENTE", que não tem
    // relação nenhuma com "pedido criado no ERP". O efeito prático: mandar o PDF
    // ao cliente — o caminho normal de qualquer venda — travava o envio ao Nomus
    // e exigia uma revisão sem motivo nenhum.
    //
    // Na 3b o motivo do remendo acabou: a Edge Function grava `numero_cp` JUNTO
    // com a marca de envio em voo, então `erp.pedidoId` sobrevive ao reload e
    // sustenta a trava sozinho. `pedido_nomus` no negócio cobre o pedido criado
    // por fora. E a guarda que vale é a do servidor (logica.ts); esta só evita
    // uma ida à rede.
    const pedidoExistente = prop.erp?.pedidoId || lead?.pedidoNomus || null;
    if (pedidoExistente) {
      toast({ kind: "err", msg: `Proposta já gerou o pedido ${pedidoExistente} · crie uma revisão para reenviar` });
      return pedidoExistente;
    }

    // Vendedor do pedido: o dono do negócio (fallback = usuário da sessão).
    const dono = lead?.ownerId ? usersRef.current.find(u => u.id === lead.ownerId) : null;
    const vendedorPedido = prop.vendedor || dono || meRef.current;
    const idVendedor = vendedorPedido?.idPessoaVendedor ?? null;

    // Validação dos campos obrigatórios Nomus
    const erros = [];
    if (!prop.emissoraId) erros.push("Emissora não selecionada");
    if (!cliente?.erpId)  erros.push(`Cliente "${cliente?.nome || "?"}" não tem idPessoaCliente no Nomus — sincronize o cadastro primeiro`);
    if (!idVendedor)      erros.push(`Vendedor "${vendedorPedido?.nome || "?"}" não tem idPessoaVendedor no Nomus — cadastre em Admin → Usuários`);
    if (!prop.tipoVendaId) erros.push("Tipo de venda (idTipoMovimentacao) não definido na proposta");
    if (!prop.condicaoPagamentoId) erros.push("Condição de pagamento não definida na proposta");
    if (!prop.formaPagamentoId) erros.push("Forma de pagamento (idFormaPagamento) não definida na proposta");
    // Proposta ANTERIOR ao registro da modalidade (0096): nao se assume FOB nem CIF. O vendedor confirma no cartao da proposta
    // (decisao do Kaio, 08/10). Antes, o pedido saia com FOB por padrao, qualquer que fosse a escolha na tela.
    if (prop.modalidadeTransporte == null) erros.push("Modalidade de transporte não confirmada nesta proposta — confirme no cartão da proposta antes de enviar ao Nomus");
    // Barrar aqui e melhor que levar 406 do ERP: a mensagem sai em portugues, na
    // hora, e dizendo qual campo. Entrou depois de o Nomus recusar o primeiro
    // envio real por falta dela (14/09).
    if (!prop.dataFaturamento) erros.push("Data de faturamento não definida na proposta — o Nomus a exige (ele a chama de data de entrega)");
    // PEDIDO DE COMPRA NAO E OBRIGATORIO (decisao do Kaio, 16/09). Ele era
    // barrado em venda corporativa, e o ERP nunca exigiu -- a exigencia era
    // nossa. Cliente corporativo que fecha sem emitir PC existe, e travar o
    // envio por causa disso parava a venda por uma regra que nao e do Nomus
    // nem da empresa.
    const itensSemId = (prop.itens || []).filter(it => !it.idProduto);
    if (itensSemId.length) erros.push(`${itensSemId.length} item(ns) sem idProduto Nomus: ${itensSemId.map(i => i.codigo).join(", ")}`);
    if (!(prop.itens || []).length) erros.push("Proposta sem itens");
    if (erros.length) {
      toast({ kind: "err", msg: erros[0] });
      return null;
    }

    // Nº CP: quem escolhe é a Edge Function, não esta tela.
    //
    // Medido em 11/09: o Nomus atribui o CP na PROPOSTA e o pedido herda. O
    // maior CP entre pedidos era 10421 e entre propostas 10423 — "maior pedido
    // + 1" daria 10422, número que uma proposta aberta já tinha reservado. Só o
    // servidor vê as duas listas ao vivo, e é lá que a colisão acontece.
    //
    // Sem banco (modo protótipo) a sequência local continua, para a tela ter o
    // que mostrar. Esse número nunca chega ao ERP.
    const cpNum = propostaSeqRef.current + 1;
    const emissora = EMISSORAS_BY_ID[prop.emissoraId] || EMISSORAS[0];

    // Itens. CADA CAMPO NO SEU CAMPO, e isto e conserto de 16/09.
    //
    // O comentario que estava aqui dizia "o Nomus nao tem conceito de ambiente".
    // E falso, e a medicao mostra o tamanho do engano -- 7.854 itens de pedido
    // reais:
    //
    //   observacoes ................... 60,4%  "10m+10m+4m"  "2X4,25M(45°)"
    //   informacoesAdicionaisProduto ... 51,2%  "GARAGEM"  "COZINHA"  "CIRC.INT"
    //
    // `observacoes` do item E A MEDIDA DE CORTE, e o ambiente tem campo proprio.
    // O CRM mandava o ambiente em `observacoes` -- ou seja, escrevia "[COZINHA]"
    // onde a producao le "2X4,25M(45°)" --, nunca preenchia o campo certo, e
    // ainda so mandava no PRIMEIRO item do grupo. Na pratica da PDLED todos os
    // itens do ambiente o carregam (conferido no CP 10428: os 20 itens tem).
    const itensNomus = (prop.itens || []).map((it, idx) => {
      const ambNome = it.ambienteId ? ((prop.ambientes || []).find(a => a.id === it.ambienteId)?.nome || "") : "";
      // O CAMPO "Dimensoes" DA TELA E A MEDIDA DE CORTE. O vendedor digita "2,4m"
      // ali (o proprio placeholder e "Dimensoes (ex: 2,4m)"), e no ERP isso e o
      // que a producao le em `observacoes` do item -- medido: "10m+10m+4m",
      // "2X4,25M(45°)", "P/6,00M".
      //
      // ATE 16/09 ISSO NUNCA CHEGOU AO ERP. O payload mandava `it.observacoes`,
      // um campo do modelo que nao tem entrada na tela e portanto era sempre
      // vazio -- so o prefixo "[AMBIENTE]" o deixava nao-vazio. O vendedor
      // digitava a medida de corte e ela morria na tela.
      //
      // As duas dividem `observacoes`, separadas por " · ", como o pedido de
      // compra ja faz no cabecalho: o Nomus nao tem campo para as duas.
      const obsDoItem = [it.dimensoes, it.observacoes].map(x => String(x || "").trim()).filter(Boolean).join(" · ");
      const descEfetivo = Math.max(it.pctDesconto || 0, prop.descontoGlobal || 0);
      return {
        item: String(idx + 1).padStart(3, "0"),
        idProduto: Number(it.idProduto),
        // OBRIGATORIO, e o contrato que temos NAO diz (uploads/nomus_api_pedidos.md
        // lista 13 campos do item e este nao esta entre eles). Descoberto pelo ERP
        // recusando o envio com {"campo":"Data de entrega","codigo":"regra.campoRequerido"}
        // mesmo com `dataFaturamentoPadrao` preenchido no cabecalho -- o "padrao" do
        // cabecalho nao supre o item. Medido: 26.865 de 26.865 itens reais (100%)
        // trazem `dataFaturamento`.
        //
        // Usa a data da proposta para todos os itens: o CRM tem UMA data de
        // entrega, nao uma por linha. O ERP aceita divergir (3.478 dos 26.808
        // itens tem data propria), mas inventar uma por item aqui seria afirmar
        // um combinado que nao existe.
        // FRONTEIRA COM O ERP: o campo la se chama `dataEntrega` e e
        // obrigatorio; aqui a coluna se chama faturamento, que e como a
        // PDLED fala (0060). A traducao acontece aqui e so aqui.
        dataEntrega: nomusDataHora(prop.dataFaturamento),
        quantidade: nomusNum(it.quantidade, 4),
        valorUnitario: nomusNum(it.precoUnitario),
        percentualDesconto: String(descEfetivo),
        idUnidadeMedida: Number(it.idUnidadeMedida || 4),
        // A MEDIDA DE CORTE, que e o que a producao le aqui.
        observacoes: obsDoItem,
        // O AMBIENTE, no campo que a PDLED de fato usa -- e em TODOS os itens do
        // grupo, nao so no primeiro.
        informacoesAdicionaisProduto: ambNome ? ambNome.toUpperCase() : "",
        status: 1,
      };
    });

    const tabela = tabelasPreco.find(t => t.id === prop.tabelaId);

    // Contrato: docs da API Nomus EC (uploads/nomus_api_pedidos.md).
    // Datas "DD/MM/YYYY HH:MM:SS"; valores monetários como string.
    // Campos que o Nomus recebe no cabeçalho mas a API não expõe como campo
    // próprio: pedido de compra do cliente e condição de pagamento em texto livre.
    // TODO[ERP]: confirmar com o Nomus se há campo dedicado; hoje vão em
    // observacoes / infCpl. Volumes, pesos, redespacho, local de retirada,
    // retenção de tributos e exportação ficam registrados no CRM (prop.fiscal)
    // e são conferidos no pedido — a API de pedidos ainda não os aceita.
    // Declarado ANTES do payload porque o payload o LE (`codigoPedido`). O
    // protótipo declarava aqui; a Task 4 desceu a declaração para junto do envio
    // ao trocar para `let` (o número passou a poder vir da Edge Function) e criou
    // uma temporal dead zone: ReferenceError na montagem do payload. Ninguém viu
    // porque os dois testes de approvePropostaToERP retornam antes desta linha
    // (guarda de idempotência e validação) e no protótipo nenhum cliente tem
    // erpId -- a linha nunca chegou a rodar.
    let pedidoId = "CP " + cpNum;

    const obsPublicaNomus = [prop.pedidoCompraCliente ? `PC cliente: ${prop.pedidoCompraCliente}` : "", prop.obsPublica || ""].filter(Boolean).join(" · ");
    const fiscal = prop.fiscal || {};

    const payload = {
      codigoPedido: pedidoId,
      idEmpresa: Number(emissora.idEmpresa || 2),
      idTipoPedido: 1,
      idTipoMovimentacao: Number(prop.tipoVendaId),
      idPessoaCliente: Number(cliente.erpId),
      idPessoaVendedor: Number(idVendedor),
      idPessoaTransportadora: prop.idPessoaTransportadora ?? null,
      idFormaPagamento: Number(prop.formaPagamentoId),
      idCondicaoPagamento: Number(prop.condicaoPagamentoId),
      // `idNomus`, nao `idTabelaPrecoNomus`: esse segundo nome so existia nas
      // QUATRO TABELAS INVENTADAS de src/data.jsx (todas com o campo nulo). O
      // mapeador real (0058) expoe `idNomus`, entao isto era sempre `undefined`
      // e o CRM nunca disse ao ERP qual tabela foi usada -- justamente o campo
      // que permitiu medir, em 16/09, qual tabela a PDLED mais usa.
      idTabelaPreco: tabela?.idNomus ?? null,
      dataEmissao: nomusDataHora(prop.dataEmissao || new Date()),
      dataEntregaPadrao: nomusDataHora(prop.dataFaturamento),
      modalidadeTransporte: Number(prop.modalidadeTransporte),   // nunca nulo aqui: a validacao acima barra a proposta antiga
      observacoes: obsPublicaNomus,
      observacoesInternas: prop.obsInterna || "",
      infCpl: prop.condicaoPagamentoTexto || "",
      valorTotalFrete: nomusValor(prop.frete),
      valorTotalSeguro: nomusValor(fiscal.seguro ?? prop.seguro),
      valorTotalOutrasDespesasAcessorias: nomusValor(fiscal.outrasDespesas ?? prop.outrasDespesas),
      itensPedido: itensNomus,
      parcelas: (prop.parcelas || []).map(p => ({
        dataVencimento: nomusData(p.vencimento),
        valorParcela: nomusNum(p.valor),
        geraAdiantamento: !!p.adiantamento,
      })),
    };

    // ----- O envio de verdade ------------------------------------------
    // Com banco, quem decide tudo é a Edge Function: ela tem a credencial do
    // ERP, escolhe o nº CP contra a sequência viva e trata a resposta perdida.
    // Sem banco (protótipo), o caminho antigo segue, para a tela continuar
    // demonstrável — mas nada sai daqui para o Nomus.
    if (api) {
      let r;
      try {
        r = await api.enviarPedidoAoNomus(propId, payload);
      } catch (e) {
        // §5: falha tem de ser visível -- e ÚTIL. A versão anterior trocava
        // qualquer erro por "Não foi possível falar com o Nomus", inclusive
        // "sua sessão expirou", que se resolve entrando de novo. Quem estava na
        // tela repetia um clique que nunca ia funcionar. A mensagem de quem
        // levantou o erro já é específica; só se usa a genérica se não houver.
        console.error('Falha ao enviar pedido ao Nomus:', e?.code || '?', e?.message || e, e?.causa || '');
        toast({ kind: "err", msg: e?.message || "Não foi possível falar com o Nomus. Tente de novo." });
        return null;
      }
      if (!r.ok) {
        console.error('Nomus recusou o envio:', r.status, r.erro, r.detalhe || '');
        toast({
          kind: "err",
          // "Em voo" não é recusa: o pedido pode existir no ERP. Oferecer
          // reenvio aqui é o que criaria o segundo CP para a mesma venda.
          msg: r.emVoo
            ? "Envio em andamento — confira no Nomus antes de tentar de novo."
            : (r.erro || "O Nomus recusou o pedido."),
        });
        setPropostas(map => ({
          ...map,
          [leadId]: (map[leadId] || []).map(p => p.id === propId ? { ...p, erp: { ...(p.erp || {}), emVoo: !!r.emVoo } } : p),
        }));
        return null;
      }
      pedidoId = r.pedido || pedidoId;
    } else {
      propostaSeqRef.current = cpNum;
      setPropostaSeq(cpNum);
    }

    setPropostas(map => ({
      ...map,
      [leadId]: (map[leadId] || []).map(p => {
        if (p.id !== propId) return p;
        return { ...p, status: "no_nomus", selecionadaFechamento: true, erp: { enviado: true, pedidoId, cpNumero: cpNum, enviadoEm: "agora", emVoo: false } };
      }).map(p => p.id === propId ? p : { ...p, selecionadaFechamento: false }),
    }));

    pushApiLog({ agente: "Nomus ERP · Pedidos", token: "nomus_integradorerp", method: "POST", endpoint: "/pdled/rest/pedidos", status: 201, time: Math.floor(180 + Math.random() * 200), payloadJson: JSON.stringify(payload, null, 2) });
    appendTimeline(leadId, { tipo: "alert", autorTipo: "agente", autor: "Integração · Nomus ERP", texto: <>Proposta <strong>{prop.controle}</strong> enviada ao Nomus · pedido <strong>{pedidoId}</strong> · {(prop.itens||[]).length} itens · <strong>{brl(prop.total)}</strong>.</>, when: "agora" });
    setLeads(arr => arr.map(l => l.id === leadId
      ? { ...l, pedidoNomus: pedidoId, ...(prop.total > 0 ? { valor: prop.total } : {}) }
      : l));
    // O pedido existe no ERP: o negócio está fechado. Avança para GANHO quem ainda
    // não chegou lá — produção e faturamento seguem no pós-venda.
    const stagesFunil = etapasPorFunil[lead?.funil] || [];
    const iAtual = stagesFunil.findIndex(x => x.id === lead?.stage);
    const iGanho = stagesFunil.findIndex(x => x.id === "GANHO");
    if (iGanho >= 0 && iAtual >= 0 && iAtual < iGanho) {
      setTimeout(() => moveLeadStageRef.current(leadId, "GANHO", { autor: "Integração · Nomus ERP", autorTipo: "agente" }), 80);
    }
    toast({ kind: "ok", msg: `Pedido ${pedidoId} criado no Nomus ERP` });
    return pedidoId;
  }, [propostas, leads, clientes, etapasPorFunil, api, tabelasPreco]);

  const exportIcs = React.useCallback((alert) => {
    const lead = leads.find(l => l.id === alert.leadId);
    const fmt = (d) => (d instanceof Date ? d : new Date(d)).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const dt = fmt(alert.when instanceof Date ? alert.when : new Date());
    // DTEND de verdade. Sem ele, a RFC 5545 §3.6.1 diz que o evento tem duracao
    // zero -- e ate 22/09 o .ics saia assim, deixando o Google/Outlook chutar a
    // duracao. Agora sai a janela real da tarefa.
    const dtEnd = fmt(alert.fim || new Date((alert.when instanceof Date ? alert.when : new Date()).getTime() + 3600e3));
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//BeamCRM//PT-BR",
      "BEGIN:VEVENT",
      "UID:" + alert.id + "@beamcrm",
      "DTSTAMP:" + dt,
      "DTSTART:" + dt,
      "DTEND:" + dtEnd,
      "SUMMARY:" + alert.tipo + (lead ? " — " + lead.empresa : ""),
      "DESCRIPTION:" + (alert.canal || ""),
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = (alert.tipo || "alerta").replace(/\s+/g, "_") + ".ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast({ kind: "ok", msg: ".ics exportado" });
  }, [leads]);

  // ----- Clientes -----
  // GRAVA, e o ID VEM DO BANCO.
  //
  // Ate 18/09 isto era `setClientes(...)` e um id inventado (`"cli_" + tempo`):
  // o vendedor cadastrava, a tela confirmava, e no F5 o cliente nao existia. Era
  // o pior dos 19 casos levantados em 16/09 -- cadastro e a porta de entrada, e
  // sem cliente gravado nao ha proposta, nem pedido, nem nota.
  //
  // E ASSINCRONO de proposito. O id da coluna e uuid gerado pelo banco; devolver
  // um id local antes da escrita faria a tela abrir a ficha de um cliente que
  // ainda pode falhar ao gravar -- e faria o `syncClienteERP` seguinte apontar
  // para um id que nao existe.
  //
  // A tela SO recebe o cliente depois que o banco confirma, e semeado do que
  // VOLTOU: o banco normaliza (cidade e UF partidos, `ie` vazia virando null), e
  // mostrar o que foi enviado esconderia a diferenca ate o proximo reload.
  const createCliente = React.useCallback(async (data) => {
    const { isCanal, canalTipo, canalResponsavel, parceriaRepasse, pctRepasse, ...rest } = data;
    const cidadeParts = (rest.cidade || "").split(" · ");
    const doCanal = {
      isCanal: !!isCanal,
      canalTipo: isCanal ? (canalTipo || "Arquitetura") : null,
      canalResponsavel: isCanal ? (canalResponsavel || rest.contato || "") : null,
      canalAtivo: isCanal ? true : null,
      parceriaRepasse: isCanal ? !!parceriaRepasse : false,
      // `pctRepasse` NAO vai ao banco no cadastro: a % do canal e do Financeiro
      // (0085) e se define DEPOIS, na ficha. Mandar `pct_repasse: 0` em todo
      // cadastro foi o que derrubou a criacao de cliente (30/09, item 2.1).
    };

    if (!api) {
      // Sem camada de dados (testes de tela antigos), mantem o comportamento
      // anterior para nao quebrar quem so desenha.
      const id = "cli_" + Date.now().toString(36);
      setClientes(arr => [{ id, ...rest, ...doCanal, pctRepasse: isCanal ? (Number(pctRepasse) || 0) : 0 }, ...arr]);
      toast({ kind: "ok", msg: isCanal ? `Canal de vendas "${data.nome}" criado` : `Cliente "${data.nome}" criado` });
      return id;
    }

    const patch = api.clientePatchParaBanco({ ...rest, ...doCanal });
    // O endereco da ficha tem cidade e UF em campos proprios; o formulario curto
    // manda "Cidade · UF" numa string so. `clientePatchParaBanco` ja parte a
    // string, e aqui montamos o endereco com as mesmas partes para as duas
    // tabelas nao discordarem.
    const endereco = rest.endereco || (cidadeParts[0]
      ? { cidade: cidadeParts[0], uf: cidadeParts[1] || "" }
      : null);

    try {
      const linha = await api.criarCliente(patch, endereco);
      const novo = api.mapearCliente(linha);
      // Na hora, e nao so no proximo render: quem chamou segue para o ERP no
      // mesmo instante e precisa achar o cliente (ver clientesRef).
      clientesRef.current = [novo, ...clientesRef.current];
      setClientes(arr => [novo, ...arr]);
      toast({ kind: "ok", msg: isCanal ? `Canal de vendas "${novo.nome}" criado` : `Cliente "${novo.nome}" criado` });
      return novo.id;
    } catch (e) {
      // Falha de carga tem de ser VISIVEL (§5 do CLAUDE.md). E, principalmente,
      // NAO devolve id: quem chamou nao pode seguir para o ERP com um cadastro
      // que nao existe.
      console.error('Nao foi possivel criar o cliente:', e.code || '', e.message);
      // Documento repetido: quem cadastra (SDR, BDR) so enxerga o que cadastrou ou
      // tem negocio dele, entao a tela nao tinha como avisar antes. E a unica forma
      // de a pessoa descobrir que o cliente existe -- e o que fazer.
      const repetido = e.code === '23505' && /uq_clientes_(cnpj|cpf)/.test(String(e.message));
      toast({ kind: "err", msg: repetido
        ? "Este CNPJ/CPF já está cadastrado. Peça à ADM de Vendas para vincular ao seu negócio."
        : e.tipo === "nicho" ? `${e.message}. O cliente não foi criado.`
        : "Não foi possível criar o cliente. Confira os dados e tente de novo." });
      return null;
    }
  }, [api, toast]);

  // GRAVA. Ate 16/09 isto era `setClientes(...)` e ponto: editar cliente nao
  // sobrevivia ao F5 -- nem o endereco, nem a inscricao estadual, nem a razao
  // social. O formulario aceitava, mostrava salvo, e o reload desfazia.
  //
  // Sexto caso do mesmo padrao nesta semana, e o mais caro dos seis: cadastro
  // errado de cliente vira nota fiscal errada.
  // Devolve uma Promise<boolean> (salvou ou nao) para quem quiser so dizer "salvo"
  // DEPOIS de o banco aceitar; quem nao se importa ignora o retorno.
  const updateCliente = React.useCallback((id, patch) => {
    setClientes(arr => arr.map(c => c.id === id ? { ...c, ...patch } : c));
    if (!api) return Promise.resolve(true);
    const dbPatch = api.clientePatchParaBanco(patch);
    // Endereco so viaja quando a tela mandou um: patch de um campo so (mudar a
    // tabela de preco, por exemplo) nao pode apagar o endereco de ninguem.
    const endereco = patch.endereco ? api.enderecoParaBanco(id, patch.endereco) : null;
    if (!Object.keys(dbPatch).length && !endereco) return Promise.resolve(true);
    return api.atualizarCliente(id, dbPatch, endereco)
      .then((linha) => {
        // Semeia do que VOLTOU: o banco normaliza (cidade/UF partidos, ie vazia
        // virando null) e a tela tem de mostrar o que ficou gravado, nao o que
        // foi enviado.
        if (linha) setClientes(arr => arr.map(c => c.id === id ? api.mapearCliente(linha) : c));
        return true;
      })
      .catch((e) => {
        console.error('Não foi possível salvar o cliente:', e.code || '', e.message);
        // 42501 vindo do gatilho da % do canal traz a razao em portugues.
        const soFinanceiro = e.code === '42501' && /Financeiro/.test(String(e.message));
        // Erro de nicho (inválido, ou lista não carregada) diz a razão: o genérico
        // faria a pessoa refazer a edição sem saber que o problema é o nicho.
        toast({ kind: 'err', msg: soFinanceiro ? 'Só o Financeiro define a % do repasse do canal.'
          : e.tipo === 'nicho' ? `${e.message}. O cadastro do cliente não foi salvo.`
          : 'Não foi possível salvar o cadastro do cliente.' });
        return false;
      });
  }, [api, toast]);

  // ===== Integração ERP (Nomus) — sincroniza cadastro do cliente =====
  // Integração real: POST/PUT https://pdled.nomus.com.br/pdled/rest/clientes (Basic Auth integradorerp).
  // Mock: gera/atualiza o idPessoaCliente (erpId) e registra o disparo nos API Logs.
  // SINCRONIZA DE VERDADE. Ate 18/09 isto inventava o identificador --
  // `String(40000 + Math.random() * 59999)` -- e escrevia um log de API falso
  // dizendo "Cliente criado no Nomus ERP - ID 47231". Nenhuma chamada saia.
  //
  // A consequencia foi medida: `id_pessoa_nomus` nunca era preenchido, e o
  // primeiro envio real de pedido barrou em "Cliente nao tem idPessoaCliente".
  //
  // O que impede o envio e dito ANTES de chamar: o Nomus devolve erro generico,
  // e o vendedor leria "nao foi possivel" sem saber que faltou o documento.
  const syncClienteERP = React.useCallback(async (id) => {
    const cli = clientesRef.current.find(c => c.id === id);
    if (!cli) return null;
    if (cli.erpId) {
      toast({ kind: "ok", msg: `Cliente já sincronizado com o Nomus · ID ${cli.erpId}` });
      return cli.erpId;
    }

    const problemas = conferirParaErp(cli);
    if (problemas.length) {
      toast({ kind: "err", msg: problemas.join(' ') });
      return null;
    }
    if (!api?.criarClienteNoNomus) {
      toast({ kind: "err", msg: "Integração com o Nomus indisponível." });
      return null;
    }

    try {
      const r = await api.criarClienteNoNomus(id, payloadDoCliente(cli));
      const erpId = r?.erpId ?? null;
      if (!erpId) {
        toast({ kind: "err", msg: "O Nomus não devolveu o identificador do cliente." });
        return null;
      }
      // Semeia do que o SERVIDOR confirmou. O mock antigo semeava do que ele
      // mesmo tinha sorteado -- e por isso a tela sempre concordava consigo.
      setClientes(arr => arr.map(c => c.id === id ? { ...c, erpId, erpSyncEm: "agora" } : c));
      toast({
        kind: "ok",
        msg: r.jaExistia
          ? `Cliente já existia no Nomus · ID ${erpId}`
          : `Cliente criado no Nomus ERP · ID ${erpId}`,
      });
      return erpId;
    } catch (e) {
      console.error('Nao foi possivel sincronizar o cliente com o Nomus:', e.code || '', e.message);
      toast({ kind: "err", msg: e.message || "Não foi possível sincronizar o cliente com o Nomus." });
      return null;
    }
  }, [api, toast, clientes]);

  // Edita um canal escrevendo de volta nos campos canal* do cliente (fonte única)
  const updateEspecificador = React.useCallback((id, patch) => {
    const map = { tipo: "canalTipo", responsavel: "canalResponsavel", ativo: "canalAtivo" };
    const clientePatch = {};
    Object.keys(patch).forEach(k => { clientePatch[map[k] || k] = patch[k]; });
    setClientes(arr => arr.map(c => c.id === id ? { ...c, isCanal: true, ...clientePatch } : c));
  }, []);

  // Promove um cliente existente a canal de vendas
  const setClienteAsCanal = React.useCallback((id, canalData) => {
    setClientes(arr => arr.map(c => c.id === id ? { ...c, isCanal: true, canalAtivo: true, canalInicio: c.canalInicio || "agora", canalClientesCount: c.canalClientesCount || 0, canalReceitaGerada: c.canalReceitaGerada || 0, canalDeals: c.canalDeals || 0, canalTaxaConv: c.canalTaxaConv || 0, ...canalData } : c));
  }, []);

  // ----- Webhooks -----
  const createWebhook = React.useCallback((data) => {
    const id = "wh_" + Date.now().toString(36);
    const wh = { id, nome: data.nome, trigger: data.trigger, funil: data.funil || "Todos", url: data.url, ativo: true, lastFire: "—", lastStatus: 0, headers: data.headers || {} };
    setWebhooks(arr => [wh, ...arr]);
    toast({ kind: "ok", msg: `Webhook "${wh.nome}" criado` });
    return id;
  }, []);

  const updateWebhook = React.useCallback((id, patch) => {
    setWebhooks(arr => arr.map(w => w.id === id ? { ...w, ...patch } : w));
  }, []);

  const fireWebhook = React.useCallback((id) => {
    const wh = webhooks.find(w => w.id === id);
    if (!wh) return;
    const status = Math.random() > 0.92 ? (Math.random() > 0.5 ? 500 : 422) : 200;
    setWebhooks(arr => arr.map(w => w.id === id ? { ...w, lastFire: "agora", lastStatus: status } : w));
    const log = {
      ts: nowStamp(),
      agente: "Webhook · " + wh.nome,
      token: "wh_" + id.slice(-6),
      method: "POST",
      endpoint: wh.url.replace(/^https?:\/\/[^\/]+/, ""),
      status,
      time: Math.floor(80 + Math.random() * 250),
      payloadJson: makeWebhookPayload(wh),
    };
    setApiLogs(arr => [log, ...arr].slice(0, 50));
    toast({ kind: status < 300 ? "ok" : "err", msg: `Webhook disparado · ${status}` });
  }, [webhooks]);

  // ----- Rastreio de conversão: quem participou de cada marco -----
  // Deriva da cadeia (fonte da verdade). Papéis ausentes retornam null —
  // um lead pode nascer sem agente, fechar sem SDR ou sem handoff.
  const getAtribuicao = React.useCallback((lead) => {
    if (!lead) return null;
    const cadeia = lead.cadeia || [];
    const elo = (tipo) => cadeia.find(e => e.tipo === tipo) || null;
    const pessoa = (e) => e ? { tipo: e.atorTipo, id: e.atorId, nome: e.atorNome, papel: e.papel, quando: e.data, meta: e.meta || {} } : null;
    const eloGeracao = elo("geracao");
    const eloContato = elo("primeiro_contato");
    const eloQual = elo("qualificacao");
    const eloHandoff = elo("handoff");
    const eloFecha = elo("fechamento");
    const originador = pessoa(eloGeracao) || pessoa(eloContato);
    const qualificador = pessoa(eloQual);
    const qualImplicita = !!(eloQual && eloQual.meta && eloQual.meta.implicita);
    // Closer = quem fechou; se ainda não fechou, o responsável atual (se houver)
    const ownerAtual = lead.ownerId ? (usersRef.current.find(u => u.id === lead.ownerId) || null) : null;
    const closer = pessoa(eloFecha) || (ownerAtual ? { tipo: "humano", id: ownerAtual.id, nome: ownerAtual.nome, papel: ownerAtual.papel, quando: null, previsto: true, meta: {} } : null);
    const idsUnicos = [...new Set([originador, qualificador, closer].filter(Boolean).map(p => p.tipo + ":" + (p.id || p.nome)))];
    return {
      originador, qualificador, closer,
      handoff: pessoa(eloHandoff),
      // Sinaliza quem NÃO participou — o relatório precisa distinguir ausência de dado faltante
      semAgente: !eloGeracao,
      semQualificador: !eloQual,
      qualificacaoImplicita: qualImplicita,
      semCloser: !eloFecha,
      soloCompleto: idsUnicos.length === 1,
      fechado: !!eloFecha,
      participantes: idsUnicos.length,
    };
  }, []);

  // ----- Tokens de agente (api_tokens) + API de lançamento/pré-qualificação -----
  const AGENT_SCOPES = ["leads:write", "leads:qualify"];

  // ----- Cadastro de agentes de IA (espelha agentes_ia + RPCs da migration 0018) -----
  // A lista global AGENTS é mutada em conjunto com o state: os módulos que a leem
  // direto (kanban, drawer, gestão, exports) enxergam o cadastro atualizado.
  const [agents, setAgentsState] = React.useState(AGENTS);
  const commitAgents = React.useCallback((next) => {
    AGENTS.length = 0; AGENTS.push(...next); setAgentsState(next);
  }, []);

  const agentCodigo = (nome) => {
    const slug = String(nome || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 18);
    const base = "ag_" + (slug || "agente");
    let cod = base, i = 2;
    while (AGENTS.some(a => a.id === cod)) cod = base + "_" + (i++);
    return cod;
  };

  const createAgent = React.useCallback((data) => {
    const nome = (data.nome || "").trim();
    if (!nome) { toast({ kind: "err", msg: "Informe o nome do agente" }); return null; }
    if (AGENTS.some(a => a.name.toLowerCase() === nome.toLowerCase())) {
      toast({ kind: "err", msg: "Já existe um agente com esse nome" }); return null;
    }
    const ag = {
      id: agentCodigo(nome), name: nome,
      color: data.color || AGENT_COLORS[0],
      canal: data.canal || "Outro",
      descricao: (data.descricao || "").trim(),
      ativo: data.ativo !== false,
      criadoEm: new Date(), criadoPor: meRef.current.nome,
    };
    commitAgents([...AGENTS, ag]);
    toast({ kind: "ok", msg: "Agente cadastrado · emita um token para conectá-lo" });
    return ag;
  }, [commitAgents]);

  const updateAgent = React.useCallback((id, patch) => {
    const nome = patch.name !== undefined ? String(patch.name).trim() : undefined;
    if (nome !== undefined && !nome) { toast({ kind: "err", msg: "O nome do agente não pode ficar vazio" }); return; }
    if (nome && AGENTS.some(a => a.id !== id && a.name.toLowerCase() === nome.toLowerCase())) {
      toast({ kind: "err", msg: "Já existe um agente com esse nome" }); return;
    }
    commitAgents(AGENTS.map(a => a.id === id ? { ...a, ...patch, ...(nome !== undefined ? { name: nome } : {}) } : a));
    // O nome do agente também carimba os tokens já emitidos (aparece nos logs).
    if (nome) setAgentTokens(arr => arr.map(t => t.agenteId === id ? { ...t, agenteNome: nome } : t));
    toast({ kind: "ok", msg: "Agente atualizado" });
  }, [commitAgents]);

  const toggleAgent = React.useCallback((id) => {
    const a = AGENTS.find(x => x.id === id);
    if (!a) return;
    const ligando = a.ativo === false;
    commitAgents(AGENTS.map(x => x.id === id ? { ...x, ativo: ligando } : x));
    toast({ kind: "ok", msg: ligando ? "Agente reativado" : "Agente desativado · os tokens dele passam a responder 401" });
  }, [commitAgents]);

  const removeAgent = React.useCallback((id) => {
    if (agentTokens.some(t => t.agenteId === id && t.ativo)) {
      toast({ kind: "err", msg: "Revogue os tokens ativos antes de excluir" }); return;
    }
    if (leads.some(l => l.agenteId === id)) {
      toast({ kind: "err", msg: "Agente com leads atribuídos — desative em vez de excluir" }); return;
    }
    commitAgents(AGENTS.filter(a => a.id !== id));
    toast({ kind: "ok", msg: "Agente excluído" });
  }, [agentTokens, leads, commitAgents]);

  const issueAgentToken = React.useCallback((data) => {
    // Integracao de agentes desligada (23/09): sem porta de entrada, isto so
    // simularia -- e a simulacao gravava negocio de verdade e logava "201" de uma
    // API inexistente. Recusa ate a porta existir (data.jsx).
    if (!INTEGRACAO_DE_AGENTES) {
      toast({ kind: "err", msg: "Integração de agentes ainda não disponível." });
      return null;
    }
    const ag = AGENTS.find(a => a.id === data.agenteId);
    if (!ag) { toast({ kind: "err", msg: "Selecione um agente" }); return null; }
    if (ag.ativo === false) { toast({ kind: "err", msg: "Agente desativado — reative antes de emitir token" }); return null; }
    const escopos = (data.escopos || []).filter(e => AGENT_SCOPES.includes(e));
    if (!escopos.length) { toast({ kind: "err", msg: "Escolha ao menos um escopo" }); return null; }
    // 256 bits de entropia — mesmo formato de util.token_emitir()
    const bytes = new Uint8Array(32);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    const rand = Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("");
    const puro = "pdl_" + ag.id.replace("ag_", "") + "_" + rand;
    const id = "tok_" + Date.now().toString(36);
    const dias = Number(data.expiraDias) || 0;
    const tok = {
      id,
      nome: data.nome || (ag.name + " · produção"),
      agenteId: ag.id,
      agenteNome: ag.name,
      prefixo: puro.slice(0, 16),
      // guardamos apenas uma impressão do valor — nunca o token em claro
      hash: "sha256:" + rand.slice(0, 16) + "…",
      escopos,
      ativo: true,
      criadoEm: new Date(),
      criadoPor: meRef.current.nome,
      expiraEm: dias ? new Date(Date.now() + dias * 864e5) : null,
      ultimoUso: null,
      chamadas: 0,
    };
    setAgentTokens(arr => [tok, ...arr]);
    toast({ kind: "ok", msg: "Token emitido · copie agora, não será exibido novamente" });
    return { ...tok, puro };
  }, [toast]);

  const revokeAgentToken = React.useCallback((id) => {
    // Integracao de agentes desligada (23/09): sem porta de entrada, isto so
    // simularia -- e a simulacao gravava negocio de verdade e logava "201" de uma
    // API inexistente. Recusa ate a porta existir (data.jsx).
    if (!INTEGRACAO_DE_AGENTES) {
      toast({ kind: "err", msg: "Integração de agentes ainda não disponível." });
      return false;
    }
    setAgentTokens(arr => arr.map(t => t.id === id ? { ...t, ativo: false, revogadoEm: new Date() } : t));
    toast({ kind: "ok", msg: "Token revogado · chamadas passam a responder 401" });
  }, [toast]);

  // Autenticação idêntica à borda de produção: prefixo → token válido, ativo, não expirado
  // ----- API logs -----
  const pushApiLog = React.useCallback((log) => {
    setApiLogs(arr => [{ ...log, ts: nowStamp() }, ...arr].slice(0, 50));
  }, []);

  const authAgentToken = React.useCallback((tokenPuro, escopo) => {
    const t = agentTokens.find(x => tokenPuro && tokenPuro.startsWith(x.prefixo));
    // Token não identificado: só aqui a trilha fica sem atribuição.
    if (!t) return { erro: { status: 401, msg: "token inválido" } };
    // Nos demais casos o token é identificável — devolvemos junto do erro para
    // que api_logs registre agente e prefixo (mesmo contrato da migration 0016).
    if (!t.ativo || t.revogadoEm) return { token: t, erro: { status: 401, msg: "token revogado" } };
    if (t.expiraEm && t.expiraEm < new Date()) return { token: t, erro: { status: 401, msg: "token expirado" } };
    // Desativar o agente derruba todos os tokens dele sem precisar revogar um a um.
    const ag = AGENTS.find(a => a.id === t.agenteId);
    if (ag && ag.ativo === false) return { token: t, erro: { status: 401, msg: "agente desativado" } };
    if (!t.escopos.includes(escopo)) return { token: t, erro: { status: 403, msg: "token sem escopo " + escopo } };
    return { token: t };
  }, [agentTokens]);

  const touchToken = React.useCallback((id) => {
    setAgentTokens(arr => arr.map(t => t.id === id ? { ...t, ultimoUso: new Date(), chamadas: t.chamadas + 1 } : t));
  }, []);

  // POST /rpc/lead_criar — lançamento de lead pelo agente
  const apiCriarLead = React.useCallback((tokenPuro, payload = {}) => {
    // Integracao de agentes desligada (23/09): sem porta de entrada, isto so
    // simularia -- e a simulacao gravava negocio de verdade e logava "201" de uma
    // API inexistente. Recusa ate a porta existir (data.jsx).
    if (!INTEGRACAO_DE_AGENTES) {
      toast({ kind: "err", msg: "Integração de agentes ainda não disponível." });
      return { ok: false, status: 503, erro: "integracao de agentes indisponivel" };
    }
    const t0 = performance.now();
    const { token, erro } = authAgentToken(tokenPuro, "leads:write");
    const logFail = (nome, prefixo, status) => pushApiLog({ agente: nome, token: prefixo || "—", method: "POST", endpoint: "/rpc/lead_criar", status, time: Math.round(performance.now() - t0) });
    if (erro) { logFail(token?.agenteNome || "desconhecido", token?.prefixo, erro.status); return { ok: false, status: erro.status, erro: erro.msg }; }
    if (!payload.empresa) { logFail(token.agenteNome, token.prefixo, 422); return { ok: false, status: 422, erro: 'campo "empresa" é obrigatório' }; }
    // idempotência por (agente, external_id)
    if (payload.external_id) {
      const dup = leads.find(l => l.externalId === payload.external_id && l.agenteId === token.agenteId);
      if (dup) { touchToken(token.id); logFail(token.agenteNome, token.prefixo, 200); return { ok: true, status: 200, duplicado: true, lead_id: dup.id }; }
    }
    const id = receiveLead({
      empresa: payload.empresa, contato: payload.contato, cargo: payload.cargo,
      email: payload.email, telefone: payload.telefone, segmento: payload.segmento,
      funil: payload.funil || "projetos", agenteId: token.agenteId, agenteNome: token.agenteNome,
      scoreFit: 0, fonte: payload.origem_canal || "API",
    });
    setLeads(arr => arr.map(l => l.id === id ? { ...l, externalId: payload.external_id || null, criadoEm: "via API", proximaAcao: "Triagem do lead de entrada" } : l));
    touchToken(token.id);
    pushApiLog({ agente: token.agenteNome, token: token.prefixo, method: "POST", endpoint: "/rpc/lead_criar", status: 201, time: Math.round(performance.now() - t0) });
    return { ok: true, status: 201, lead_id: id, etapa: "ENTRADA" };
  }, [authAgentToken, leads, receiveLead, pushApiLog, touchToken, toast]);

  // POST /rpc/lead_prequalificar — pré-qualificação posterior
  const apiPrequalificarLead = React.useCallback((tokenPuro, payload = {}) => {
    // Integracao de agentes desligada (23/09): sem porta de entrada, isto so
    // simularia -- e a simulacao gravava negocio de verdade e logava "201" de uma
    // API inexistente. Recusa ate a porta existir (data.jsx).
    if (!INTEGRACAO_DE_AGENTES) {
      toast({ kind: "err", msg: "Integração de agentes ainda não disponível." });
      return { ok: false, status: 503, erro: "integracao de agentes indisponivel" };
    }
    const t0 = performance.now();
    const { token, erro } = authAgentToken(tokenPuro, "leads:qualify");
    const log = (nome, prefixo, status) => pushApiLog({ agente: nome, token: prefixo || "—", method: "PATCH", endpoint: "/rpc/lead_prequalificar", status, time: Math.round(performance.now() - t0) });
    if (erro) { log(token?.agenteNome || "desconhecido", token?.prefixo, erro.status); return { ok: false, status: erro.status, erro: erro.msg }; }
    const score = payload.score_fit != null ? Number(payload.score_fit) : null;
    const bant = payload.bant_score != null ? Number(payload.bant_score) : null;
    if (score != null && (score < 0 || score > 100)) { log(token.agenteNome, token.prefixo, 422); return { ok: false, status: 422, erro: "score_fit deve estar entre 0 e 100" }; }
    if (bant != null && (bant < 0 || bant > 4)) { log(token.agenteNome, token.prefixo, 422); return { ok: false, status: 422, erro: "bant_score deve estar entre 0 e 4" }; }
    const lead = leads.find(l => (payload.lead_id && l.id === payload.lead_id) ||
      (!payload.lead_id && payload.external_id && l.externalId === payload.external_id && l.agenteId === token.agenteId));
    if (!lead) { log(token.agenteNome, token.prefixo, 404); return { ok: false, status: 404, erro: "lead não encontrado" }; }
    if (lead.perdido) { log(token.agenteNome, token.prefixo, 409); return { ok: false, status: 409, erro: "lead já marcado como perdido" }; }
    setLeads(arr => arr.map(l => l.id !== lead.id ? l : {
      ...l,
      scoreFit: score != null ? score : l.scoreFit,
      bantScore: bant != null ? bant : l.bantScore,
      calor: payload.calor || l.calor,
      valor: payload.valor != null ? Number(payload.valor) : l.valor,
      segmento: payload.segmento || l.segmento,
      contato: payload.contato || l.contato,
      email: payload.email || l.email,
      telefone: payload.telefone || l.telefone,
      proximaAcao: payload.avancar ? "Contato humano · lead pré-qualificado" : l.proximaAcao,
    }));
    appendTimeline(lead.id, {
      tipo: "qualificacao", autorTipo: "agente", autor: token.agenteNome,
      texto: <>{payload.resumo || "Pré-qualificação registrada."}{score != null ? <> · score <strong>{score}</strong></> : ""}</>,
      when: "agora",
    });
    if (payload.avancar) moveLeadStage(lead.id, "QUALIFICADO", { autorTipo: "agente", autor: token.agenteNome });
    touchToken(token.id);
    pushApiLog({ agente: token.agenteNome, token: token.prefixo, method: "PATCH", endpoint: "/rpc/lead_prequalificar", status: 200, time: Math.round(performance.now() - t0) });
    return { ok: true, status: 200, lead_id: lead.id, etapa: payload.avancar ? "QUALIFICADO" : "ENTRADA" };
  }, [authAgentToken, leads, pushApiLog, appendTimeline, moveLeadStage, touchToken, toast]);

  // ----- Users -----
  const updateUser = React.useCallback((id, patch) => {
    setUsers(arr => arr.map(u => u.id === id ? { ...u, ...patch } : u));
  }, []);

  // Parte 2 (0080): cargo, ativo e vendedor do Nomus gravam no banco, e a tela
  // so muda depois que ele aceitou. A funcao grava os tres juntos -- o vendedor
  // nulo LIMPA --, por isso o que nao mudou vai com o valor atual.
  const definirUsuario = React.useCallback(async (id, patch) => {
    const atual = users.find((u) => u.id === id);
    if (!atual) return false;
    const novo = { papel: atual.papel, ativo: atual.ativo, idPessoaVendedor: atual.idPessoaVendedor ?? null, ...patch };
    if (!api) {
      toast({ kind: "err", msg: "Sem conexão com o banco: nada foi gravado." });
      return false;
    }
    try {
      await api.definirUsuario(id, novo);
    } catch (e) {
      console.error('Não foi possível gravar o usuário:', e.message);
      toast({ kind: "err", msg: mensagemDeAdmin(e, "Não foi possível gravar o usuário.") });
      return false;
    }
    setUsers((arr) => arr.map((u) => (u.id === id ? { ...u, ...novo } : u)));
    // O banco vale na hora; a tela da pessoa so troca de menus ao recarregar
    // (spec da Parte 2, armadilha 4).
    const trocouCargo = patch.papel !== undefined && patch.papel !== atual.papel;
    toast({ kind: "ok", msg: trocouCargo ? `Cargo gravado. ${atual.nome} vê o novo cargo ao recarregar a página.` : "Usuário atualizado" });
    return true;
  }, [api, users, toast]);

  // Exclusao logica (0101): o banco e quem decide (so Admin, nunca a si mesmo,
  // nunca o ultimo Admin). A pessoa so sai da lista depois que ele aceitou.
  const excluirUsuario = React.useCallback(async (id) => {
    const atual = users.find((u) => u.id === id);
    if (!atual) return false;
    if (!api) {
      toast({ kind: "err", msg: "Sem conexão com o banco: nada foi gravado." });
      return false;
    }
    try {
      await api.excluirUsuario(id);
    } catch (e) {
      console.error('Não foi possível excluir o usuário:', e.message);
      // PT404 (nao encontrado): o PostgREST deixa o codigo "PTxyz" mandar o status HTTP; P0002 viraria 500. mensagemDeAdmin so conhece P0002, e nao mudo o que ela faz para os outros metodos.
      const msg = e?.code === 'PT404' ? "Usuário não encontrado." : mensagemDeAdmin(e, "Não foi possível excluir o usuário.");
      toast({ kind: "err", msg });
      return false;
    }
    setUsers((arr) => arr.filter((u) => u.id !== id));
    toast({ kind: "ok", msg: "Usuário excluído." });
    return true;
  }, [api, users, toast]);

  const regravarPapeis = React.useCallback(async () => {
    try {
      aplicarPapeis(await carregarPapeis());
    } catch (e) {
      console.error('Cargo gravado, mas nao foi possivel recarregar os cargos:', e.message);
      toast({ kind: "err", msg: "Cargo gravado, mas a tela não recarregou. Recarregue a página." });
    }
  }, [carregarPapeis, aplicarPapeis, toast]);

  // dados: { label?, descricao?, escopos?, permissoes? } -- so as chaves de
  // escopo/permissao enviadas mudam no banco.
  const salvarCargo = React.useCallback(async (codigo, dados) => {
    if (!api) return { ok: false, erro: "Sem conexão com o banco: nada foi gravado." };
    const atual = papeis[codigo];
    try {
      await api.salvarCargo(codigo, {
        label: dados.label ?? atual?.label,
        descricao: dados.descricao,
        escopos: dados.escopos,
        permissoes: dados.permissoes,
      });
    } catch (e) {
      console.error('Não foi possível gravar o cargo:', e.message);
      return { ok: false, erro: mensagemDeAdmin(e, "Não foi possível gravar o cargo.") };
    }
    await regravarPapeis();
    return { ok: true };
  }, [api, papeis, regravarPapeis]);

  const excluirCargo = React.useCallback(async (codigo) => {
    if (!api) return { ok: false, erro: "Sem conexão com o banco: nada foi excluído." };
    try {
      await api.excluirCargo(codigo);
    } catch (e) {
      console.error('Não foi possível excluir o cargo:', e.message);
      return { ok: false, erro: mensagemDeAdmin(e, "Não foi possível excluir o cargo.") };
    }
    await regravarPapeis();
    return { ok: true };
  }, [api, regravarPapeis]);

  // Telefone/WhatsApp do consultor: GRAVA (0078) e so entao muda a tela.
  const salvarTelefoneUsuario = React.useCallback(async (id, telefone) => {
    if (api) {
      try {
        await api.atualizarTelefoneUsuario(id, telefone);
      } catch (e) {
        console.error('Não foi possível salvar o telefone:', e.message);
        toast({ kind: "err", msg: e.code === '42501' ? "Você não tem permissão para alterar este usuário." : "Não foi possível salvar o telefone." });
        return false;
      }
    }
    setUsers(arr => arr.map(u => u.id === id ? { ...u, telefone } : u));
    toast({ kind: "ok", msg: "Telefone salvo" });
    return true;
  }, [api]);

  // Cada usuário edita o próprio cadastro — papel e escopo continuam sendo do Admin.
  // GRAVA o proprio nome, iniciais e telefone (levantamento de 02/10). Ate aqui `updateMe` so mexia na memoria e a tela
  // dizia "Perfil atualizado". So avisa e mostra depois que o banco aceitou; devolve true/false.
  const salvarMeuPerfil = React.useCallback(async ({ nome, iniciais, telefone }) => {
    const id = meRef.current.id;
    if (api) {
      try {
        await api.salvarMeuPerfil(id, { nome, iniciais, telefone });
      } catch (e) {
        console.error('Não foi possível salvar o perfil:', e.code || '', e.message);
        toast({ kind: "err", msg: e.code === 'nome_vazio' ? "O nome não pode ficar em branco."
          : e.code === '42501' ? "Você não tem permissão para alterar este perfil." : "Não foi possível salvar o perfil." });
        return false;
      }
    }
    const tel = String(telefone ?? '').trim();
    setMe(m => ({ ...m, nome: String(nome).trim(), initials: iniciais || m.initials, telefone: tel }));
    setUsers(arr => arr.map(u => u.id === id ? { ...u, nome: String(nome).trim(), initials: iniciais || u.initials, telefone: tel } : u));
    toast({ kind: "ok", msg: "Perfil atualizado" });
    return true;
  }, [api]);
  const setConfigTabela = React.useCallback((id, patch) => {
    setConfig(c => ({ ...c, tabelas: { ...c.tabelas, [id]: { ...(c.tabelas[id] || {}), ...patch } } }));
  }, []);
  const setConfigTransportadora = React.useCallback((id, patch) => {
    setConfig(c => ({ ...c, transportadoras: { ...c.transportadoras, [id]: { ...(c.transportadoras[id] || {}), ...patch } } }));
  }, []);
  const setConfigComercial = React.useCallback((patch) => {
    setConfig(c => ({ ...c, comercial: { ...c.comercial, ...patch } }));
  }, []);
  usersRef.current = users;

  // ----- Gestão: metas -----
  const setMetaEmpresa = React.useCallback((valor) => {
    metasSujasRef.current = true;
    setMetas(m => ({ ...m, empresa: Math.max(0, valor) }));
  }, []);
  const setMetaFunil = React.useCallback((funil, valor) => {
    metasSujasRef.current = true;
    setMetas(m => ({ ...m, porFunil: { ...m.porFunil, [funil]: Math.max(0, valor) } }));
  }, []);
  const setMetaIndividual = React.useCallback((userId, campo, valor) => {
    metasSujasRef.current = true;
    setMetas(m => ({ ...m, individuais: { ...m.individuais, [userId]: { ...(m.individuais[userId] || {}), [campo]: Math.max(0, valor) } } }));
  }, []);
  const setMetaAgente = React.useCallback((agentId, valor) => {
    setMetas(m => ({ ...m, agentes: { ...m.agentes, [agentId]: { ...(m.agentes[agentId] || {}), leadsEnriquecidos: Math.max(0, valor) } } }));
  }, []);
  // GRAVA. Ate 22/09 este botao so mostrava "Metas salvas e aplicadas aos
  // relatorios" -- nada ia ao banco, e o F5 devolvia os numeros fixos.
  //
  // Grava so o que difere da meta vigente do banco, sempre na competencia do
  // MES ATUAL (a tela de Gestao edita a meta do mes corrente). Meta herdada de
  // agosto e nao tocada nao gera linha nova: continua valendo por heranca.
  const saveMetas = React.useCallback(async () => {
    const base = metasBaseRef.current;
    if (!api?.gravarMeta || !base) {
      toast({ kind: "err", msg: "Não foi possível salvar as metas agora. Recarregue a página e tente de novo." });
      return false;
    }
    const competencia = api.competenciaDoMes(MES_ATUAL_ID);
    const alteracoes = [];
    if ((metas.empresa || 0) !== (base.empresa || 0)) {
      alteracoes.push({ escopo: "empresa", competencia, metrica: "faturamento", valor: metas.empresa || 0 });
    }
    for (const [codigo, valor] of Object.entries(metas.porFunil || {})) {
      if ((valor || 0) === (base.porFunil[codigo] || 0)) continue;
      const funil = funis.find((f) => f.id === codigo);
      // Funil sem uuid nao veio do banco: nao ha linha onde gravar. Pular em
      // silencio esconderia isso, entao registra.
      if (!funil?.uuid) { console.error("Meta de funil sem funil no banco:", codigo); continue; }
      alteracoes.push({ escopo: "funil", funilId: funil.uuid, competencia, metrica: "faturamento", valor: valor || 0 });
    }
    for (const [uid, campos] of Object.entries(metas.individuais || {})) {
      for (const [campo, valor] of Object.entries(campos || {})) {
        if ((valor || 0) === ((base.individuais[uid] || {})[campo] || 0)) continue;
        alteracoes.push({ escopo: "usuario", profileId: uid, competencia, metrica: api.metricaDoCampo(campo), valor: valor || 0 });
      }
    }
    if (!alteracoes.length) {
      metasSujasRef.current = false;
      toast({ kind: "info", msg: "Nenhuma meta da empresa, dos funis ou da equipe foi alterada." });
      return true;
    }

    const resultados = await Promise.allSettled(alteracoes.map((a) => api.gravarMeta(a)));
    const falhas = resultados.filter((r) => r.status === "rejected");
    // Sem falha, o rascunho deixa de ser rascunho: a recarga abaixo o substitui
    // pelo que o banco tem (igual ao digitado). Com falha, o rascunho FICA -- o
    // que gravou vira base, o que nao gravou continua na tela para nova
    // tentativa, em vez de sumir junto com o erro.
    if (!falhas.length) metasSujasRef.current = false;
    try {
      setMetasLinhas(await api.listarMetas());
    } catch (e) {
      console.error("Nao foi possivel recarregar as metas apos gravar:", e.message);
    }
    if (falhas.length) {
      falhas.forEach((f) => console.error("Nao foi possivel gravar meta:", f.reason?.code || "", f.reason?.message));
      const semPermissao = falhas.some((f) => f.reason?.code === "42501");
      toast({
        kind: "err",
        msg: semPermissao
          ? "Só a Direção pode alterar metas."
          : `${falhas.length} de ${alteracoes.length} metas não foram salvas. Tente de novo.`,
      });
      return false;
    }
    toast({ kind: "ok", msg: "Metas salvas e aplicadas aos relatórios" });
    return true;
  }, [api, metas, funis, toast, MES_ATUAL_ID]);

  // ----- Gestão: taxas de comissão -----
  const setComissaoRate = React.useCallback((userId, rate) => {
    setComissaoRates(r => ({ ...r, [userId]: Math.max(0, rate) / 100 }));
  }, []);

  // ----- Pós-venda -----
  const updatePosVenda = React.useCallback((leadId, patch) => {
    setPosVenda(map => ({ ...map, [leadId]: { ...(map[leadId] || {}), ...patch } }));
  }, []);
  const avancarPosVenda = React.useCallback((leadId, etapa) => {
    setPosVenda(map => ({ ...map, [leadId]: { ...(map[leadId] || {}), etapa, desdeEm: new Date().toISOString(), diasDesde: 0 } }));
    const lead = leads.find(l => l.id === leadId);
    appendTimeline(leadId, {
      tipo: "alert", autorTipo: "humano", autor: meRef.current.nome,
      texto: <>Pós-venda avançou para: <strong>{POSVENDA_ETAPAS_BY_ID[etapa]?.label || etapa}</strong>.</>,
      when: "agora",
    });
    toast({ kind: "ok", msg: `Pós-venda → ${POSVENDA_ETAPAS_BY_ID[etapa]?.label || etapa}` });
  }, [leads]);

  // Clientes "em risco": ativos sem negócio GANHO nos últimos 90 dias
  const getClientesEmRisco = React.useCallback(() => {
    const noventa = 90;
    return clientes.filter(c => {
      if (c.isCanal) return false;
      if (c.status !== "ativo") return false;
      const ultimaCompra = leads.filter(l => l.clienteId === c.id && l.stage === "GANHO" && !l.perdido).length;
      const diasUlt = c.lastBuyDias;
      return !ultimaCompra && (diasUlt == null || diasUlt >= noventa);
    });
  }, [clientes, leads]);

  // ----- SAC (chamados / ocorrências pós-venda) -----
  const createChamado = React.useCallback((data) => {
    const seq = sacSeqRef.current + 1;
    sacSeqRef.current = seq;
    setSacSeq(seq);
    const id = "ch_" + Date.now().toString(36);
    const protocolo = `SAC-${new Date().getFullYear()}-${String(seq).padStart(4, "0")}`;
    const abertura = data.descricao
      ? [buildChamadoInteracao({ autorTipo: "cliente", canal: data.canal, texto: data.descricao, whenHrs: 0 }, 0, id, meRef.current)]
      : [];
    const chamado = {
      id, protocolo,
      clienteId: data.clienteId || null,
      leadId: data.leadId || null,
      especificadorId: data.especificadorId || null,
      produtoId: data.produtoId || null,
      // Nº CP: informado no formulário ou herdado do negócio vinculado
      pedidoNomus: data.pedidoNomus || (data.leadId ? (leads.find(l => l.id === data.leadId)?.pedidoNomus || null) : null),
      tipo: data.tipo || "duvida",
      canal: data.canal || "email",
      prioridade: data.prioridade || "media",
      status: "aberto",
      responsavelId: data.responsavelId || meRef.current.id,
      assunto: data.assunto || "Novo chamado",
      descricao: data.descricao || "",
      abertoEm: new Date(),
      abertoHrs: 0,
      abertoQuando: "agora",
      resolvidoEm: null,
      resolucao: null,
      csat: null,
      reaberturas: 0,
      passosDone: [],
      interacoes: abertura,
    };
    setChamados(arr => [chamado, ...arr]);
    const cli = data.clienteId ? clientes.find(c => c.id === data.clienteId) : null;
    // Registra na timeline do negócio vinculado, se houver
    if (data.leadId) {
      appendTimeline(data.leadId, {
        tipo: "alert", autorTipo: "humano", autor: meRef.current.nome,
        texto: <>Chamado de SAC <strong>{protocolo}</strong> aberto · {SAC_TIPOS_BY_ID[chamado.tipo]?.short}.</>,
        when: "agora",
      });
    }
    toast({ kind: "ok", msg: `Chamado ${protocolo} aberto${cli ? ` · ${cli.nome}` : ""}` });
    return id;
  }, [sacSeq, clientes, leads]);

  const togglePassoChamado = React.useCallback((id, idx) => {
    setChamados(arr => arr.map(c => {
      if (c.id !== id) return c;
      const done = c.passosDone || [];
      return { ...c, passosDone: done.includes(idx) ? done.filter(i => i !== idx) : [...done, idx] };
    }));
  }, []);

  const updateChamado = React.useCallback((id, patch) => {
    setChamados(arr => arr.map(c => c.id === id ? { ...c, ...patch } : c));
  }, []);

  const moveChamadoStatus = React.useCallback((id, status) => {
    setChamados(arr => arr.map(c => {
      if (c.id !== id) return c;
      const now = new Date();
      const prevDef = SAC_STATUS_BY_ID[c.status];
      const nextDef = SAC_STATUS_BY_ID[status];
      let slaOffsetMs = c.slaOffsetMs || 0;
      let slaPausadoEm = c.slaPausadoEm || null;
      // Saindo de status pausado → acumula o tempo pausado no offset
      if (prevDef?.slaPaused && !nextDef?.slaPaused && slaPausadoEm) {
        slaOffsetMs += now.getTime() - new Date(slaPausadoEm).getTime();
        slaPausadoEm = null;
      }
      // Entrando em status pausado → marca início da pausa
      if (!prevDef?.slaPaused && nextDef?.slaPaused) {
        slaPausadoEm = now;
      }
      const next = { ...c, status, slaOffsetMs, slaPausadoEm };
      if ((status === "resolvido" || status === "fechado") && !c.resolvidoEm) next.resolvidoEm = now;
      if (status !== "resolvido" && status !== "fechado") next.resolvidoEm = null;
      // Interação automática de sistema na timeline do chamado
      const label = nextDef?.label || status;
      const inter = {
        id: "int_" + Date.now().toString(36),
        autorTipo: "sistema", autorId: null, autor: "Sistema",
        canal: "sistema", texto: `Status alterado para ${label}${nextDef?.slaPaused ? " · SLA pausado" : ""}.`,
        whenHrs: 0, when: "agora",
      };
      next.interacoes = [...c.interacoes, inter];
      return next;
    }));
    toast({ kind: (status === "resolvido" || status === "fechado") ? "ok" : "info", msg: `Chamado → ${SAC_STATUS_BY_ID[status]?.label || status}` });
  }, []);

  const addChamadoInteracao = React.useCallback((id, data) => {
    setChamados(arr => arr.map(c => {
      if (c.id !== id) return c;
      const all = usersRef.current;
      const autorTipo = data.autorTipo || "humano";
      const inter = {
        id: "int_" + Date.now().toString(36),
        autorTipo,
        autorId: autorTipo === "humano" ? (data.autorId || meRef.current.id) : null,
        autor: autorTipo === "cliente" ? "Cliente" : (all.find(u => u.id === (data.autorId || meRef.current.id))?.nome || meRef.current.nome),
        canal: data.canal || c.canal,
        texto: data.texto,
        whenHrs: 0,
        when: "agora",
      };
      // Observação interna não altera o status do chamado
      return { ...c, interacoes: [...c.interacoes, inter] };
    }));
    toast({ kind: "ok", msg: "Observação registrada" });
  }, []);

  const resolveChamado = React.useCallback((id, { resolucao, csat } = {}) => {
    setChamados(arr => arr.map(c => {
      if (c.id !== id) return c;
      const inter = {
        id: "int_" + Date.now().toString(36),
        autorTipo: "sistema", autorId: null, autor: "Sistema",
        canal: "sistema", texto: "Chamado resolvido" + (csat ? ` · CSAT ${csat}/5` : "") + ". Pesquisa de satisfação enviada ao cliente.",
        whenHrs: 0, when: "agora",
      };
      return {
        ...c, status: "resolvido", resolvidoEm: new Date(),
        resolucao: resolucao || c.resolucao || "Chamado resolvido.",
        csat: csat != null ? csat : c.csat,
        interacoes: [...c.interacoes, inter],
      };
    }));
    toast({ kind: "ok", msg: "Chamado resolvido" + (csat ? ` · CSAT ${csat}/5` : "") });
  }, []);

  const reopenChamado = React.useCallback((id) => {
    setChamados(arr => arr.map(c => {
      if (c.id !== id) return c;
      const inter = {
        id: "int_" + Date.now().toString(36),
        autorTipo: "sistema", autorId: null, autor: "Sistema",
        canal: "sistema", texto: "Chamado reaberto e retornou para Em Atendimento.",
        whenHrs: 0, when: "agora",
      };
      return { ...c, status: "em_atendimento", resolvidoEm: null, reaberturas: (c.reaberturas || 0) + 1, interacoes: [...c.interacoes, inter] };
    }));
    toast({ kind: "info", msg: "Chamado reaberto" });
  }, []);

  const addLaude = React.useCallback((chamadoId, laudeData) => {
    setChamados(arr => arr.map(c => {
      if (c.id !== chamadoId) return c;
      const inter = {
        id: "int_" + Date.now().toString(36),
        autorTipo: "sistema", autorId: null, autor: "Sistema",
        canal: "sistema", texto: `Laude técnico emitido por ${laudeData.analista}. Causa raiz: ${laudeData.causaRaiz}. Ação corretiva: ${laudeData.acaoCorretiva}.`,
        whenHrs: 0, when: "agora",
      };
      return {
        ...c,
        status: "laude_emitido",
        laude: { ...laudeData, emitidoEm: new Date(), emitidoQuando: "agora" },
        interacoes: [...c.interacoes, inter],
      };
    }));
    toast({ kind: "ok", msg: "Laude técnico emitido" });
  }, []);

  const getChamadosByCliente = React.useCallback((clienteId) => chamados.filter(c => c.clienteId === clienteId), [chamados]);

  // ----- Timeline -----
  // LE o que aconteceu, e so isso.
  //
  // Ate 18/09 esta funcao concatenava `buildTimeline(lead)`, que FABRICAVA sete
  // tipos de evento a partir dos campos do negocio -- com autor fixo no codigo
  // ("Ana Vargas"), data calculada (`diasNoEstagio + 5`) e texto inventado.
  //
  // A timeline e o que alguem consulta para reconstruir o que houve num negocio:
  // passagem de bastao, cobranca interna, disputa com cliente. Evento inventado
  // ali e afirmacao falsa que ninguem tem motivo para questionar -- e atribuida
  // a uma pessoa pelo nome.
  const getTimeline = React.useCallback((leadId) => timeline[leadId] || [], [timeline]);

  // ----- Helpers de derivação -----
  const getClienteById = React.useCallback((id) => clientes.find(c => c.id === id), [clientes]);
  const getEspecificadorById = React.useCallback((id) => especificadores.find(e => e.id === id), [especificadores]);
  // Do BANCO, nao da constante inventada. Sem `id` nao ha o que procurar --
  // devolver a primeira da lista faria o negocio herdar tabela por acidente.
  // Itens de uma tabela MANUAL, sob demanda. Uma funcao estreita em vez de expor
  // `api` no contexto: expor a api inteira deixaria qualquer tela falar com o
  // banco por fora do store, que e a fronteira que este arquivo existe para
  // manter.
  //
  // So `TABELA PAULA` e `Especial Fortaleza` sao manuais (31 itens ao todo).
  // Tabela de FATOR nao tem item nenhum -- o preco dela e conta.
  const carregarItensTabela = React.useCallback(async (tabelaId) => {
    if (!api || !tabelaId) return [];
    try {
      return await api.listarItensTabelaPreco(tabelaId);
    } catch (e) {
      console.error('Não foi possível carregar os itens da tabela de preço:', e.message);
      toast({ kind: 'err', msg: 'Não foi possível carregar os preços desta tabela.' });
      return [];
    }
  }, [api, toast]);

  const getTabelaById = React.useCallback((id) => (id ? tabelasPreco.find(t => t.id === id) : undefined), [tabelasPreco]);
  const getLeadsByCliente = React.useCallback((clienteId) => leads.filter(l => l.clienteId === clienteId), [leads]);
  const getAlertsByLead = React.useCallback((leadId) => alerts.filter(a => a.leadId === leadId), [alerts]);
  const getAlertsByCliente = React.useCallback((clienteId) => alerts.filter(a => a.clienteId === clienteId), [alerts]);

  // ----- Escopo por login -----
  // Cada papel recebe do provider apenas os registros que lhe dizem respeito
  // (regras em auth.jsx → DATA_SCOPE). Os helpers internos seguem com a base
  // completa — o recorte acontece na fronteira de leitura.
  // Mudanças na matriz de permissões (Admin → Cargos) re-renderizam todo o app.
  const rbacVersion = useRbacVersion ? useRbacVersion() : 0;
  const leadsVisiveis    = React.useMemo(() => (scopeLeads    ? scopeLeads(leads, me)       : leads),    [leads, me.id, me.papel, rbacVersion]);
  const clientesVisiveis = React.useMemo(() => (scopeClientes ? scopeClientes(clientes, me) : clientes), [clientes, me.id, me.papel, rbacVersion]);
  const alertsVisiveis   = React.useMemo(() => (scopeAlerts   ? scopeAlerts(alerts, me)     : alerts),   [alerts, me.id, me.papel, rbacVersion]);
  const chamadosVisiveis = React.useMemo(() => (scopeChamados ? scopeChamados(chamados, me, clientesVisiveis) : chamados), [chamados, clientesVisiveis, me.id, me.papel, rbacVersion]);

  const value = {
    me, rbacVersion,
    leads: leadsVisiveis, alerts: alertsVisiveis, clientes: clientesVisiveis,
    leadsTodos: leads, clientesTodos: clientes,
    especificadores, webhooks, apiLogs, users, anexos, funis, etapasPorFunil, papeis,
    notifications, notifyPeople, markNotificationsRead,
    activeFunnel, setActiveFunnel,
    salvarTelefoneUsuario, createLead, updateLead, setProximaAcao, moveLeadStage, markLeadLost, reopenLead, deleteLead, confirmRepasse, handoffToCloser, addNote, getTimeline, timeline,
    createAlert, toggleAlertDone, updateAlert, toggleSubtask, addSubtask, exportIcs, touchAtividade,
    updateBant,
    getAnexosByLead, carregarAnexos, enviarAnexos, removerAnexo, baixarAnexo,
    setLeadTabela,
    getPropostasByLead, createProposta, updateProposta, removeProposta, selectPropostaForClosing, sendProposta, revogarLinkProposta, approvePropostaToERP,
    // Lookups do Nomus (0052): a tela de proposta monta os selects com estes, nao
    // mais com as constantes de src/data.jsx -- ver nota na carga, acima.
    condicoesPagamento, tiposVenda,
    // Catálogo do banco (14/09): a tela de proposta lia a constante vazia.
    produtos, atualizarProduto, consultarProdutosNomus,
    updatePropostaStatus, criarRevisao, duplicarProposta,
    createCliente, updateCliente, syncClienteERP, updateEspecificador, setClienteAsCanal,
    createWebhook, updateWebhook, fireWebhook,
    updateUser, salvarMeuPerfil, definirUsuario, excluirUsuario, salvarCargo, excluirCargo,
    config, setConfigTabela, setConfigTransportadora, setConfigComercial,
    agentTokens, issueAgentToken, revokeAgentToken, apiCriarLead, apiPrequalificarLead, AGENT_SCOPES,
    agents, createAgent, updateAgent, toggleAgent, removeAgent,
    getAtribuicao,
    meusConvites, responderConvite, consultarAgenda,
    metas, metasDoMes, setMetaEmpresa, setMetaFunil, setMetaIndividual, setMetaAgente, saveMetas,
    comissaoRates, setComissaoRate,
    posVenda, updatePosVenda, avancarPosVenda,
    chamados: chamadosVisiveis, createChamado, updateChamado, moveChamadoStatus, addChamadoInteracao, resolveChamado, reopenChamado, getChamadosByCliente, togglePassoChamado, addLaude,
    pushApiLog, appendTimeline, appendCadeia, receiveLead,
    fireWebhookByEvent, toastWithUndo, undoBuffer, doUndo, dismissUndo, getClientesEmRisco,
    getClienteById, getEspecificadorById, getTabelaById, tabelasPreco, carregarItensTabela,
    contagemDoFunil,
    getLeadsByCliente, getAlertsByLead, getAlertsByCliente,
    toast, toasts,
  };
  return (
    <StoreContext.Provider value={value}>
      {children}
      {undoBuffer && (
        <div className="undo-bar">
          <Icon name="rotate-ccw" size={14} />
          <span className="undo-msg">{undoBuffer.message}</span>
          <button className="undo-btn" onClick={doUndo}>Desfazer</button>
          <button className="undo-x" onClick={dismissUndo} title="Fechar">×</button>
        </div>
      )}
    </StoreContext.Provider>
  );
}

// Recusas das funcoes admin_* (0080). 42501 e a primeira linha (quem chama nao
// e Admin ativo); 22023/P0002 sao as travas de consistencia, com a mensagem ja
// escrita para a tela no proprio banco.
function mensagemDeAdmin(e, padrao) {
  if (e?.code === '42501') return 'Só o Admin administra usuários e cargos.';
  if ((e?.code === '22023' || e?.code === 'P0002') && e.message) return e.message;
  return padrao;
}

function nowStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth()+1)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function makeWebhookPayload(wh) {
  return JSON.stringify({
    event: wh.trigger,
    funil: wh.funil,
    timestamp: new Date().toISOString(),
    data: {
      lead_id: "ld_" + Math.random().toString(36).slice(2, 8),
      empresa: "Acme Indústria",
      stage: "QUALIFICADO",
      valor_proposta: 120000,
      dynamic: { fonte: "AI_AGENT", fit_comercial: "alto", score_llm: 84 }
    }
  }, null, 2);
}

export { StoreProvider, useStore, nowStamp, makeWebhookPayload };
